// Bói Đê backend client.
// All responses follow envelope: { data, errors, error_description, status }
// Frontend always reads `data` (or throws on errors / status === "failed").

import {
  isInsufficientFundsError,
  requestQuickTopup,
  type QuickTopupOffer,
} from "@/lib/quick-topup";
import { BOIDE_API_URL, BOIDE_ACCOUNT_API_URL } from "@/lib/boide-api-url";
import { isStandalonePwa } from "@/lib/pwa";
import { noteMaintenanceResponse } from "@/lib/maintenance-config";
import { noteFeatureMaintenance } from "@/lib/feature-maintenance";

// Re-export cho nơi khác import type từ boide-api (vd thanh-toan.tsx).
export type { QuickTopupOffer } from "@/lib/quick-topup";

// Định nghĩa nằm ở module riêng để phá vòng import với `maintenance-config`
// (xem ghi chú trong `boide-api-url.ts`). Re-export để mọi chỗ đang import từ đây
// không phải sửa.
// ⚠️ Phải có CẢ `import` lẫn `export`: `export … from` chỉ chuyển tiếp ra ngoài chứ
// KHÔNG đưa tên vào phạm vi của chính file này — mà file này dùng nó ở ~200 chỗ.
export { BOIDE_API_URL, BOIDE_ACCOUNT_API_URL };

const TOKEN_KEY = "boide_access_token";
const LEGACY_TOKEN_KEY = "access_token";
const REFRESH_KEY = "boide_refresh_token";
const LEGACY_REFRESH_KEY = "refresh_token";
const CHART_KEY = "boide_tuvi_chart_id";
export const BOIDE_AUTH_CHANGED_EVENT = "boide-auth-changed";

// ---------- Token / chart storage ----------
//
// GHI NHỚ ĐĂNG NHẬP
//
// 🔴 HAI KHO, KHÔNG PHẢI MỘT CỜ SUÔNG. "Ghi nhớ" = `localStorage` (sống qua lần tắt
// trình duyệt, hành vi cũ và vẫn là mặc định); bỏ tick = `sessionStorage` (chết khi
// đóng tab/cửa sổ). Nếu chỉ ghi một cờ mà vẫn để token trong `localStorage` thì lời
// hứa "không ghi nhớ" là hứa suông — người dùng máy chung đóng trình duyệt xong,
// người sau mở lên vẫn thấy đăng nhập sẵn.
//
// 🔴 MỌI LẦN GHI PHẢI XOÁ BÊN KIA. Không xoá thì token `localStorage` của lần trước
// nằm lại, và `getBoideToken()` (đọc cả hai kho) sẽ hồi sinh một phiên lẽ ra đã kết
// thúc — hỏng đúng ca mà tính năng này sinh ra để chặn.
//
// ⚠️ Đọc SESSION TRƯỚC: ngay sau khi bỏ tick và đăng nhập, hai kho có thể cùng có
// token (kho cũ chưa kịp dọn ở một tab khác) — token vừa cấp mới là token đúng.
const REMEMBER_KEY = "boide_remember_me";
/** Email/tên đăng nhập của lần đăng nhập gần nhất — chỉ để điền sẵn ô, KHÔNG phải phiên. */
const LAST_USERNAME_KEY = "boide_last_username";

function safeStorage(kind: "local" | "session"): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    // Chế độ riêng tư / trình duyệt chặn storage.
    return null;
  }
}

/** Mặc định GHI NHỚ — giữ nguyên hành vi trước khi có tuỳ chọn này. */
export function isRememberLogin(): boolean {
  return safeStorage("local")?.getItem(REMEMBER_KEY) !== "0";
}

/**
 * Chọn kho cho phiên sắp tới. Gọi TRƯỚC khi đăng nhập.
 *
 * Cũng DI CHUYỂN token đang có sang kho mới, để tuỳ chọn này còn đúng khi được đổi
 * lúc đang đăng nhập (ví dụ một công tắc trong phần Tài khoản sau này).
 */
export function setRememberLogin(remember: boolean) {
  const local = safeStorage("local");
  if (!local) return;
  local.setItem(REMEMBER_KEY, remember ? "1" : "0");
  const token = getBoideToken();
  const refresh = getBoideRefreshToken();
  if (token) setBoideToken(token);
  if (refresh) setBoideRefreshToken(refresh);
}

function writeToken(keys: [string, string], token: string) {
  const remember = isRememberLogin();
  const target = safeStorage(remember ? "local" : "session");
  const other = safeStorage(remember ? "session" : "local");
  for (const key of keys) {
    other?.removeItem(key);
    if (token) target?.setItem(key, token);
    else target?.removeItem(key);
  }
}

function readToken(keys: [string, string]): string {
  const session = safeStorage("session");
  const local = safeStorage("local");
  for (const store of [session, local]) {
    for (const key of keys) {
      const value = store?.getItem(key);
      if (value) return value;
    }
  }
  return "";
}

export function getBoideToken(): string {
  return readToken([TOKEN_KEY, LEGACY_TOKEN_KEY]);
}

export function getBoideRefreshToken(): string {
  return readToken([REFRESH_KEY, LEGACY_REFRESH_KEY]);
}

export function setBoideToken(token: string) {
  if (typeof window === "undefined") return;
  writeToken([TOKEN_KEY, LEGACY_TOKEN_KEY], token);
  window.dispatchEvent(
    new CustomEvent(BOIDE_AUTH_CHANGED_EVENT, { detail: { authenticated: !!token } }),
  );
}
export function setBoideRefreshToken(token: string) {
  if (typeof window === "undefined") return;
  writeToken([REFRESH_KEY, LEGACY_REFRESH_KEY], token);
}

/**
 * Email/tên đăng nhập lần trước, để điền sẵn ô đăng nhập.
 *
 * ⚠️ Chỉ ghi khi người dùng CHỌN ghi nhớ, và xoá ngay khi họ bỏ tick: trên máy chung,
 * để lại email của người trước cũng là một kiểu rò rỉ — nhỏ hơn token, nhưng vẫn là
 * thứ họ vừa bảo mình đừng giữ.
 */
export function setLastLoginUsername(username: string) {
  const local = safeStorage("local");
  if (!local) return;
  if (username) local.setItem(LAST_USERNAME_KEY, username);
  else local.removeItem(LAST_USERNAME_KEY);
}

export function getLastLoginUsername(): string {
  return safeStorage("local")?.getItem(LAST_USERNAME_KEY) ?? "";
}
const POLICY_AGREED_KEY_PREFIX = "boide_policy_agreed_";

export function hasAgreedPolicy(username: string): boolean {
  if (typeof window === "undefined" || !username) return false;
  return localStorage.getItem(POLICY_AGREED_KEY_PREFIX + username) === "1";
}
export function setAgreedPolicy(username: string) {
  if (typeof window === "undefined" || !username) return;
  localStorage.setItem(POLICY_AGREED_KEY_PREFIX + username, "1");
}

export function getTuviChartId(): string {
  if (typeof window === "undefined") return "";
  return localStorage.getItem(CHART_KEY) || "";
}
export function setTuviChartId(id: string) {
  if (typeof window === "undefined") return;
  if (id) localStorage.setItem(CHART_KEY, id);
  else localStorage.removeItem(CHART_KEY);
}

function authHeaders(extra?: HeadersInit): HeadersInit {
  const token = getBoideToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(extra || {}),
  };
}

/**
 * `detail` BE trả khi token thật sự không dùng được — gặp là đăng xuất.
 * - "Not authenticated": thiếu header Authorization (FastAPI OAuth2).
 * - "Token is invalid": token không còn trong Redis (hết hạn / bị purge khi đổi mật khẩu).
 *
 * 🔴 KHÔNG thêm "Auth service temporarily unavailable" vào đây: BE trả detail đó khi
 * Redis nghẽn — token vẫn tốt, logout lúc ấy là đá cả loạt user ra đúng lúc quá tải.
 */
const AUTH_ERROR_DETAILS = new Set(["Not authenticated", "Token is invalid"]);

export class BoideAuthError extends Error {
  constructor(message = "Not authenticated") {
    super(message);
    this.name = "BoideAuthError";
  }
}

type ApiErrorItem = { detail?: unknown; msg?: unknown };
type ApiEnvelope = {
  data?: unknown;
  errors?: ApiErrorItem[];
  error_description?: string;
  status?: string;
  reason?: string;
  quick_topup_offer?: QuickTopupOffer;
};

async function unwrap<T>(res: Response): Promise<T> {
  // Backend trả 503 cho mọi API công khai khi admin bật chế độ bảo trì. Báo cho
  // store biết để cả app chuyển sang màn bảo trì, thay vì để người dùng gặp một
  // chuỗi lỗi rời rạc không nói được vì sao. Hàm này tự chặn nhịp và tự đi hỏi lại
  // `/v1/maintenance` — 503 còn đến từ nginx lúc quá tải, hai thứ khác nhau.
  noteMaintenanceResponse(res.status);
  const text = await res.text();
  let json: ApiEnvelope;
  try {
    json = text ? (JSON.parse(text) as ApiEnvelope) : {};
  } catch {
    throw new Error(`Phản hồi không phải JSON (${res.status}): ${text.slice(0, 120)}`);
  }
  const errDetail =
    Array.isArray(json.errors) && json.errors[0]?.detail ? String(json.errors[0].detail) : "";
  if (AUTH_ERROR_DETAILS.has(errDetail)) {
    throw new BoideAuthError(errDetail);
  }
  if (!res.ok || json.status === "failed") {
    const msg =
      json.error_description ||
      errDetail ||
      (Array.isArray(json.errors) &&
        json.errors
          .map((e) => e.detail || e.msg)
          .filter(Boolean)
          .join(", ")) ||
      `HTTP ${res.status}`;
    const error = Object.assign(new Error(String(msg)), json);
    // Bảo trì RIÊNG một tính năng (BE v121): lời nhắn đã nằm trong `msg` nên toast của
    // nơi gọi hiện đúng câu admin soạn; tải lại trạng thái để banner trang hiện ngay.
    if ((json as { reason?: string }).reason === "feature_maintenance") {
      noteFeatureMaintenance();
    }
    // Ưu tiên structured error; regex chỉ là fallback tương thích khi backend
    // cũ chưa trả reason (giữ trong giai đoạn rollout — xem spec Quick Topup).
    if (json.reason === "insufficient_funds") {
      requestQuickTopup(
        "Số dư không đủ để tiếp tục. Nạp nhanh ngay tại đây để tiếp tục.",
        json.quick_topup_offer,
      );
    } else if (isInsufficientFundsError(error)) {
      requestQuickTopup("Số dư không đủ để tiếp tục. Nạp nhanh ngay tại đây để tiếp tục.");
    }
    throw error;
  }
  return (json.data ?? json) as T;
}

/** BE trả 409 `reason: "in_progress"` khi đúng lượt bói trả phí này đang được xử lý
 *  ở một request khác (bấm lặp, hoặc thử lại sau khi mất kết nối) — xem
 *  `src/services/wallet/inflight_guard.py` bên ai-be. Không phải lỗi thật. */
export function isInProgressError(e: unknown): boolean {
  return (
    typeof e === "object" && e !== null && (e as { reason?: unknown }).reason === "in_progress"
  );
}

const IN_PROGRESS_RETRY_MS = 6000;
const IN_PROGRESS_MAX_WAIT_MS = 4 * 60_000;

/**
 * Gọi `first()`; nếu BE báo lượt này đang chạy ở request khác thì chờ rồi gọi `again()`
 * cho tới khi lấy được kết quả của chính lượt ấy.
 *
 * 🔴 `again` PHẢI là lời gọi KHÔNG tính phí thêm (vd `force_refresh: false`): BE trả
 * cache / kết quả vừa xong cho nó. Truyền lại `force_refresh: true` là trả tiền lần
 * nữa đúng lúc lượt đầu vừa xong.
 */
async function retryWhileInProgress<T>(
  first: () => Promise<T>,
  again: () => Promise<T>,
): Promise<T> {
  try {
    return await first();
  } catch (e) {
    if (!isInProgressError(e)) throw e;
  }
  const startedAt = Date.now();
  for (;;) {
    await new Promise((r) => setTimeout(r, IN_PROGRESS_RETRY_MS));
    try {
      return await again();
    } catch (e) {
      if (!isInProgressError(e) || Date.now() - startedAt > IN_PROGRESS_MAX_WAIT_MS) throw e;
    }
  }
}

// ============================================================
// Web Push / PWA
// ============================================================

export type WebPushResult = { sent: number; failed: number };

export async function registerWebPushSubscription(
  subscription: PushSubscription,
  deviceName?: string,
): Promise<{ registered: boolean }> {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error("Push subscription không hợp lệ");
  }
  const res = await fetch(`${BOIDE_API_URL}/v1/push/web-subscriptions`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: json.keys,
      device_name: deviceName?.slice(0, 255),
    }),
  });
  return unwrap<{ registered: boolean }>(res);
}

export async function unregisterWebPushSubscription(endpoint: string): Promise<void> {
  const res = await fetch(`${BOIDE_API_URL}/v1/push/web-subscriptions`, {
    method: "DELETE",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ endpoint }),
  });
  await unwrap<{ removed: boolean }>(res);
}

export async function sendWebPushTest(): Promise<WebPushResult> {
  const res = await fetch(`${BOIDE_API_URL}/v1/push/test`, {
    method: "POST",
    headers: authHeaders(),
  });
  return unwrap<WebPushResult>(res);
}

// ============================================================
// Auth
// ============================================================

// /v1/users/me returns the SAME shape as login (no `id` field)
export type BoideUser = {
  username: string;
  name?: string | null;
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
  avatar?: string | null;
  email_verified?: boolean;
  last_login_at?: string | null;
  login_type?: string;
  type?: string;
  credits?: number | null;
  amount?: number | null;
  // Only present on login/sign-up/me responses
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
};

export type BoideProfile = {
  id: number;
  username: string;
  name?: string | null;
  full_name?: string | null;
  real_name?: string | null;
  email?: string | null;
  phone?: string | null;
  avatar?: string | null;
  birth_date?: string | null;
  birth_time?: string | null;
  birth_calendar?: "solar" | "lunar" | null;
  /**
   * CHUỖI hiển thị do BE tự tính, KHÔNG phải số ngày: `"DD/MM/YYYY ÂL"`, có thể
   * kèm hậu tố `" (nhuận)"` (cột `users.lunar_birth_date TEXT`). Muốn lấy số
   * ngày thì dùng `lunarBirthDay()` — đừng đưa thẳng chuỗi này vào form/payload.
   */
  lunar_birth_date?: string | null;
  lunar_birth_month?: number | null;
  lunar_birth_year?: number | null;
  lunar_is_leap_month?: boolean;
  birth_location?: string | null;
  current_location?: string | null;
  occupation?: string | null;
  life_goals?: string | null;
  hobbies?: string[];
  profile_completed?: boolean;
  gender?: "male" | "female" | string | null;
  social_links?: SocialLinks | null;
  default_diviner_id?: string | null;
  live_personal_intelligence_enabled?: boolean;
};

/** Link mạng xã hội (lưu dạng JSONB map trên users.social_links). */
export type SocialLinks = {
  facebook?: string;
  zalo?: string;
  instagram?: string;
  tiktok?: string;
  youtube?: string;
};

/**
 * Header `Authorization: Basic` cho `/v1/users/login`.
 *
 * 🔴 CHỈ ASCII. Starlette giải Basic bằng `.decode("ascii")`, nên một mật khẩu có
 * dấu tiếng Việt (hoặc emoji) sẽ bị BE từ chối ở tầng framework — trước cả khi tới
 * controller — và người dùng chỉ thấy "đăng nhập thất bại" không lý do. `btoa` cũng
 * tự ném với ký tự > U+00FF, nên chặn sớm ở đây và nói thẳng ra. Cùng lý lẽ,
 * `validatePassword` cấm ký tự ngoài ASCII ngay từ lúc ĐẶT mật khẩu: đặt được mà
 * không đăng nhập được là ca hỏng tệ nhất.
 */
function basicAuthHeader(username: string, password: string): string {
  try {
    return btoa(`${username}:${password}`);
  } catch {
    throw new Error(
      "Tên đăng nhập hoặc mật khẩu chứa ký tự không hỗ trợ (chỉ dùng chữ không dấu, số và ký hiệu).",
    );
  }
}

export async function loginBoide(username: string, password: string): Promise<BoideUser> {
  const basic = basicAuthHeader(username, password);
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/login`, {
    method: "POST",
    headers: { Authorization: `Basic ${basic}` },
  });
  const data = await unwrap<BoideUser>(res);
  if (data.access_token) setBoideToken(data.access_token);
  if (data.refresh_token) setBoideRefreshToken(data.refresh_token);
  return data;
}

export async function googleLoginBoide(
  accessToken: string,
  referralCode?: string,
  track?: string,
): Promise<BoideUser> {
  const body: Record<string, unknown> = { access_token: accessToken };
  if (referralCode) body.referral_code = referralCode;
  if (track) body.track = track;
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/gg-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await unwrap<BoideUser>(res);
  if (data.access_token) setBoideToken(data.access_token);
  if (data.refresh_token) setBoideRefreshToken(data.refresh_token);
  return data;
}

/**
 * Đăng ký bằng email + mật khẩu.
 *
 * 🔴 KHÔNG trả token và KHÔNG đăng nhập được ngay: BE tạo user Firebase → tạo user
 * Postgres → gửi mail xác thực, rồi dừng ở đó (`ctr_sign_up` cố ý không set token vì
 * tài khoản chưa activate). Cổng `email_verified` nằm ở `/login`. Vì vậy call site
 * phải đưa người dùng sang màn "kiểm tra hộp thư", đừng điều hướng vào app như một
 * lượt đăng nhập thành công.
 *
 * Hai nhánh `setBoideToken` bên dưới giữ lại phòng khi BE đổi ý và trả token — không
 * có token thì chúng là no-op.
 */
export async function signUpBoide(input: {
  email: string;
  username: string;
  password: string;
  referralCode?: string;
  track?: string;
}): Promise<BoideUser> {
  const body: Record<string, unknown> = {
    email: input.email.trim(),
    username: input.username.trim(),
    password: input.password,
  };
  if (input.referralCode) body.referral_code = input.referralCode;
  if (input.track) body.track = input.track;
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/sign-up`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await unwrap<BoideUser>(res);
  if (data.access_token) setBoideToken(data.access_token);
  if (data.refresh_token) setBoideRefreshToken(data.refresh_token);
  return data;
}

// ============================================================
// Mật khẩu (quên / đặt lại / đổi / thêm)
// ============================================================

export type PasswordActionResult = { status?: string; message?: string };

/**
 * Gửi lại mail xác thực — dùng khi `/login` trả "User is not verified".
 *
 * Đòi cả mật khẩu vì lúc này chưa có token: BE xác thực lại danh tính rồi mới
 * nhờ Firebase gửi mail (`ctr_resend_verification`).
 */
export async function resendVerificationEmail(
  username: string,
  password: string,
): Promise<PasswordActionResult> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/resend-verification`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username: username.trim(), password }),
  });
  return unwrap<PasswordActionResult>(res);
}

/**
 * Quên mật khẩu — BE nhờ Firebase gửi mail đặt lại.
 *
 * 🔴 BE LUÔN trả 200 kèm CÙNG một câu, bất kể username/email có tồn tại hay không
 * (chống dò tài khoản — xem `ctr_forgot_password`). Đừng dựng UI kiểu "email này
 * chưa đăng ký" từ response: thông tin đó không có trong đây, và hứa hẹn ngược lại
 * là mở lại đúng lỗ hổng BE vừa bịt. Chỉ 429 (quá nhiều yêu cầu) mới là lỗi thật.
 */
export async function forgotPassword(username: string): Promise<PasswordActionResult> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username: username.trim() }),
  });
  return unwrap<PasswordActionResult>(res);
}

/**
 * Đặt mật khẩu mới khi ĐÃ đăng nhập — dùng cho cả "đổi mật khẩu" lẫn "thêm mật khẩu"
 * cho tài khoản Google/Apple (BE là một endpoint duy nhất: `POST /v1/users/reset-password`).
 *
 * 🔴 GỌI XONG LÀ TOKEN HIỆN TẠI CHẾT: BE dọn cache đăng nhập (`repo_purge_user_cache`)
 * để buộc đăng nhập lại trên MỌI thiết bị — kể cả thiết bị vừa đổi. Mọi call site phải
 * `logoutBoide()` rồi đưa người dùng về /login ngay sau đó, nếu không request kế tiếp
 * rơi vào chuỗi 401 không ai hiểu vì sao.
 *
 * ⚠️ BE KHÔNG kiểm mật khẩu hiện tại (body chỉ có `new_password`). Việc bắt nhập mật
 * khẩu cũ nằm ở FE — xem `verifyCurrentPassword`.
 */
export async function changePassword(newPassword: string): Promise<PasswordActionResult> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/reset-password`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ new_password: newPassword }),
  });
  return unwrap<PasswordActionResult>(res);
}

/**
 * Kiểm mật khẩu hiện tại bằng chính endpoint đăng nhập.
 *
 * ⚠️ CỐ Ý KHÔNG ghi token trả về (khác `loginBoide`): đây là một phép KIỂM giữa luồng
 * đổi mật khẩu, ghi đè token ở đây chỉ làm rối bước đăng xuất ngay sau đó.
 *
 * Trả `false` khi mật khẩu sai; ném tiếp mọi lỗi khác (mạng, 5xx, tài khoản bị khoá)
 * để call site không báo nhầm "sai mật khẩu" cho một sự cố máy chủ.
 */
export async function verifyCurrentPassword(username: string, password: string): Promise<boolean> {
  const basic = basicAuthHeader(username, password);
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/login`, {
    method: "POST",
    headers: { Authorization: `Basic ${basic}` },
  });
  try {
    await unwrap<BoideUser>(res);
    return true;
  } catch (err) {
    if (isWrongPasswordError(err)) return false;
    throw err;
  }
}

/** BE trả nguyên văn hằng `ERROR_PASSWORD_IS_NOT_CORRECT` ở `src/utils/constant/auth.py`. */
export function isWrongPasswordError(err: unknown): boolean {
  return err instanceof Error && /password is not correct/i.test(err.message);
}

/** BE trả `ERROR_USER_IS_NOT_VERIFIED` kèm HTTP 403 — FE mời gửi lại mail xác thực. */
export function isNotVerifiedError(err: unknown): boolean {
  return err instanceof Error && /user is not verified/i.test(err.message);
}

export async function getMe(): Promise<BoideUser> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/me`, { headers: authHeaders() });
  return unwrap<BoideUser>(res);
}

export type DeleteAccountResult = {
  deleted: boolean;
  deleted_at?: string | null;
  /** Số ngày phải chờ trước khi đăng ký lại bằng chính email này (BE: 90 ngày). */
  can_recreate_after_days?: number | null;
};

/**
 * Xoá tài khoản (xoá mềm ở BE). Token hiện tại chết ngay sau khi gọi thành công
 * vì BE dọn cache đăng nhập → mọi request sau đó đều 401.
 */
export async function deleteAccount(reason?: string): Promise<DeleteAccountResult> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/me`, {
    method: "DELETE",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ reason: reason?.trim() || null }),
  });
  return unwrap<DeleteAccountResult>(res);
}

export const POLICY_CONTENT =
  "Chúng tôi có thể thu thập thông tin tài khoản, thông tin liên hệ, lịch sử tham vấn, giao dịch và dữ liệu kỹ thuật cần thiết để vận hành dịch vụ. Nội dung trên nền tảng chỉ mang tính tham khảo. Tiền nạp vào hệ thống được quy đổi thành số dư dịch vụ và không được hoàn lại hoặc rút ra dưới bất kỳ hình thức nào.";

export async function agreePolicy(
  content: string = POLICY_CONTENT,
): Promise<{ agreed: boolean; agreed_at: string }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/policy/agree`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ content }),
  });
  return unwrap(res);
}

/** Ngày âm lịch hợp lệ tối đa (tháng âm 29 hoặc 30 ngày). */
const MAX_LUNAR_DAY = 30;
/** Khớp phần "DD/MM/YYYY" ở đầu chuỗi `lunar_birth_date` của BE. */
const LUNAR_DISPLAY_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/;

/**
 * Bóc số ngày âm lịch từ `profile.lunar_birth_date`.
 *
 * BE lưu trường này dạng chuỗi hiển thị (`"24/06/1997 ÂL"`) nên dùng thẳng làm
 * số ngày sẽ ra dữ liệu rác kiểu `"24/06/1997 ÂL/06/1997"` và payload sai kiểu.
 * `lunar_birth_month`/`lunar_birth_year` thì đã là số nguyên — dùng trực tiếp.
 */
export function lunarBirthDay(profile: BoideProfile): number | null {
  const match = String(profile.lunar_birth_date ?? "")
    .trim()
    .match(LUNAR_DISPLAY_RE);
  if (!match) return null;
  const day = Number(match[1]);
  return day >= 1 && day <= MAX_LUNAR_DAY ? day : null;
}

export async function getProfile(): Promise<BoideProfile> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/profile`, { headers: authHeaders() });
  return unwrap<BoideProfile>(res);
}

export async function updateProfile(input: Partial<BoideProfile>): Promise<BoideProfile> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/profile`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  return unwrap<BoideProfile>(res);
}

export async function updateDefaultDiviner(
  divinerId: string,
): Promise<{ default_diviner_id: string }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/profile/default-diviner`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ diviner_id: divinerId }),
  });
  return unwrap(res);
}

/**
 * @deprecated Ghi vào cột cũ trên account-be — ai-be KHÔNG còn đọc cột đó (v127).
 * Dùng `updateAiContextSettings({ live_memory })`.
 */
export async function updateLivePersonalIntelligence(
  enabled: boolean,
): Promise<{ live_personal_intelligence_enabled: boolean }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/profile/live-personal-intelligence`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ enabled }),
  });
  return unwrap(res);
}

// ─── Dữ kiện cá nhân thầy được dùng (v127) ──────────────────────────────────
// Mỗi khoá là một công tắc; MẶC ĐỊNH TẮT HẾT. Khoá phải khớp `CONTEXT_KEYS` trong
// `src/services/ai_context/ai_context_settings.py` của ai-be.
//
// 🔴 Gọi `BOIDE_API_URL` (ai-be), KHÔNG phải `BOIDE_ACCOUNT_API_URL`: dù đường dẫn
// nằm dưới `/v1/users`, chính ai-be là nơi dựng prompt và đọc cài đặt này —
// account-be không có endpoint đó.

export type AiContextKey = "profile" | "linked_sessions" | "live_memory";
export type AiContextSettings = Record<AiContextKey, boolean>;

export const AI_CONTEXT_DEFAULTS: AiContextSettings = {
  profile: false,
  linked_sessions: false,
  live_memory: false,
};

export async function getAiContextSettings(): Promise<AiContextSettings> {
  const res = await fetch(`${BOIDE_API_URL}/v1/users/profile/ai-context`, {
    headers: authHeaders(),
  });
  const data = await unwrap<{ settings: Partial<AiContextSettings> }>(res);
  return { ...AI_CONTEXT_DEFAULTS, ...(data?.settings ?? {}) };
}

export async function updateAiContextSettings(
  changes: Partial<AiContextSettings>,
): Promise<AiContextSettings> {
  const res = await fetch(`${BOIDE_API_URL}/v1/users/profile/ai-context`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(changes),
  });
  const data = await unwrap<{ settings: Partial<AiContextSettings> }>(res);
  return { ...AI_CONTEXT_DEFAULTS, ...(data?.settings ?? {}) };
}

/** Upload ảnh đại diện (multipart). KHÔNG set Content-Type cho FormData. */
export async function uploadAvatar(file: File): Promise<{ avatar: string }> {
  const fd = new FormData();
  fd.append("avatar", file);
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/avatar`, {
    method: "POST",
    headers: authHeaders(),
    body: fd,
  });
  return unwrap<{ avatar: string }>(res);
}

export type DailyFortuneCard = {
  key: "energy" | "direction" | "element";
  label: string;
  value: string;
  hint: string;
};

export type DailyFortuneResponse = {
  date: string;
  lunar_date: string;
  personalized: boolean;
  energy_score: number;
  cards: DailyFortuneCard[];
  /** BE cũ / cache trong ngày có thể chưa có hai khoá dưới. */
  lucky?: VanKhiMayMan | null;
  tiet_khi?: VanKhiTietKhi | null;
};

export async function getDailyFortuneMe(): Promise<DailyFortuneResponse> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/daily-fortune/me`, { headers: authHeaders() });
  return unwrap<DailyFortuneResponse>(res);
}

// ============================================================
// Điểm danh (daily check-in) & Chuỗi bói (streak + leaderboard)
// ============================================================

export type CheckinSlot = {
  day: number; // 1..7
  reward: number; // VND
  is_bonus: boolean; // ngày thứ 7
  claimed: boolean;
};

export type CheckinStatus = {
  checked_in_today: boolean;
  current_streak: number;
  today_cycle_day: number | null;
  next_cycle_day: number;
  next_reward: number;
  cycle: CheckinSlot[];
};

export type CheckinResult = {
  success: boolean;
  already_checked_in: boolean;
  cycle_day: number | null;
  streak: number | null;
  reward: number | null;
  new_balance: number | null;
  message: string;
};

export async function getCheckinStatus(): Promise<CheckinStatus> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/checkin/status`, { headers: authHeaders() });
  return unwrap<CheckinStatus>(res);
}

export async function doCheckin(): Promise<CheckinResult> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/checkin`, {
    method: "POST",
    headers: authHeaders(),
  });
  return unwrap<CheckinResult>(res);
}

export type StreakMe = {
  current_streak: number;
  longest_streak: number;
  total_boi_days: number;
  last_boi_date: string | null;
  join_leaderboard: boolean;
};

export type LeaderboardEntry = {
  rank: number;
  user_id: number;
  display_name: string;
  avatar: string | null;
  score: number;
};

export type LeaderboardPeriod = "all" | "week" | "month";

export type LeaderboardResponse = {
  period: string;
  entries: LeaderboardEntry[];
  my_rank: number | null;
  my_score: number;
  join_leaderboard: boolean;
  total: number;
  limit: number;
  offset: number;
};

export async function getMyStreak(): Promise<StreakMe> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/streak/me`, { headers: authHeaders() });
  return unwrap<StreakMe>(res);
}

export async function setStreakLeaderboard(
  join: boolean,
): Promise<{ join_leaderboard: boolean; message: string }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/streak/me/leaderboard`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ join }),
  });
  return unwrap(res);
}

export async function getLeaderboard(
  period: LeaderboardPeriod = "all",
  limit = 50,
  offset = 0,
): Promise<LeaderboardResponse> {
  const res = await fetch(
    `${BOIDE_ACCOUNT_API_URL}/v1/streak/leaderboard?period=${period}&limit=${limit}&offset=${offset}`,
    { headers: authHeaders() },
  );
  return unwrap<LeaderboardResponse>(res);
}

export function logoutBoide() {
  if (typeof window === "undefined") return;
  // Dọn CẢ HAI kho, không phụ thuộc tuỳ chọn ghi nhớ đang bật hay tắt: người vừa đổi
  // tuỳ chọn giữa chừng mà chỉ dọn một kho là đăng xuất xong vẫn còn token bên kia.
  for (const store of [safeStorage("local"), safeStorage("session")]) {
    for (const key of [TOKEN_KEY, LEGACY_TOKEN_KEY, REFRESH_KEY, LEGACY_REFRESH_KEY]) {
      store?.removeItem(key);
    }
  }
}

// ============================================================
// Payments
// ============================================================

export type SepayOrder = {
  code: string;
  qr_url?: string | null;
  url_qr?: string | null;
  amount: number;
  bank?: string | null;
  account?: string | null;
  status: string;
  created_at?: string;
  paid_at?: string | null;
  credited_amount?: number | null;
  bonus_amount?: number | null;
  promotion_source?: string | null;
  promotion_rate?: number | null;
  quick_topup_offer_id?: number | null;
  quick_topup_bonus_rate?: number | null;
  quick_topup_expires_at?: string | null;
  topup_bonus_campaign_version?: number | null;
  topup_bonus_tier_id?: number | null;
  topup_bonus_campaign_name?: string | null;
  topup_bonus_threshold?: number | null;
  topup_bonus_amount?: number | null;
  estimated_bonus_amount?: number | null;
  estimated_credited_amount?: number | null;
  // Đơn MUA GÓI (v98): hiện "Gói Lớn — 200.000đ/ngày trong 30 ngày" thay cho dòng
  // "sẽ được cộng X vào ví" — ví KHÔNG tăng khi mua gói.
  // ⚠️ Chỉ có ở phản hồi TẠO đơn; `GET /orders/{code}/status` không trả hai khóa
  // này, nên giữ lại bản của lượt tạo thay vì đợi lượt poll bù cho.
  plan?: {
    slug: string;
    name: string;
    daily_quota: number;
    duration_days: number;
  } | null;
  upgrade?: {
    days_left?: number;
    ends_on?: string | null;
    from_plan_slug?: string;
    from_daily_quota?: number;
    to_plan_slug?: string;
    to_daily_quota?: number;
    new_value?: number;
    refund_value?: number;
    amount_due?: number;
    expires_at?: string | null;
  } | null;
};

export type TopupBonusTier = {
  id?: number | null;
  threshold_amount: number;
  bonus_amount: number;
  is_active: boolean;
};

export type TopupBonusCampaign = {
  id: number;
  name: string;
  version: number;
  active: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  server_now: string;
  tiers: TopupBonusTier[];
};

export type MonthlyTopupTier = {
  threshold: number;
  rate: number;
  unlocked: boolean;
  is_next: boolean;
};

export type MonthlyTopupProgress = {
  month: string;
  timezone: string;
  paid_amount: number;
  unlocked_rate: number;
  next_threshold: number | null;
  next_rate: number | null;
  remaining_amount: number;
  progress_percent: number;
  tiers: MonthlyTopupTier[];
};

export type VoucherValidation = {
  valid: boolean;
  type: string;
  value: number;
  amount_to_pay: number;
  amount_credited: number;
  benefit_type: string;
  benefit_amount: number;
  message: string;
};

export async function validateVoucher(code: string, amount: number): Promise<VoucherValidation> {
  const res = await fetch(
    `${BOIDE_API_URL}/v1/vouchers/${encodeURIComponent(code)}/validate?amount=${amount}`,
    { headers: authHeaders() },
  );
  return unwrap<VoucherValidation>(res);
}

export type VoucherRedeem = {
  success: boolean;
  credited_amount: number;
  new_balance: number;
  message?: string;
};

// Đổi giftcode: cộng thẳng số dư cho user hiện tại (khác validateVoucher — kia chỉ
// kiểm tra mã để áp vào đơn nạp). Gọi BE qua BOIDE_API_URL, không hardcode host.
export async function redeemVoucher(code: string): Promise<VoucherRedeem> {
  const res = await fetch(`${BOIDE_API_URL}/v1/vouchers/${encodeURIComponent(code)}/redeem`, {
    method: "POST",
    headers: authHeaders(),
  });
  return unwrap<VoucherRedeem>(res);
}

export async function createSepayOrder(
  amount: number,
  voucherCode?: string,
  referralCode?: string,
  quickTopupOfferId?: number,
): Promise<SepayOrder> {
  const body: Record<string, unknown> = { amount };
  if (voucherCode) body.voucher_code = voucherCode;
  if (referralCode) body.referral_code = referralCode;
  if (quickTopupOfferId) body.quick_topup_offer_id = quickTopupOfferId;
  const res = await fetch(`${BOIDE_API_URL}/v1/payments/sepay/orders`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  return unwrap<SepayOrder>(res);
}

export async function getQuickTopupOffer(): Promise<QuickTopupOffer | null> {
  const res = await fetch(`${BOIDE_API_URL}/v1/payments/sepay/quick-topup/offers`, {
    headers: authHeaders(),
  });
  // data: null khi hôm nay chưa có offer — unwrap trả envelope, validate bằng id
  const offer = await unwrap<QuickTopupOffer | null>(res);
  return offer && typeof offer.id === "number" ? offer : null;
}

export async function ensureQuickTopupOffer(): Promise<QuickTopupOffer> {
  const res = await fetch(`${BOIDE_API_URL}/v1/payments/sepay/quick-topup/offers`, {
    method: "POST",
    headers: authHeaders(),
  });
  return unwrap<QuickTopupOffer>(res);
}

export async function getSepayOrderStatus(code: string): Promise<SepayOrder> {
  const res = await fetch(`${BOIDE_API_URL}/v1/payments/sepay/orders/${code}/status`, {
    headers: authHeaders(),
  });
  return unwrap<SepayOrder>(res);
}

export async function getMonthlyTopupProgress(): Promise<MonthlyTopupProgress> {
  const res = await fetch(`${BOIDE_API_URL}/v1/payments/sepay/monthly-progress`, {
    headers: authHeaders(),
  });
  return unwrap<MonthlyTopupProgress>(res);
}

export async function getTopupBonusCampaign(): Promise<TopupBonusCampaign> {
  const res = await fetch(`${BOIDE_API_URL}/v1/payments/sepay/bonus-campaign`, {
    headers: authHeaders(),
  });
  return unwrap<TopupBonusCampaign>(res);
}

// ── Gói quota ngày (migrations v98/v99) ──────────────────────────────────────
//
// Mô hình: mỗi lần mua sinh MỘT ĐOẠN mang sẵn mức quota của nó, và các đoạn
// ĐƯỢC PHÉP chồng nhau — quota một ngày là TỔNG mức của mọi đoạn phủ ngày đó.
// Vì vậy FE không được coi "gói của tôi" là một thứ số ít: `subscriptions` là
// một MẢNG, và tên gói hiện lên là tên của (các) đoạn đang chạy.
//
// 🔴 Mua gói và nâng cấp đều đi qua ĐÚNG luồng SePay của trang nạp tiền
// (`createSepayOrder` kèm `plan_slug`), không có endpoint "mua" riêng. Giá do DB
// quyết: `amount` client gửi bị BỎ QUA khi có `plan_slug`.

export type SubscriptionPlan = {
  id: number;
  slug: string;
  name: string;
  description?: string | null;
  price: number;
  /**
   * Giá gốc để gạch ngang cạnh giá bán (khuyến mãi). BE chưa trả trường này —
   * `compareAtPrice` trong `plan-grid.tsx` có bảng dự phòng theo slug, và khi BE
   * bổ sung cột thì FE tự ăn ngay, không phải deploy lại.
   */
  original_price?: number | null;
  daily_quota: number;
  duration_days: number;
  is_active?: boolean;
  sort_order?: number;
};

export type UserSubscription = {
  id: number;
  plan_slug: string;
  plan_name: string;
  daily_quota: number;
  duration_days: number;
  price_paid: number;
  starts_on?: string | null;
  ends_on?: string | null;
  status: string;
  source: string;
  order_code?: string | null;
  created_at?: string | null;
  // Tính CẢ hôm nay: gói hết hạn hôm nay vẫn còn quota tới nửa đêm.
  days_left: number;
  is_live: boolean;
};

export type QuotaStatus = {
  quota_date: string;
  daily_quota: number;
  // `quota_limit` >= `daily_quota`: dòng sổ hôm nay có thể giữ trần cao hơn tổng
  // các đoạn hiện tại (mua rồi hoàn/thu hồi trong ngày). Thanh tiến độ phải chia
  // cho con số này, không phải `daily_quota`.
  quota_limit: number;
  used_amount: number;
  remaining: number;
  has_subscription: boolean;
  subscriptions: UserSubscription[];
  wallet_amount: number;
  // Tiêu được thật sự = quota còn lại + ví. Đây là con số quyết định "bói được
  // hay không", nên mọi câu "số dư không đủ" phải đối chiếu với nó.
  spendable: number;
};

export type SubscriptionUpgradeOption = {
  plan_slug: string;
  plan_name: string;
  daily_quota: number;
  amount_due: number;
  new_value: number;
  refund_value: number;
};

export type SubscriptionUpgradeOptions = {
  ok: boolean;
  reason?: string | null;
  days_left: number;
  min_days: number;
  current_daily_quota?: number;
  ends_on?: string | null;
  options: SubscriptionUpgradeOption[];
};

export type SubscriptionUpgradeQuote = {
  ok: boolean;
  reason?: string | null;
  days_left: number;
  min_days: number;
  subscription_id: number;
  min_transfer?: number;
  from_plan_slug?: string;
  from_plan_name?: string;
  from_daily_quota?: number;
  to_plan_slug?: string;
  to_plan_name?: string;
  to_daily_quota?: number;
  ends_on?: string | null;
  new_value?: number;
  refund_value?: number;
  amount_due?: number;
};

export async function listSubscriptionPlans(): Promise<SubscriptionPlan[]> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/subscriptions/plans`, {
    headers: authHeaders(),
  });
  const data = await unwrap<{ plans?: SubscriptionPlan[] } | SubscriptionPlan[]>(res);
  // BE trả `{plans: [...]}`; chịu luôn dạng mảng trần để thứ tự deploy nào cũng an toàn.
  return Array.isArray(data) ? data : (data?.plans ?? []);
}

export async function getMyQuotaStatus(): Promise<QuotaStatus> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/subscriptions/me`, {
    headers: authHeaders(),
  });
  return unwrap<QuotaStatus>(res);
}

export async function getSubscriptionUpgradeOptions(
  subscriptionId: number,
): Promise<SubscriptionUpgradeOptions> {
  const res = await fetch(
    `${BOIDE_ACCOUNT_API_URL}/v1/subscriptions/upgrade-options?subscription_id=${subscriptionId}`,
    { headers: authHeaders() },
  );
  return unwrap<SubscriptionUpgradeOptions>(res);
}

export async function getSubscriptionUpgradeQuote(
  subscriptionId: number,
  planSlug: string,
): Promise<SubscriptionUpgradeQuote> {
  const query = new URLSearchParams({
    subscription_id: String(subscriptionId),
    plan_slug: planSlug,
  });
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/subscriptions/upgrade-quote?${query}`, {
    headers: authHeaders(),
  });
  // ⚠️ Không nâng được cũng là 200 kèm `ok=false` + `reason` — đừng bắt lỗi ở đây.
  return unwrap<SubscriptionUpgradeQuote>(res);
}

/** Đơn MUA GÓI. Không truyền `amount`: giá lấy từ DB, gửi lên cũng bị bỏ qua. */
export async function createPlanOrder(planSlug: string): Promise<SepayOrder> {
  const res = await fetch(`${BOIDE_API_URL}/v1/payments/sepay/orders`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ plan_slug: planSlug }),
  });
  return unwrap<SepayOrder>(res);
}

/**
 * Đơn NÂNG CẤP. Server tính lại tiền bù từ đầu — KHÔNG gửi số tiền FE đang hiện:
 * giữa lúc báo giá và lúc bấm có thể đã qua nửa đêm và số ngày còn lại tụt một ngày.
 */
export async function createPlanUpgradeOrder(
  subscriptionId: number,
  planSlug: string,
): Promise<SepayOrder> {
  const res = await fetch(`${BOIDE_API_URL}/v1/payments/sepay/orders`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ upgrade_subscription_id: subscriptionId, plan_slug: planSlug }),
  });
  return unwrap<SepayOrder>(res);
}

export type WalletTransactionType = "all" | "payment" | "chat" | string;

export type WalletTransaction = {
  id: number | string;
  /** ⚠️ CHỈ phần VÍ. Lượt tiêu bằng gói có `amount = 0` mà vẫn tốn tiền — xem dưới. */
  amount: number;
  old_amount: number;
  current_amount: number;
  type: string;
  status: string;
  created_at: string;
  // Tên sự kiện + ghi chú (vd thưởng hộp thư, cộng bù nạp lỗi) — có thể null.
  name?: string | null;
  note?: string | null;
  // ── Quota gói (v98) ───────────────────────────────────────────────────────
  // 🔴 Hạn mức ngày bị trừ TRƯỚC ví, nên `amount` một mình KHÔNG phải số tiền
  // người dùng đã tiêu: một lượt bói 250.000đ của người có gói 200.000đ/ngày ghi
  // `amount = -50.000` và `quota_amount = -200.000`. Hiện mỗi `amount` là nói với
  // họ rằng lượt ấy tốn 50.000đ.
  // Dấu: cùng chiều với `amount` (âm = trừ). Bản ghi cũ không có các trường này.
  payment_method?: "wallet" | "quota" | "mixed" | string;
  quota_amount?: number;
  quota_old_amount?: number | null;
  quota_current_amount?: number | null;
  /** Ngày (lịch VN) của hạn mức bị trừ — quota reset lúc 0h nên nó có thể khác ngày giao dịch. */
  quota_date?: string | null;
  /** `amount + quota_amount` do BE tính sẵn. Đây mới là số tiền thật của lượt đó. */
  total_amount?: number;
};

export type WalletTransactionsParams = {
  type?: WalletTransactionType;
  page?: number;
  limit?: number;
};

export type WalletTransactionsResponse = {
  total_page: number;
  total: number;
  page: number;
  limit: number;
  transactions: WalletTransaction[];
};

export async function listWalletTransactions(
  params: WalletTransactionsParams = {},
): Promise<WalletTransactionsResponse> {
  const query = new URLSearchParams({
    type: params.type || "all",
    page: String(params.page || 1),
    limit: String(params.limit || 10),
  });
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/users/transactions?${query.toString()}`, {
    headers: authHeaders(),
  });
  return unwrap<WalletTransactionsResponse>(res);
}

// ============================================================
// Diviners + Sessions
// ============================================================

export type FortuneType = "iching" | "tuvi" | "bazi" | "tong_hop";

export type Diviner = {
  id: string;
  ten: string;
  title?: string;
  mo_ta?: string;
  phong_cach?: string;
  avatar?: string; // presigned URL từ MinIO
};

export type SessionMetadata = {
  fortune_type?: FortuneType;
  tuvi_chart_id?: string;
  chart_ids?: string[];
  bazi_chart_id?: string;
  bazi_chart_ids?: string[];
  hexagrams?: unknown[];
  /** Danh sách phiên chỉ trả số quẻ (dump quẻ bị strip cho nhẹ payload). */
  hexagrams_count?: number;
  /** Snapshot lá số Bói Đê (compute-only, không nằm trong danh sách lá số đã lập). */
  tuvi_snapshots?: unknown[];
  bazi_snapshots?: unknown[];
  boi_de?: {
    topic_slug?: string;
    topic_name?: string;
    /** v72: dựng ghost topic khi thẻ đã tắt + chọn renderer khi restore. */
    topic_icon?: string;
    reading_kind?: string;
    price_charged?: number;
    question?: string;
    extra_answers?: Record<string, string>;
    reading_md?: string;
    structured?: unknown;
    generated_at?: string;
    ngay_tot?: { report?: unknown; alternatives?: unknown[]; options?: unknown[] | null } | null;
    compat?: unknown;
  };
  /** Phiên bói chỉ tay: session vẫn là `tong_hop` để chat, nhưng kết quả (ảnh +
   * vùng khoanh) nằm ở bảng riêng — mở lại phải đi qua `/boi-chi-tay?reading=`,
   * không phải màn Bói Đê chung. Danh sách phiên chỉ giữ 3 key nhẹ. */
  palm?: {
    reading_id?: string;
    price_charged?: number;
    generated_at?: string;
    question?: string;
    reading_md?: string;
  };
};

export type SessionSummary = {
  id: string;
  subject?: string;
  viec_can_xem?: string;
  status: string;
  created_at: string;
  has_que?: boolean;
  message_count?: number;
  diviner_id: string;
  metadata: SessionMetadata;
  fortune_type: FortuneType;
  is_ready: boolean;
};

export type CreateSessionResp = {
  session_id: string;
  subject: string;
  diviner_id: string;
  metadata: SessionMetadata;
  fortune_type: FortuneType;
  is_ready: boolean;
  message?: string;
};

export type SessionDetail = {
  id: string;
  subject: string;
  status: string;
  diviner_id: string;
  metadata: SessionMetadata;
  fortune_type: FortuneType;
  is_ready: boolean;
  hexagram_result: HexagramResult | null;
  related_session_count: number;
  /**
   * Lượt chat Kinh Dịch đang chạy nền của phiên (xem `waitForChatJob`).
   *
   * Đây là đường DUY NHẤT để nối lại với một lượt đã trả tiền sau khi người dùng tải
   * lại trang: BE chốt sổ (lưu câu trả lời + trừ tiền) ở nhịp hỏi thăm, nên tab đóng
   * là không còn ai hỏi thăm và lượt ấy nằm treo.
   */
  pending_chat_job?: string | null;
  created_at: string;
  updated_at: string;
};

export type LucHao = {
  vi_tri: number;
  loai: "dương" | "âm" | string;
  bien: boolean;
  bit?: number | null;
  is_moving?: boolean;
  luc_than?: string | null;
  can_chi?: string | null;
  phuc_than?: string | null;
  luc_thu?: string | null;
  the_ung?: "the" | "ung" | null;
  tk?: string | null;
  vuong_suy?: string | null;
  quat_than?: string | null;
  loc?: string | null;
  ma?: string | null;
  quy?: string | null;
  dao?: string | null;
};

export type Que = {
  ten: string;
  ten_tuong?: string;
  ho_que?: string;
  cung_que?: string;
  ten_anh?: string;
  so_que: number;
  thuoc_tinh?: string;
  ngu_hanh?: string;
  luc_hao: LucHao[];
  the_hao?: number;
  ung_hao?: number;
  ten_thuong_bat_quai?: string;
  ten_ha_bat_quai?: string;
  thuong_quai_so?: number | null;
  ha_quai_so?: number | null;
  thuong_quai_binary?: number[] | null;
  ha_quai_binary?: number[] | null;
  binary_6?: number[] | null;
};

export type HexagramResult = {
  session_id: string;
  viec_can_xem: string;
  phuong_phap?: string;
  thoi_gian?: {
    solar_datetime?: string;
    lunar_date?: string;
    can_chi_gio?: string;
    can_chi_ngay?: string;
    can_chi_thang?: string;
    can_chi_nam?: string;
    tiet_khi?: string;
    nhat_than?: string;
    nguyet_lenh?: string;
  };
  que_chinh?: Que;
  que_bien?: Que | null;
  co_hao_bien?: boolean;
  hao_dong?: number | null;
  tieu_de_chuan?: string | null;
  tieu_de_bien?: string | null;
  so_thuong_que?: number;
  so_ha_que?: number;
  so_hao_dong?: number;
  so_thuong_que_bien?: number;
  so_ha_que_bien?: number;
  created_at?: string;
};

export type ChatMessage = {
  id: string;
  session_id: string;
  role: "user" | "assistant" | "system";
  content: string;
  attachments?: unknown[];
  timestamp: string;
  metadata?: Record<string, unknown>;
  rating?: number | null;
  rating_comment?: string | null;
  rating_comment_rewarded_at?: string | null;
};

export type ChatReply = {
  session_id: string;
  message_id: string;
  reply: string;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  price?: number;
  current_amount?: number;
};

/**
 * Response của `POST .../chat` — HAI DẠNG, và FE phải nhận được cả hai.
 *
 * Lượt Kinh Dịch chạy nền trên worker thì BE trả `job_id` ngay (không giữ request HTTP,
 * nên không còn dính trần Cloudflare ~100s khi hàng đợi tắc). Mọi lượt còn lại — Tử Vi,
 * Bát Tự, tổng hợp, lượt có file đính kèm, và cả Kinh Dịch khi `ICHING_USE_CELERY=false`
 * — vẫn trả `reply` ngay trong response.
 *
 * ⚠️ Phân biệt bằng KHOÁ CÓ MẶT (`job_id`), không bằng cấu hình phía FE: cờ tắt/bật nằm
 * ở BE và đổi được lúc chạy, nên FE đoán trước là sai ngay lần đầu ai đó tắt cờ.
 */
type ChatSubmitResponse = Partial<ChatReply> & { job_id?: string; status?: string };

export type ChatJobStatus = {
  status: "pending" | "done" | "failed";
  job_id?: string;
  error?: string;
} & Partial<ChatReply>;

/** Nhịp hỏi thăm: dày lúc đầu (phần lớn lượt xong trong 10–30s), thưa dần khi phải xếp
 *  hàng sau một lượt bói chỉ tay 8–15 phút. */
const CHAT_JOB_POLL_FAST_MS = 1500;
const CHAT_JOB_POLL_SLOW_MS = 4000;
const CHAT_JOB_FAST_WINDOW_MS = 45_000;
/** Trần chờ phía FE. Rộng hơn `ICHING_JOB_MAX_WAIT` của BE một quãng để BE là bên tuyên
 *  bố hỏng — hai bên cùng bỏ cuộc một lúc thì lỗi hiện ra tuỳ nơi nào nhanh tay hơn. */
const CHAT_JOB_MAX_WAIT_MS = 12 * 60_000;

export async function getChatJob(sessionId: string, jobId: string): Promise<ChatJobStatus> {
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/${sessionId}/chat-jobs/${jobId}`, {
    headers: authHeaders(),
  });
  return unwrap<ChatJobStatus>(res);
}

/**
 * Hỏi thăm một lượt chat nền tới khi có câu trả lời. Ném `Error` nếu lượt hỏng.
 *
 * ⚠️ Lỗi MẠNG của một nhịp hỏi thăm KHÔNG được giết cả lượt: mất sóng vài giây là
 * chuyện thường trên di động, trong khi lượt chat vẫn đang chạy trên worker và tiền đã
 * chốt. Bỏ qua rồi hỏi lại ở nhịp sau; chỉ `status: "failed"` từ BE hoặc quá trần chờ
 * mới là hỏng thật.
 *
 * ⚠️ `BoideAuthError` thì PHẢI ném ngay — token hết hạn mà cứ poll tiếp là quay vòng
 * vô nghĩa cho tới hết 12 phút.
 */
export async function waitForChatJob(
  sessionId: string,
  jobId: string,
  opts?: { signal?: AbortSignal },
): Promise<ChatReply> {
  const startedAt = Date.now();
  for (;;) {
    if (opts?.signal?.aborted) throw new Error("Đã huỷ chờ lượt chat");

    let job: ChatJobStatus | null = null;
    try {
      job = await getChatJob(sessionId, jobId);
    } catch (e) {
      if (e instanceof BoideAuthError) throw e;
      // nuốt — xem docstring
    }

    if (job?.status === "done" && job.reply) {
      return job as ChatReply;
    }
    if (job?.status === "failed") {
      throw new Error(job.error || "Lượt chat không hoàn tất. Bạn thử lại nhé.");
    }
    const waited = Date.now() - startedAt;
    if (waited > CHAT_JOB_MAX_WAIT_MS) {
      throw new Error("Thầy suy tư hơi lâu. Bạn thử lại sau ít phút nhé.");
    }
    await new Promise((r) =>
      setTimeout(
        r,
        waited < CHAT_JOB_FAST_WINDOW_MS ? CHAT_JOB_POLL_FAST_MS : CHAT_JOB_POLL_SLOW_MS,
      ),
    );
  }
}

export async function getDiviners(): Promise<Diviner[]> {
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/diviners`);
  return unwrap<Diviner[]>(res);
}

/** Mỗi trang bao nhiêu phiên. Trùng mặc định của BE — đổi ở đây là đổi cả hai. */
export const SESSIONS_PAGE_SIZE = 10;

/** Tab lọc phiên. `tong_hop_free` = Bói Đê tự do (trừ phiên theo chủ đề). */
export type SessionKind =
  "all" | "iching" | "tuvi" | "bazi" | "tong_hop" | "palm" | "tong_hop_free";

export type SessionPage = {
  items: SessionSummary[];
  total: number;
  page: number;
  limit: number;
};

export type ListSessionsParams = {
  page?: number;
  limit?: number;
  kind?: SessionKind;
  /** Từ khoá tìm theo tiêu đề / tên chủ đề. BE tự bỏ dấu, gõ có dấu hay không đều được. */
  q?: string;
  sort?: "newest" | "oldest";
  /** Chỉ lấy phiên có ít nhất N tin nhắn (trang Lịch sử dùng 1). */
  minMessages?: number;
};

/**
 * Một trang danh sách phiên.
 *
 * 🔴 **Lọc / tìm kiếm / sắp xếp đi qua THAM SỐ, không lọc lại ở client.** BE cũ trả
 * TOÀN BỘ phiên (không phân trang) nên trang Lịch sử lọc trong bộ nhớ; với 10 dòng mỗi
 * trang thì cách ấy chỉ soi được 10 dòng đang hiện — ô tìm kiếm sẽ nói "không tìm thấy"
 * về những phiên vẫn còn nguyên trong DB.
 *
 * ⚠️ **Chịu được CẢ HAI khuôn trả về** (mảng trần của BE cũ và `{items,total,page,limit}`
 * của BE mới) nên thứ tự deploy nào cũng an toàn — đừng bỏ nhánh chịu-mảng-cũ trước khi
 * BE đã lên bản mới ở mọi môi trường.
 */
export async function listSessions(params: ListSessionsParams = {}): Promise<SessionPage> {
  const page = params.page ?? 1;
  const limit = params.limit ?? SESSIONS_PAGE_SIZE;
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    kind: params.kind ?? "all",
    sort: params.sort ?? "newest",
    min_messages: String(params.minMessages ?? 0),
  });
  if (params.q?.trim()) query.set("q", params.q.trim());
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions?${query.toString()}`, {
    headers: authHeaders(),
  });
  const data = await unwrap<SessionSummary[] | SessionPage>(res);
  if (Array.isArray(data)) {
    // BE cũ: trả trọn danh sách, không hiểu tham số lọc nào — lọc bù ở client để
    // trang vẫn dùng được trong lúc BE chưa lên bản mới.
    const filtered = filterSessionsLocally(data, params);
    return { items: filtered, total: filtered.length, page: 1, limit: filtered.length || limit };
  }
  return {
    items: data?.items ?? [],
    total: Number(data?.total ?? 0),
    page: Number(data?.page ?? page),
    limit: Number(data?.limit ?? limit),
  };
}

const SEARCH_MARKS_PATTERN = /[̀-ͯ]/g;

/** Bỏ dấu + đ→d + lowercase — bản FE của `normalize_search_text` bên BE. */
export function normalizeSearchText(value: string): string {
  return value
    .normalize("NFD")
    .replace(SEARCH_MARKS_PATTERN, "")
    .replaceAll("đ", "d")
    .replaceAll("Đ", "D")
    .toLocaleLowerCase("vi");
}

const SESSION_KIND_LABELS: Partial<Record<FortuneType, string>> = {
  iching: "Kinh Dịch",
  tong_hop: "Bói Đê",
  tuvi: "Tử Vi",
  bazi: "Bát Tự",
};

/** Đường lùi cho BE chưa phân trang — giữ đúng ngữ nghĩa các `kind` bên BE. */
function filterSessionsLocally(
  items: SessionSummary[],
  params: ListSessionsParams,
): SessionSummary[] {
  const kind = params.kind ?? "all";
  const needle = normalizeSearchText((params.q ?? "").trim());
  const minMessages = params.minMessages ?? 0;
  const out = items.filter((s) => {
    // Quẻ đã gieo nhưng chưa luận giải (số dư không đủ) có 0 tin nhắn mà vẫn là một
    // phiên có thật — giữ lại, đúng như mệnh đề HAVING bên BE.
    if ((s.message_count ?? 0) < minMessages && !s.has_que) return false;
    const isPalm = Boolean(s.metadata?.palm?.reading_id);
    if (kind === "palm" && !(s.fortune_type === "tong_hop" && isPalm)) return false;
    if (kind === "tong_hop" && !(s.fortune_type === "tong_hop" && !isPalm)) return false;
    if (kind === "tong_hop_free" && !(s.fortune_type === "tong_hop" && !s.metadata?.boi_de))
      return false;
    if (["iching", "tuvi", "bazi"].includes(kind) && s.fortune_type !== kind) return false;
    if (!needle) return true;
    const haystack = normalizeSearchText(
      [
        s.subject,
        s.viec_can_xem,
        s.fortune_type === "tong_hop" ? s.metadata?.boi_de?.topic_name : undefined,
        SESSION_KIND_LABELS[s.fortune_type],
      ]
        .filter(Boolean)
        .join(" "),
    );
    return haystack.includes(needle);
  });
  const dir = params.sort === "oldest" ? 1 : -1;
  return out.sort(
    (a, b) => dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
  );
}

export async function getSession(sessionId: string): Promise<SessionDetail> {
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/${sessionId}`, {
    headers: authHeaders(),
  });
  return unwrap<SessionDetail>(res);
}

/**
 * Trường phái luận Kinh Dịch. `tong_hop` (mặc định) gửi cả Mai Hoa lẫn Lục Hào cho thầy;
 * hai lựa chọn còn lại chỉ gửi một hệ. Cả ba vẫn lập quẻ theo thời gian (Mai Hoa) — BE
 * chỉ đổi khối dữ kiện đưa cho LLM (`src/services/hexagram/hexagram_context.py`).
 */
export type IchingSchool = "tong_hop" | "mai_hoa" | "luc_hao";

export async function createSession(input: {
  subject: string;
  diviner_id?: string;
  fortune_type: FortuneType;
  tuvi_chart_id?: string;
  chart_ids?: string[];
  bazi_chart_id?: string;
  bazi_chart_ids?: string[];
  iching_school?: IchingSchool;
}): Promise<CreateSessionResp> {
  const body: Record<string, unknown> = {
    subject: input.subject,
    diviner_id: input.diviner_id || "cu_an",
    fortune_type: input.fortune_type,
  };
  if (input.fortune_type === "tuvi" && input.tuvi_chart_id) {
    body.tuvi_chart_id = input.tuvi_chart_id;
  }
  if (input.fortune_type === "bazi" && input.bazi_chart_id) {
    body.bazi_chart_id = input.bazi_chart_id;
  }
  if (input.fortune_type === "tong_hop" && input.chart_ids) {
    body.chart_ids = input.chart_ids;
  }
  if (input.fortune_type === "tong_hop" && input.bazi_chart_ids) {
    body.bazi_chart_ids = input.bazi_chart_ids;
  }
  // Chỉ gửi khi khác mặc định: BE cũ không có trường này, pydantic bỏ trường lạ nên
  // gửi cũng không vỡ — nhưng không gửi thì request y hệt trước đây.
  if (input.fortune_type === "iching" && input.iching_school && input.iching_school !== "tong_hop") {
    body.iching_school = input.iching_school;
  }
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  return unwrap<CreateSessionResp>(res);
}

/** Gắn lá số Tử Vi vào phiên tổng hợp. */
export async function addSessionChart(
  sessionId: string,
  chartId: string,
): Promise<{ chart_ids: string[] }> {
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/${sessionId}/charts`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ chart_id: chartId }),
  });
  return unwrap(res);
}

/** Bỏ lá số khỏi phiên tổng hợp. */
export async function removeSessionChart(
  sessionId: string,
  chartId: string,
): Promise<{ chart_ids: string[] }> {
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/${sessionId}/charts/${chartId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return unwrap(res);
}

/** Gắn lá số Bát Tự vào phiên tổng hợp. */
export async function addSessionBaziChart(
  sessionId: string,
  chartId: string,
): Promise<{ bazi_chart_ids: string[] }> {
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/${sessionId}/bazi-charts`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ chart_id: chartId }),
  });
  return unwrap(res);
}

/** Bỏ lá số Bát Tự khỏi phiên tổng hợp. */
export async function removeSessionBaziChart(
  sessionId: string,
  chartId: string,
): Promise<{ bazi_chart_ids: string[] }> {
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/${sessionId}/bazi-charts/${chartId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return unwrap(res);
}

/** Bỏ 1 quẻ đã gieo (theo vị trí) khỏi phiên tổng hợp. */
export async function removeSessionHexagram(
  sessionId: string,
  index: number,
): Promise<{ hexagrams_count: number }> {
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/${sessionId}/hexagrams/${index}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return unwrap(res);
}

export async function renameSession(
  sessionId: string,
  subject: string,
): Promise<{ id: string; subject: string }> {
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/${sessionId}/rename`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ subject }),
  });
  return unwrap(res);
}

export async function deleteSession(sessionId: string): Promise<{ message: string }> {
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/${sessionId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return unwrap<{ message: string }>(res);
}

export async function castHexagram(
  sessionId: string,
  castTime?: string,
): Promise<{
  success: boolean;
  session_id: string;
  hexagram_result: HexagramResult;
}> {
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/${sessionId}/cast`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(castTime ? { cast_time: castTime } : {}),
  });
  return unwrap(res);
}

export async function exportHexagram(sessionId: string): Promise<HexagramResult> {
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/${sessionId}/export`, {
    headers: authHeaders(),
  });
  return unwrap<HexagramResult>(res);
}

export async function getChatHistory(sessionId: string): Promise<{
  session_id: string;
  messages: ChatMessage[];
  total: number;
}> {
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/${sessionId}/history`, {
    headers: authHeaders(),
  });
  return unwrap(res);
}

/** Chấm sao (1..5) cho 1 câu trả lời AI; rating=null để bỏ chấm. */
export async function rateChatMessage(
  sessionId: string,
  messageId: string,
  rating: number | null,
  ratingComment?: string | null,
): Promise<{
  message_id: string;
  rating: number | null;
  rating_comment?: string | null;
  rating_comment_rewarded_at?: string | null;
  rewarded: boolean;
  reward_amount: number;
  current_amount?: number | null;
}> {
  const body: { rating: number | null; rating_comment?: string | null } = { rating };
  if (ratingComment !== undefined) body.rating_comment = ratingComment;
  const res = await fetch(
    `${BOIDE_API_URL}/v2/chat/sessions/${sessionId}/messages/${messageId}/rating`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    },
  );
  return unwrap(res);
}

export async function sendChatMessage(input: {
  sessionId: string;
  message: string;
  files?: File[];
  relatedSessionIds?: string[];
  systemPromptOverride?: string;
  fortuneType?: FortuneType;
  /** Slug cung Tử Vi đang hỏi (vd "menh"). Khi có, BE chỉ nạp đúng cung đó vào context. */
  cungKey?: TuviCungKey;
  /** Huỷ việc CHỜ (lượt chạy nền vẫn chạy tiếp trên worker và vẫn được chốt sổ ở nhịp
   *  hỏi thăm sau — kể cả từ một tab khác). */
  signal?: AbortSignal;
}): Promise<ChatReply> {
  let ftype = input.fortuneType;
  if (!ftype) {
    try {
      const session = await getSession(input.sessionId);
      ftype = session.fortune_type;
    } catch (e) {
      console.error("Failed to fetch session to fall back fortuneType", e);
      ftype = "tuvi";
    }
  }

  const fd = new FormData();
  fd.append("message", input.message);
  if (input.relatedSessionIds?.length) {
    fd.append("related_session_ids", input.relatedSessionIds.join(","));
  }
  if (input.systemPromptOverride) {
    fd.append("system_prompt_override", input.systemPromptOverride);
  }
  if (input.cungKey) {
    fd.append("cung_key", input.cungKey);
  }
  input.files?.forEach((f) => fd.append("files", f));

  const pathType =
    ftype === "iching"
      ? "iching"
      : ftype === "tong_hop"
        ? "tong-hop"
        : ftype === "bazi"
          ? "bazi"
          : "tuvi";
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/${input.sessionId}/${pathType}/chat`, {
    method: "POST",
    headers: authHeaders(), // do NOT set Content-Type for FormData
    body: fd,
  });
  const submitted = await unwrap<ChatSubmitResponse>(res);

  // Lượt chạy nền: BE mới chỉ nhận việc, câu trả lời tới qua hỏi thăm. Giấu hẳn việc
  // này sau cùng một chữ ký `Promise<ChatReply>` để mọi nơi gọi (kinh-dich, tổng hợp,
  // mystic-chat) không phải biết lượt của mình đi đường nào.
  if (submitted.job_id && !submitted.reply) {
    return waitForChatJob(input.sessionId, submitted.job_id, { signal: input.signal });
  }
  return submitted as ChatReply;
}

// SSE streaming chat — yields text chunks until [DONE]
export async function* streamChatMessage(input: {
  sessionId: string;
  message: string;
  files?: File[];
  relatedSessionIds?: string[];
  signal?: AbortSignal;
}): AsyncGenerator<string, void, void> {
  const fd = new FormData();
  fd.append("message", input.message);
  if (input.relatedSessionIds?.length) {
    fd.append("related_session_ids", input.relatedSessionIds.join(","));
  }
  input.files?.forEach((f) => fd.append("files", f));

  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/${input.sessionId}/stream`, {
    method: "POST",
    headers: authHeaders(),
    body: fd,
    signal: input.signal,
  });
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n\n")) >= 0) {
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const line = raw.replace(/^data:\s?/, "");
      if (line === "[DONE]") return;
      if (line.startsWith("[ERROR]")) throw new Error(line.replace("[ERROR]", "").trim());
      if (line) yield line;
    }
  }
}

export async function linkSessions(sessionId: string, sessionIds: string[]) {
  const res = await fetch(`${BOIDE_API_URL}/v2/chat/sessions/${sessionId}/link`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ session_ids: sessionIds }),
  });
  return unwrap<{ id: string; linked_count: number }>(res);
}

// ============================================================
// Bói Đê — hub bói theo chủ đề (/v1/boi-de)
// ============================================================

export type BoiDeInputKind = "single" | "couple" | "date" | "free";

/** Dispatch renderer kết quả + khung prompt BE (cột boi_de_topics.reading_kind, v72). */
export type BoiDeReadingKind = "standard" | "quick" | "compare" | "timeline";

/** Descriptor field phụ per topic (cột extra_inputs, v72) — FE render, BE validate. */
export type BoiDeExtraInput = {
  key: string;
  label: string;
  type: "text" | "textarea" | "select" | "date";
  required?: boolean;
  placeholder?: string;
  max_length?: number;
  options?: { value: string; label: string }[];
};

/** Nhãn ô người per topic (cột ui_labels, v72) — thay hardcode theo slug. */
export type BoiDeUiLabels = {
  person_labels?: string[];
  extra_person_label?: string;
  add_person_label?: string;
};

export type BoiDeTopic = {
  id: number;
  slug: string;
  name: string;
  description: string;
  /** Tên icon lucide (Heart, Coins, ...) — FE map sang component, fallback Sparkles. */
  icon: string;
  /** Giá cho số người TỐI THIỂU; mỗi người thêm cộng extra_person_price. */
  price: number;
  input_kind: BoiDeInputKind;
  min_persons: number;
  max_persons: number;
  extra_person_price: number;
  /** Nhóm hiển thị trên hub (xem-nhanh/tinh-cam/cong-danh/gia-dinh/nha-cua/kham-pha). */
  group_key: string;
  reading_kind: BoiDeReadingKind;
  extra_inputs: BoiDeExtraInput[];
  ui_labels: BoiDeUiLabels;
  /** Knobs BE (purpose_kind, suggest_options, question_required...) — FE chỉ đọc vài key. */
  config: Record<string, unknown>;
  sort_order: number;
};

export type BoiDePersonInput = {
  ho_ten: string;
  nam: number;
  thang: number;
  ngay: number;
  gio: number;
  phut?: number;
  gioi_tinh: "nam" | "nu" | "nữ";
  is_lunar?: boolean;
  /** Sinh trong tháng âm NHUẬN — chỉ có nghĩa khi is_lunar = true. */
  is_leap_month?: boolean;
};

export type BoiDeStartInput = {
  /** Danh sách người xem (số lượng theo min/max_persons của topic) — ưu tiên. */
  persons?: BoiDePersonInput[];
  /** @deprecated dùng `persons`; giữ tương thích client cũ. */
  person?: BoiDePersonInput;
  /** @deprecated dùng `persons`; giữ tương thích client cũ. */
  partner?: BoiDePersonInput;
  /** YYYY-MM-DD — chủ đề xem ngày; để trống = chế độ TÌM NGÀY TỐT (BE quét cửa sổ tương lai). */
  target_date?: string;
  /** Chế độ tìm ngày: tìm từ ngày nào (YYYY-MM-DD, mặc định hôm nay; quá khứ bị BE kẹp về hôm nay). */
  search_from?: string;
  /** Chế độ tìm ngày: quét bao nhiêu ngày kể từ search_from (7–90). */
  search_days?: number;
  purpose?: string;
  /** Loại việc (khoá engine) đè cấu hình thẻ — gửi khi chuyển từ trang Xem ngày. */
  purpose_kind?: string;
  question?: string;
  diviner_id?: string;
  /** Bật để tự gieo và hiển thị thêm một quẻ Mai Hoa; mặc định tắt. */
  include_hexagram?: boolean;
  /** Trả lời cho topic.extra_inputs — key → value (select gửi value). */
  extra_answers?: Record<string, string>;
};

export type NgayTotReport = {
  solar_date: string;
  display: string;
  lunar_display: string;
  can_chi_ngay: string;
  chi_thang: string;
  tiet_khi: string;
  truc: { name: string; y_nghia: string; tot_cho: string; xau_cho: string };
  sao: { name: string; is_hoang_dao: boolean; y_nghia: string };
  is_hoang_dao: boolean;
  gio_hoang_dao: { gio_chi: string; khung_gio: string; sao: string }[];
  tuoi?: { chi_tuoi: string; kind: string; delta: number } | null;
  /** v72: xung/hợp với TỪNG tuổi (thẻ ngày nhiều người, vd cưới hỏi hai họ). */
  tuoi_list?: { birth_year?: number; chi_tuoi: string; kind: string; delta: number }[];
  /** v72: điểm cộng/trừ theo loại việc (cưới hỏi, khai trương...). */
  purpose?: { kind: string; label: string; delta: number } | null;
  /** v72: nhãn phương án khi nằm trong ngay_tot.options. */
  option_key?: "tot_nhat" | "can_bang" | "du_phong";
  option_label?: string;
  score: number;
  verdict: string;
};

// ─── Kết quả structured theo reading_kind (v72) ─────────────────────────────

export type BoiDeCompareOption = {
  key: string;
  label: string;
  muc_thuan_loi: "cao" | "vua" | "thap";
  score: number;
  diem_manh: string[];
  rui_ro_chinh: string[];
  thoi_diem_phu_hop: string;
};

export type BoiDeCompare = {
  kind: "compare";
  options: BoiDeCompareOption[];
  recommendation: {
    pick: "A" | "B" | "tuy_dieu_kien";
    ly_do: string;
    dieu_kien_thay_doi: string;
  };
};

export type BoiDeTimelineItem = {
  period: string;
  window_start: string;
  window_end: string;
  muc_do: "cao" | "vua" | "thap";
  note: string;
};

export type BoiDeTimeline = {
  kind: "timeline";
  timeline: BoiDeTimelineItem[];
  best_window: string;
};

export type BoiDeStructured = BoiDeCompare | BoiDeTimeline;

export type BoiDeCompat = {
  type: "se_duyen" | "lam_an";
  /** Có khi 2 người; 3+ người dùng `pairs` (chấm từng cặp, v70). */
  score?: number;
  band?: { label?: string; key?: string };
  breakdown?: Record<string, unknown>;
  pairs?: { a: string; b: string; score: number; band: string }[];
};

export type BoiDeNgayTot = {
  /** null = chế độ TÌM NGÀY TỐT (không có ngày dự định, chỉ có options). */
  report: NgayTotReport | null;
  alternatives: NgayTotReport[];
  /** v72: 3 phương án ngày đề xuất (thẻ có config.suggest_options / chế độ tìm ngày). */
  options?: NgayTotReport[] | null;
  /** Chế độ tìm ngày: cửa sổ đã quét (BE đã kẹp về tương lai). */
  search_from?: string;
  search_days?: number;
};

export type BoiDeStartResult = {
  session_id: string;
  topic: Pick<
    BoiDeTopic,
    "slug" | "name" | "icon" | "price" | "input_kind" | "reading_kind" | "group_key"
  >;
  reading_md: string;
  structured: BoiDeStructured | null;
  tuvi_charts: TuviChart[];
  bazi_charts: BaziChart[];
  hexagram: HexagramResult | null;
  ngay_tot: BoiDeNgayTot | null;
  compat: BoiDeCompat | null;
  price_charged: number;
  balance: number | null;
};

export async function listBoiDeTopics(): Promise<BoiDeTopic[]> {
  const res = await fetch(`${BOIDE_API_URL}/v1/boi-de/topics`);
  return unwrap<{ topics: BoiDeTopic[] }>(res).then((d) => d.topics);
}

/** Bắt đầu một lượt Bói Đê — call dài (lập lá số + AI luận; quẻ là tùy chọn, 30–90s). */
export async function startBoiDeReading(
  slug: string,
  input: BoiDeStartInput,
): Promise<BoiDeStartResult> {
  // Gửi lại y hệt là an toàn: BE khoá theo chữ ký request và trả lại đúng phiên vừa
  // luận (`reused`, không trừ tiền) thay vì luận + trừ lần hai.
  const call = async () => {
    const res = await fetch(`${BOIDE_API_URL}/v1/boi-de/topics/${slug}/start`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(input),
    });
    return unwrap<BoiDeStartResult>(res);
  };
  return retryWhileInProgress(call, call);
}

// ============================================================
// Bát Tự / Tứ Trụ — Lá số
// ============================================================

export type BaziHiddenStem = {
  can: string;
  ngu_hanh: string;
  am_duong: string;
  thap_than?: string | null;
};

export type BaziPillar = {
  can: string;
  chi: string;
  can_chi: string;
  can_ngu_hanh: string;
  chi_ngu_hanh: string;
  can_am_duong: string;
  chi_am_duong: string;
  thap_than_can?: string | null;
  tang_can: BaziHiddenStem[];
  tiet_khi_thang?: string;
  tiet_khi_date?: string;
  /** Thời điểm giao tiết (giờ VN, "YYYY-MM-DDTHH:mm") — trụ tháng xét theo giờ này. */
  tiet_khi_moment?: string;
  /** Tiết (trong 24) đang chạy lúc sinh — vd "Thu Phân". Khác tiết khởi tháng. */
  tiet_khi_hien_tai?: string;
};

/** Dụng/Hỷ/Kỵ thần do ENGINE tính (phù ức + điều hậu) — LLM chỉ diễn giải. */
export type BaziDungThan = {
  phuong_phap: string;
  dung: string[];
  hy: string[];
  ky: string[];
  dieu_hau?: string | null;
  co_the_tong_cach?: string | null;
  ly_do: string;
};

export type BaziChart = {
  id: string;
  user_id?: number;
  ho_ten: string;
  nam_sinh: number;
  thang_sinh: number;
  ngay_sinh: number;
  gio_sinh: number;
  phut_sinh: number;
  gioi_tinh: string;
  lunar?: Record<string, unknown>;
  pillars: Record<"year" | "month" | "day" | "hour", BaziPillar>;
  day_master: {
    day_master: string;
    ngu_hanh: string;
    score: number;
    band: string;
    label: string;
    ty_le_phe_ta?: number;
    dac_lenh?: boolean;
    dac_dia?: boolean;
    dac_the?: boolean;
  };
  dung_than?: BaziDungThan | null;
  khoi_van?: { nam: number; thang: number; label: string; tiet_moc: string; direction: string } | null;
  /** Bài luận đã khai mở dựa trên trụ cũ (engine đã hiệu chỉnh) — khai mở lại miễn phí. */
  reading_stale?: boolean;
  element_balance: { ngu_hanh: string; score: number; percent: number }[];
  relations: { type: string; label: string; branches: string[] }[];
  luck_cycles: {
    so_thu: number;
    can: string;
    chi: string;
    can_chi: string;
    tuoi_bat_dau: number;
    tuoi_ket_thuc: number;
    nam_bat_dau: number;
    direction: string;
    term_anchor: string;
  }[];
  notes?: string[];
  input_data?: Record<string, unknown>;
  created_at: string;
};

export type BaziChartSummary = {
  id: string;
  ho_ten: string;
  day_master: string;
  nam_sinh: number;
  gioi_tinh: string;
  created_at: string;
  /** Lá số đã từng khai mở (có reading) chưa — dùng để tính phí khai mở free lần đầu. */
  has_reading?: boolean;
};

/** Phí khai mở luận giải Bát Tự (đồng). Lá số đầu tiên của user miễn phí. */
export const BAZI_READING_PRICE = 10000;

export type CreateBaziChartInput = {
  use_profile?: boolean;
  ho_ten?: string;
  nam?: number;
  thang?: number;
  ngay?: number;
  gio?: number;
  phut?: number;
  gioi_tinh?: "nam" | "nu" | "nữ";
  is_lunar?: boolean;
  /** Sinh trong tháng âm NHUẬN — chỉ có nghĩa khi is_lunar = true. */
  is_leap_month?: boolean;
};

export async function createBaziChart(input: CreateBaziChartInput): Promise<BaziChart> {
  const res = await fetch(`${BOIDE_API_URL}/v1/bazi/la-so`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  return unwrap<BaziChart>(res);
}

export type BaziChartPage = {
  items: BaziChartSummary[];
  total: number;
  page: number;
  limit: number;
  /**
   * Đã có lá số nào từng khai mở chưa — đếm trên TOÀN BỘ lá số, không phải trang đang
   * xem. Suy từ 10 dòng của một trang là người có 30 lá số đã khai mở sẽ được hứa
   * "Miễn phí" rồi bị trừ tiền thật.
   */
  has_any_reading: boolean;
};

/**
 * Một trang danh sách lá số Bát Tự.
 *
 * ⚠️ **Chịu được CẢ HAI khuôn trả về**: mảng trần của BE cũ (trần cứng 50 lá số) và
 * `{items,total,page,limit,has_any_reading}` của BE mới.
 */
export async function listBaziCharts(page = 1, limit = CHARTS_PAGE_SIZE): Promise<BaziChartPage> {
  const res = await fetch(`${BOIDE_API_URL}/v1/bazi/la-so?page=${page}&limit=${limit}`, {
    headers: authHeaders(),
  });
  const data = await unwrap<BaziChartSummary[] | BaziChartPage>(res);
  if (Array.isArray(data)) {
    return {
      items: data,
      total: data.length,
      page: 1,
      limit: data.length || limit,
      has_any_reading: data.some((c) => c.has_reading),
    };
  }
  return {
    items: data?.items ?? [],
    total: Number(data?.total ?? 0),
    page: Number(data?.page ?? page),
    limit: Number(data?.limit ?? limit),
    has_any_reading: Boolean(data?.has_any_reading),
  };
}

export async function getBaziChart(chartId: string): Promise<BaziChart> {
  const res = await fetch(`${BOIDE_API_URL}/v1/bazi/la-so/${chartId}`, {
    headers: authHeaders(),
  });
  return unwrap<BaziChart>(res);
}

export async function updateBaziChart(
  chartId: string,
  input: Omit<CreateBaziChartInput, "use_profile">,
): Promise<BaziChart> {
  const res = await fetch(`${BOIDE_API_URL}/v1/bazi/la-so/${chartId}`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  return unwrap<BaziChart>(res);
}

export async function deleteBaziChart(chartId: string): Promise<{ id: string; message: string }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/bazi/la-so/${chartId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return unwrap<{ id: string; message: string }>(res);
}

// ─── Bát Tự — Khai mở (luận giải LLM) ─────────────────────────────────────────

export type BaziReadingSection = {
  key: string;
  label?: string;
  content_md: string;
};

export type BaziReadingGod = {
  elements: string[];
  content_md: string;
};

export type BaziReading = {
  chart_id: string;
  dai_cuc: string;
  dung_than?: BaziReadingGod | null;
  hy_than?: BaziReadingGod | null;
  ky_than?: BaziReadingGod | null;
  sections: BaziReadingSection[];
  generated_at?: string | null;
  model?: string | null;
  price_charged?: number;
  balance?: number | null;
  cached?: boolean;
  /** Lá số đã được hiệu chỉnh sau khi khai mở — khai mở lại (force_refresh) MIỄN PHÍ. */
  reading_stale?: boolean;
};

export class BaziReadingNotFoundError extends Error {
  constructor(message = "Lá số Bát Tự chưa được khai mở") {
    super(message);
    this.name = "BaziReadingNotFoundError";
  }
}

/** Khai mở lá số Bát Tự — sinh luận giải đầy đủ (lá số đầu free, sau đó trừ phí). */
export async function castBaziReading(chartId: string, forceRefresh = false): Promise<BaziReading> {
  const call = async (force: boolean) => {
    const res = await fetch(`${BOIDE_API_URL}/v1/bazi/la-so/${chartId}/reading`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ force_refresh: force }),
    });
    return unwrap<BaziReading>(res);
  };
  // Lượt chờ KHÔNG force_refresh: chỉ lấy kết quả của lượt đang chạy, không trả tiền lần nữa.
  return retryWhileInProgress(
    () => call(forceRefresh),
    () => call(false),
  );
}

/** Đọc luận giải đã cache. Trả về `null` khi chart chưa khai mở (BE 404). */
export async function getBaziReading(chartId: string): Promise<BaziReading | null> {
  const res = await fetch(`${BOIDE_API_URL}/v1/bazi/la-so/${chartId}/reading`, {
    headers: authHeaders(),
  });
  if (res.status === 404) return null;
  return unwrap<BaziReading>(res);
}

// ============================================================
// Tử Vi — Lá số
// ============================================================

export type TuviStar = {
  ten: string;
  loai: "chinh_tinh" | "phu_tinh" | "tieu_tinh" | string;
  mau?: string;
  trang_thai?: string;
  ngu_hanh?: string;
};

export type TuviCung = {
  ten_cung: string;
  chi: string;
  thien_can?: string;
  sao: TuviStar[];
  dai_van_tuoi?: string | null;
  tieu_van_nam?: string | null;
  is_menh: boolean;
  is_than: boolean;
};

export type TuviDaiVan = {
  so_thu: number;
  chi: string;
  chi_idx: number;
  tuoi_bat_dau: number;
  tuoi_ket_thuc: number;
  nam_bat_dau: number;
};

// Sao lưu niên (lưu động) đính theo chart cho năm xem — vị trí theo chi_idx (Tý=0…Hợi=11).
export type TuviChartLuuNien = {
  year: number;
  can_chi: string;
  sao: Record<string, number>; // tên sao lưu -> chi_idx
  tieu_van_chi_idx?: number | null;
};

export type TuviChart = {
  id: string;
  user_id?: number;
  ho_ten: string;
  nam_sinh: number;
  thang_sinh: number;
  ngay_sinh: number;
  gio_sinh: number;
  nam_am?: number;
  thang_am?: number;
  ngay_am?: number;
  gioi_tinh: "nam" | "nu" | "nữ" | string;
  am_duong?: string;
  can_nam?: string;
  chi_nam?: string;
  can_thang?: string;
  chi_thang?: string;
  can_ngay?: string;
  chi_ngay?: string;
  can_gio?: string;
  chi_gio?: string;
  ban_menh?: string;
  menh_cuc?: string;
  so_cuc?: number;
  menh_cuc_relation?: string;
  can_luong?: string;
  chu_menh?: string;
  chu_than?: string;
  lai_nhan_cung?: string;
  nam_xem?: number;
  can_nam_xem?: string;
  chi_nam_xem?: string;
  cung_list: TuviCung[];
  dai_van: TuviDaiVan[];
  luu_nien?: TuviChartLuuNien | null;
  input_data?: Record<string, unknown>;
  created_at: string;
  scores?: TuviScores | null;
};

// Điểm huyền khí + điểm 12 cung — tính tất định ở BE (xem src/services/tuvi/tuvi_score.py).
export type TuviCungScore = {
  key: TuviCungKey;
  label: string; // vd "Cung Mệnh"
  score: number; // 0–100
};

export type TuviHuyenKhiBand = {
  key: "rat_cao" | "cao" | "kha" | "trung_binh" | "can_luu_y" | string;
  label: string;
  description: string;
};

export type TuviScores = {
  cungs: TuviCungScore[];
  diem_huyen_khi: number; // 0–100
  band: TuviHuyenKhiBand;
};

export type TuviChartSummary = {
  id: string;
  ho_ten: string;
  menh_cuc?: string;
  ban_menh?: string;
  nam_sinh: number;
  gioi_tinh: string;
  created_at: string;
};

export type CreateTuviChartInput = {
  use_profile?: boolean;
  ho_ten?: string;
  nam?: number;
  thang?: number;
  ngay?: number;
  gio?: number;
  gioi_tinh?: "nam" | "nu" | "nữ";
  is_lunar?: boolean;
  /** Sinh trong tháng âm NHUẬN — chỉ có nghĩa khi is_lunar = true. */
  is_leap_month?: boolean;
  nam_xem?: number; // năm xem hạn (DL) — ảnh hưởng tiểu vận + sao lưu niên
};

export async function createTuviChart(input: CreateTuviChartInput): Promise<TuviChart> {
  const res = await fetch(`${BOIDE_API_URL}/v1/tuvi/la-so`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  return unwrap<TuviChart>(res);
}

// Bộ sao lưu động (lưu niên) theo năm cho một lá số đã lưu.
export type TuviLuuNienCungInfo = { chi_idx: number; chi: string; cung_ten: string };
export type TuviLuuNien = {
  chart_id: string;
  year: number;
  can_chi: string;
  sao: Record<string, TuviLuuNienCungInfo | null>; // tên sao lưu -> cung
  tieu_van_cung: TuviLuuNienCungInfo | null;
  cung_list: TuviCung[]; // cung_list đã overlay sao lưu (loai = "luu_nien")
};

/** Lấy sao lưu niên + cung_list đã overlay cho một năm bất kỳ (mặc định năm hiện tại). */
export async function getTuviLuuNien(chartId: string, year?: number): Promise<TuviLuuNien> {
  const qs = year ? `?year=${year}` : "";
  const res = await fetch(`${BOIDE_API_URL}/v1/tuvi/la-so/${chartId}/luu-nien${qs}`, {
    headers: authHeaders(),
  });
  return unwrap<TuviLuuNien>(res);
}

// ============================================================
// Câu hỏi xác tín — tạo đồng thời Tử Vi + Bát Tự
// ============================================================

export type TrustProbeStatus = "pending" | "ready" | "failed";

export type TrustProbeQuestion = {
  id: string;
  text: string;
  basis: "tuvi" | "bazi" | "combined" | string;
  confidence: "low" | "medium" | "high" | string;
};

export type TrustProbe = {
  id: string;
  status: TrustProbeStatus;
  questions: TrustProbeQuestion[];
  model?: string | null;
  error?: string | null;
};

export type CreateTrustProbeChartsInput = CreateTuviChartInput & {
  source: "tuvi" | "bazi";
  phut?: number;
};

export type CreateTrustProbeChartsResponse = {
  tuvi_chart: TuviChart;
  bazi_chart: BaziChart;
  probe: Pick<TrustProbe, "id" | "status">;
};

export async function createTrustProbeCharts(
  input: CreateTrustProbeChartsInput,
): Promise<CreateTrustProbeChartsResponse> {
  const res = await fetch(`${BOIDE_API_URL}/v1/trust-probe/la-so`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  return unwrap<CreateTrustProbeChartsResponse>(res);
}

export async function getTrustProbe(probeId: string): Promise<TrustProbe> {
  const res = await fetch(`${BOIDE_API_URL}/v1/trust-probe/${probeId}`, {
    headers: authHeaders(),
  });
  return unwrap<TrustProbe>(res);
}

export async function submitTrustProbeAnswers(
  probeId: string,
  answers: { question_id: string; answer: boolean }[],
): Promise<{ probe_id: string; saved: number }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/trust-probe/${probeId}/answers`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ answers }),
  });
  return unwrap<{ probe_id: string; saved: number }>(res);
}

// ============================================================
// Đoán giờ sinh — 13 lá số Tử Vi + 13 lá số Bát Tự (giờ Tý tách sớm/muộn)
// ============================================================

/** Đáp án bỏ qua — BE tự thêm vào mọi câu và KHÔNG tính vào điểm. */
export const BIRTH_TIME_SKIP = "khong_ro";

export type BirthTimeQuestion = {
  id: string;
  group: string;
  text: string;
  hint: string;
  weight: number;
  /** false = câu ngữ cảnh (vd tình trạng hôn nhân): không tính điểm, chỉ để lọc câu khác. */
  scored: boolean;
  /** Chỉ hiện khi câu `key` đã được trả lời bằng một trong các đáp án kèm theo. */
  when: Record<string, string[]>;
  min_age: number | null;
  max_age: number | null;
  options: { id: string; label: string }[];
  /**
   * 🔴 CÂU CHỐT KHUNG — nó LỌC ứng viên chứ không cộng điểm, và phải trả lời XONG rồi
   * mới xin được bộ câu chấm điểm (bộ chọn của BE tối ưu theo đúng tập còn lại).
   * Trộn nó vào cùng một danh sách với câu thường là quay về đúng hành vi cũ: khung
   * giờ chỉ còn là một lá phiếu nặng 16%, và giờ đứng đầu rơi ra ngoài khung đã khai.
   */
  narrow?: boolean;
};

/** Đáp án các câu chốt khung đã có — gửi kèm để BE trả câu kế tiếp + bộ câu đã lọc. */
export type BirthTimeNarrowAnswers = Partial<Record<"thoi_diem" | "khung_hep" | "nua_dem", string>>;

export type BirthTimeNarrowSummary = {
  da_ap_dung: boolean;
  tong_ung_vien: number;
  con_lai: number;
  da_loai: number;
  buoc: { cau: string; dap_an: string; nhan: string; truoc: number; sau: number }[];
  bo_qua: string[];
  gio_con_lai: { ma: string; ten: string; khung_gio: string }[];
};

export type BirthTimeQuestionSet = {
  min_answered: number;
  /** Mốc năm BE dùng để tính tuổi — FE tính theo đúng mốc này để lọc giống BE. */
  current_year: number;
  /** 13 khung giờ — cho ô chọn của chế độ "tôi biết giờ sinh". */
  hour_slots: BirthTimeHourSlot[];
  questions: BirthTimeQuestion[];
  /** Giá niêm yết một lượt đoán. Hiện luôn 0 — tính năng đang miễn phí, FE không đọc. */
  price: number;
  /** Còn lượt miễn phí không. Vô nghĩa khi `price` = 0, FE không đọc. */
  free_available: boolean;
  /** `graph` = chọn riêng cho ngày sinh đã gửi · `static` = danh sách cố định. */
  selected_by?: "graph" | "static";
  /** Chỉ có ở chế độ `graph`: cặp giờ nào vẫn còn khó tách sau bộ câu này (để soi log). */
  graph?: {
    so_cau: number;
    nham_lan_con_lai: number;
    nham_lan_ban_dau: number;
    cap_kho_nhat: { cap: string[]; tach_duoc: number }[];
  };
  authenticated: boolean;
  /** Lượt chưa chấm (mở bài rồi bỏ dở) — `/start` trả lại đúng lượt này. */
  open_run_id: string | null;
  /** BE đã hỗ trợ luồng chốt khung chưa. BE cũ không trả khoá này ⇒ undefined. */
  narrow_flow?: boolean;
  /**
   * Câu chốt khung KẾ TIẾP, hoặc `null` khi đã hỏi hết. Còn khác null thì `questions`
   * BÊN DƯỚI CHƯA PHẢI bộ cuối — nó được chọn cho tập ứng viên hiện tại, mà tập ấy
   * còn hẹp lại sau câu này.
   */
  narrow_question?: BirthTimeQuestion | null;
  /** Lời khai khung giờ đã loại bớt bao nhiêu ứng viên. */
  thu_hep?: BirthTimeNarrowSummary | null;
};

/** Kết quả mở một lượt đoán. */
export type BirthTimeRun = {
  run_id: string;
  /** Luôn 0 khi tính năng miễn phí (`BIRTH_TIME_PRICE` = 0 ở BE). */
  price_charged: number;
  balance: number | null;
  /** true = nhận lại lượt đang mở dở, không trừ thêm đồng nào. */
  reused: boolean;
};

/**
 * Câu có hợp hoàn cảnh người trả lời không — mirror `is_visible` bên BE.
 * Chưa biết tuổi thì ẩn mọi câu có điều kiện tuổi (hiện cả hai câu Thân cư
 * "trước/sau 30 tuổi" cùng lúc là hỏi trùng).
 */
export function isBirthTimeQuestionVisible(
  q: BirthTimeQuestion,
  answers: Record<string, string>,
  age: number | null,
): boolean {
  if (q.min_age != null || q.max_age != null) {
    if (age == null) return false;
    if (q.min_age != null && age < q.min_age) return false;
    if (q.max_age != null && age > q.max_age) return false;
  }
  return Object.entries(q.when ?? {}).every(([qid, allowed]) => allowed.includes(answers[qid]));
}

export type BirthTimeCandidate = {
  hang: number;
  /** Định danh khung giờ — duy nhất; `chi_idx` thì KHÔNG (Tý sớm và Tý muộn cùng 0). */
  ma: string;
  thu_tu: number;
  /** Nhãn hiển thị: "Tý sớm", "Sửu", …, "Tý muộn". */
  chi: string;
  /** Tý muộn: lá số dựng theo ngày hôm sau (can chi ngày, ngày âm). */
  ty_muon: boolean;
  chi_idx: number;
  khung_gio: string;
  gio_dai_dien: number;
  do_khop: number;
  /** Điểm riêng từng vòng — chỉ có sau vòng 2, để người dùng thấy vòng 2 đã đổi gì. */
  do_khop_vong_1?: number;
  do_khop_vong_2?: number | null;
  khop: string[];
  lech: string[];
  tom_tat: {
    menh_chi: string | null;
    menh_chinh_tinh: string[];
    than_cu: string | null;
    menh_cuc: string | null;
    diem_menh: number | null;
    tru_gio: string;
    thap_than_gio: string | null;
    nhat_chu: string | null;
  };
};

/**
 * Ba quy tắc hiển thị của BE — thứ biến một bảng xếp hạng thành câu trả lời thật thà.
 * KHÔNG suy lại ở FE: `khoang_gop` cần biết hai khung có liền nhau trên đồng hồ không
 * (kể cả cặp Tý muộn ↔ Tý sớm bắc qua 0h), mà FE không giữ bản đồ khung giờ nào.
 */
export type BirthTimeDisplayHints = {
  khoang_gop: { khung_gio: string; gom: string[]; mo_ta: string } | null;
  canh_bao: string[];
};

export type BirthTimeEstimate = {
  ket_qua: BirthTimeCandidate[];
  /** TOP 3 (đôi khi 4 khi hạng 3–4 sát nhau) — đây mới là câu trả lời, không phải `ket_qua[0]`. */
  top: BirthTimeCandidate[];
  hien_thi: BirthTimeDisplayHints;
  goi_y: { ma: string; chi: string; khung_gio: string; gio_dai_dien: number };
  do_tin_cay: "cao" | "trung_binh" | "thap";
  do_tin_cay_mo_ta: string;
  so_cau_da_tra_loi: number;
  so_cau_phan_biet: number;
  /** Câu đã trả lời nhưng BE bỏ vì không hợp hoàn cảnh (tuổi / tình trạng hôn nhân). */
  cau_bo_qua: string[];
  tong_trong_so: number;
  gap_1_2: number | null;
  gap_1_3: number;
  /** BE thấy chưa đủ chắc — nên mời người dùng sang vòng 2. */
  nen_hoi_them: boolean;
  /** Có id thì mới đi tiếp vòng 2 được (DB hỏng thì BE trả null, kết quả vòng 1 vẫn dùng). */
  probe_id: string | null;
  co_the_hoi_them: boolean;
  tuoi: number;
  ngay_sinh_duong_lich: string;
  ghi_chu: string[];
  /** Lượt đã trả tiền dùng để chấm kết quả này. */
  run_id?: string;
  /** Số tiền lượt này đã trả (trừ ở `/start`); 0 = lượt miễn phí đầu tiên. */
  price_charged?: number;
  /**
   * Sau vòng 2 mà các giờ đứng đầu vẫn sát: BE mời PHỎNG VẤN THÊM (bộ câu mới, không
   * trùng câu đã hỏi, không tính thêm tiền). BE bản cũ không trả khoá này ⇒ coi như false.
   */
  co_the_phong_van_them?: boolean;
  /** Đã phỏng vấn thêm bao nhiêu lượt / tối đa bao nhiêu lượt. */
  so_luot_hoi_them?: number;
  so_luot_hoi_them_toi_da?: number;
  /** Lời khai khung giờ đã lọc bớt bao nhiêu ứng viên trước khi chấm. */
  thu_hep?: BirthTimeNarrowSummary | null;
  /**
   * Bảng xếp hạng trên CẢ 13 giờ, bỏ qua khung giờ đã khai — cho nút "người nhà nhớ
   * chưa chắc". Chỉ có khi thật sự đã lọc. Đây là một CÁCH XEM, không phải một lượt
   * đoán khác: vòng 2 vẫn chấm trên tập đã lọc.
   */
  mo_rong?: {
    ket_qua: BirthTimeCandidate[];
    top: BirthTimeCandidate[];
    goi_y: { ma: string; chi: string; khung_gio: string; gio_dai_dien: number };
    gap_1_2: number | null;
    do_tin_cay: "cao" | "trung_binh" | "thap";
    mo_ta: string;
  } | null;
  /** Chỉ có ở chế độ "tôi biết giờ sinh" — BE đối chiếu SAU khi đã xếp hạng xong. */
  doi_chieu?: {
    gio_that: string;
    hang: number | null;
    trong_top: boolean;
    tong_ung_vien: number;
  };
};

/** Một khung giờ trong 13 khung — dùng cho ô chọn "tôi biết giờ sinh". */
export type BirthTimeHourSlot = { ma: string; ten: string; khung_gio: string };

/**
 * Câu hỏi VÒNG 2 — do LLM sinh tại chỗ, bám đúng các ứng viên còn lại.
 * Không có `when`/`min_age`/`group` như câu vòng 1: bộ này sinh riêng cho một lượt.
 * `ung_ho` (ánh xạ đáp án → ứng viên) KHÔNG bao giờ được trả ra đây — nó là đáp án.
 */
export type BirthTimeRound2Question = {
  id: string;
  text: string;
  weight: number;
  /** BE bắt LLM chỉ đích danh ô nào trong lá số câu này khai thác — hiện ra để người dùng soát. */
  vi_sao_hoi: string;
  nguon: string;
  options: { id: string; label: string }[];
};

export type BirthTimeRound2Set = {
  probe_id: string;
  questions: BirthTimeRound2Question[];
  model: string | null;
  cached: boolean;
};

export type BirthTimeEstimateInput = {
  /** Lượt đã mở, lấy từ `startBirthTimeRun()`. Thiếu nó BE trả 400. */
  run_id: string;
  ho_ten?: string;
  nam: number;
  thang: number;
  ngay: number;
  gioi_tinh: "nam" | "nu";
  is_lunar?: boolean;
  is_leap_month?: boolean;
  answers: Record<string, string>;
  /**
   * Chế độ "tôi biết giờ sinh — thử xem có đoán trúng không": mã khung giờ thật.
   * BE KHÔNG dùng nó để chấm, chỉ lưu lại và đối chiếu sau khi xếp hạng xong. Đây là
   * đường duy nhất sinh ra bộ dữ liệu có nhãn để đo độ chính xác thật của tính năng.
   */
  known_hour?: string | null;
};

/**
 * Mở một lượt đoán, ngay khi người dùng khai xong ngày sinh. Tính năng đang MIỄN PHÍ
 * nên không trừ gì; BE vẫn giữ đường trừ tiền cho trường hợp bật lại `BIRTH_TIME_PRICE`.
 *
 * Idempotent: còn lượt chưa chấm thì trả lại đúng lượt đó (`reused: true`), nên gọi
 * lại sau khi bỏ dở là đi tiếp chứ không mở lượt mới.
 */
export async function startBirthTimeRun(): Promise<BirthTimeRun> {
  const res = await fetch(`${BOIDE_API_URL}/v1/birth-time/start`, {
    method: "POST",
    headers: authHeaders(),
  });
  return unwrap<BirthTimeRun>(res);
}

/** Ngày sinh gửi kèm để BE chọn bộ câu hỏi RIÊNG cho ca đó. */
export type BirthTimeQuestionQuery = {
  nam: number;
  thang: number;
  ngay: number;
  gioi_tinh: "nam" | "nu";
  is_lunar?: boolean;
  is_leap_month?: boolean;
};

/**
 * Bộ câu hỏi vòng 1.
 *
 * 🔴 TRUYỀN `birth` VÀO NẾU ĐÃ BIẾT NGÀY SINH. 13 lá số ứng viên lập được ngay từ
 * ngày–tháng–năm–giới tính, tức TRƯỚC khi hỏi câu nào, nên BE chọn được bộ câu bám
 * đúng những cặp giờ mà CHÍNH ca này chưa tách được (`birth_time_graph`), thay vì
 * phát một danh sách cố định. Không truyền thì vẫn trả danh sách tĩnh như cũ.
 *
 * ⚠️ Gọi cái này TRƯỚC khi chuyển sang màn hỏi, đừng để nó về giữa chừng: bộ câu đổi
 * dưới chân người đang trả lời thì câu họ vừa chọn rơi vào một câu khác.
 */
export async function getBirthTimeQuestions(
  birth?: BirthTimeQuestionQuery,
  narrow?: BirthTimeNarrowAnswers,
): Promise<BirthTimeQuestionSet> {
  const params = new URLSearchParams();
  if (birth) {
    params.set("nam", String(birth.nam));
    params.set("thang", String(birth.thang));
    params.set("ngay", String(birth.ngay));
    params.set("gioi_tinh", birth.gioi_tinh);
    params.set("is_lunar", String(!!birth.is_lunar));
    params.set("is_leap_month", String(!!birth.is_leap_month));
  }
  // ⚠️ Gửi cả "khong_ro": với BE nó nghĩa là "đã hỏi, người dùng không nhớ" — khác hẳn
  // việc bỏ trống (chưa hỏi). Bỏ trống thì `next_narrow_question` phát lại chính câu
  // vừa bấm bỏ qua, và người dùng kẹt ở đó.
  for (const key of ["thoi_diem", "khung_hep", "nua_dem"] as const) {
    const value = narrow?.[key];
    if (value) params.set(key, value);
  }
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await fetch(`${BOIDE_API_URL}/v1/birth-time/questions${qs}`, {
    headers: authHeaders(),
  });
  return unwrap<BirthTimeQuestionSet>(res);
}

export async function estimateBirthTime(input: BirthTimeEstimateInput): Promise<BirthTimeEstimate> {
  const res = await fetch(`${BOIDE_API_URL}/v1/birth-time/estimate`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  return unwrap<BirthTimeEstimate>(res);
}

/**
 * VÒNG 2 — call LLM DUY NHẤT của cả tính năng (~45s với model `reasoning`).
 * Idempotent: gọi lại trả bộ cũ (`cached: true`), không đốt thêm một call.
 */
export async function getBirthTimeRound2Questions(probeId: string): Promise<BirthTimeRound2Set> {
  const res = await fetch(`${BOIDE_API_URL}/v1/birth-time/probes/${probeId}/questions`, {
    method: "POST",
    headers: authHeaders(),
  });
  return unwrap<BirthTimeRound2Set>(res);
}

/** Chấm vòng 2 — tất định ở BE, trả top 3 cuối đã gộp cả hai vòng. */
export async function submitBirthTimeRound2(
  probeId: string,
  answers: Record<string, string>,
): Promise<BirthTimeEstimate> {
  const res = await fetch(`${BOIDE_API_URL}/v1/birth-time/probes/${probeId}/answers`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ answers }),
  });
  return unwrap<BirthTimeEstimate>(res);
}

/**
 * PHỎNG VẤN THÊM — sau vòng 2 mà các giờ đứng đầu vẫn sát nhau, BE mời trả lời thêm
 * một bộ câu hỏi MỚI (không trùng trục đã hỏi). Tối đa `so_luot_hoi_them_toi_da` lượt,
 * không tính thêm tiền. Chỉ gọi khi kết quả có `co_the_phong_van_them: true`.
 * Idempotent: lượt đang dở thì trả lại đúng bộ đó (`cached: true`).
 */
export async function getBirthTimeMoreQuestions(probeId: string): Promise<{
  probe_id: string;
  round: number;
  questions: BirthTimeRound2Question[];
  cached: boolean;
}> {
  const res = await fetch(`${BOIDE_API_URL}/v1/birth-time/probes/${probeId}/more-questions`, {
    method: "POST",
    headers: authHeaders(),
  });
  return unwrap(res);
}

/** Chấm lượt phỏng vấn thêm — BE gộp với MỌI vòng trước rồi trả kết quả mới. */
export async function submitBirthTimeMore(
  probeId: string,
  answers: Record<string, string>,
): Promise<BirthTimeEstimate> {
  const res = await fetch(`${BOIDE_API_URL}/v1/birth-time/probes/${probeId}/more-answers`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ answers }),
  });
  return unwrap<BirthTimeEstimate>(res);
}

export type BirthTimeHistoryItem = {
  id: string;
  ho_ten: string;
  ngay_sinh_duong_lich: string;
  gioi_tinh: string;
  is_lunar: boolean;
  status: "round1" | "questions_ready" | "done";
  summary: {
    top: { ma: string; chi: string; khung_gio: string; do_khop: number; hang?: number }[];
    khoang_gop: string | null;
    do_tin_cay: "cao" | "trung_binh" | "thap" | null;
    do_tin_cay_mo_ta: string | null;
    so_luot_hoi_them?: number;
  } | null;
  predicted_hour: string | null;
  known_hour: string | null;
  predicted_rank: number | null;
  so_luot_hoi_them: number;
  price_charged: number;
  created_at: string | null;
};

export type BirthTimeHistoryPage = {
  items: BirthTimeHistoryItem[];
  total: number;
  page: number;
  limit: number;
};

/** Lịch sử đoán giờ sinh — phân trang 10/trang, mới nhất trước. */
export async function listBirthTimeHistory(page = 1, limit = 10): Promise<BirthTimeHistoryPage> {
  const res = await fetch(`${BOIDE_API_URL}/v1/birth-time/history?page=${page}&limit=${limit}`, {
    headers: authHeaders(),
  });
  return unwrap<BirthTimeHistoryPage>(res);
}

/** Kết quả ĐẦY ĐỦ của một lượt cũ — BE dựng lại từ đáp án đã lưu (chấm tất định). */
export async function getBirthTimeHistory(probeId: string): Promise<BirthTimeEstimate> {
  const res = await fetch(`${BOIDE_API_URL}/v1/birth-time/history/${probeId}`, {
    headers: authHeaders(),
  });
  return unwrap<BirthTimeEstimate>(res);
}

export async function deleteBirthTimeHistory(probeId: string): Promise<void> {
  const res = await fetch(`${BOIDE_API_URL}/v1/birth-time/history/${probeId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  await unwrap(res);
}

/** Mỗi trang bao nhiêu lá số. Trùng mặc định của BE — đổi ở đây là đổi cả hai. */
export const CHARTS_PAGE_SIZE = 10;

export type TuviChartPage = {
  items: TuviChartSummary[];
  total: number;
  page: number;
  limit: number;
};

/**
 * Một trang danh sách lá số Tử Vi.
 *
 * ⚠️ **Chịu được CẢ HAI khuôn trả về**: mảng trần của BE cũ (trần cứng 20 lá số) và
 * `{items,total,page,limit}` của BE mới — đừng bỏ nhánh chịu-mảng-cũ trước khi BE đã
 * lên bản mới ở mọi môi trường.
 */
export async function listTuviCharts(page = 1, limit = CHARTS_PAGE_SIZE): Promise<TuviChartPage> {
  const res = await fetch(`${BOIDE_API_URL}/v1/tuvi/la-so?page=${page}&limit=${limit}`, {
    headers: authHeaders(),
  });
  const data = await unwrap<TuviChartSummary[] | TuviChartPage>(res);
  if (Array.isArray(data)) {
    return { items: data, total: data.length, page: 1, limit: data.length || limit };
  }
  return {
    items: data?.items ?? [],
    total: Number(data?.total ?? 0),
    page: Number(data?.page ?? page),
    limit: Number(data?.limit ?? limit),
  };
}

export async function getTuviChart(chartId: string): Promise<TuviChart> {
  const res = await fetch(`${BOIDE_API_URL}/v1/tuvi/la-so/${chartId}`, {
    headers: authHeaders(),
  });
  return unwrap<TuviChart>(res);
}

export async function updateTuviChart(
  chartId: string,
  input: Omit<CreateTuviChartInput, "use_profile">,
): Promise<TuviChart> {
  const res = await fetch(`${BOIDE_API_URL}/v1/tuvi/la-so/${chartId}`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  return unwrap<TuviChart>(res);
}

export async function deleteTuviChart(chartId: string): Promise<{ id: string; message: string }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/tuvi/la-so/${chartId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return unwrap<{ id: string; message: string }>(res);
}

// ============================================================
// Tử Vi — Khai mở (luận giải toàn bộ lá số, JSON structured)
// ============================================================

// Slug ổn định của 12 cung (giống BE — xem src/services/tuvi/tuvi_format.py).
export type TuviCungKey =
  | "menh"
  | "phu_mau"
  | "phuc_duc"
  | "dien_trach"
  | "quan_loc"
  | "no_boc"
  | "thien_di"
  | "tat_ach"
  | "tai_bach"
  | "tu_tuc"
  | "phu_the"
  | "huynh_de";

export type TuviCungReading = {
  key: TuviCungKey;
  label: string; // vd "Cung Mệnh"
  content_md: string; // markdown của cung này (có heading ##, bold **...**)
};

export type TuviReading = {
  chart_id: string;
  dai_cuc: string; // markdown Đại cục (KHÔNG chứa nội dung 12 cung) — FREE
  cungs: TuviCungReading[]; // 12 cung (rỗng nếu chưa mở khoá)
  generated_at: string;
  model: string | null;
  cached: boolean;
  // Billing 12 cung: lá số đầu free, lá số mới tốn phí.
  cungs_unlocked?: boolean;
  cungs_locked?: boolean;
  cungs_price?: number; // 0 nếu free (lần đầu)
  cungs_free?: boolean;
};

// Map từ slug API sang tên cung trong dữ liệu chart hiện hữu (chart.cungs[].cung).
// Dùng khi user click 1 cung trên la-so view và muốn lấy reading tương ứng.
export const TUVI_CUNG_KEY_BY_NAME: Record<string, TuviCungKey> = {
  Mệnh: "menh",
  "Phụ Mẫu": "phu_mau",
  "Phúc Đức": "phuc_duc",
  "Điền Trạch": "dien_trach",
  "Quan Lộc": "quan_loc",
  "Nô Bộc": "no_boc",
  "Thiên Di": "thien_di",
  "Tật Ách": "tat_ach",
  "Tài Bạch": "tai_bach",
  "Tử Tức": "tu_tuc",
  "Phu Thê": "phu_the",
  "Huynh Đệ": "huynh_de",
};

export class TuviReadingNotFoundError extends Error {
  constructor(message = "Lá số chưa được khai mở") {
    super(message);
    this.name = "TuviReadingNotFoundError";
  }
}

/**
 * Khai mở lá số — luận giải Đại cục + 12 cung, trả markdown.
 * Idempotent: trả cache nếu đã có và `forceRefresh` không bật.
 */
export async function castTuviReading(
  chartId: string,
  opts: { forceRefresh?: boolean; year?: number } = {},
): Promise<TuviReading> {
  const res = await fetch(`${BOIDE_API_URL}/v1/tuvi/la-so/${chartId}/reading`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ force_refresh: !!opts.forceRefresh, year: opts.year }),
  });
  return unwrap<TuviReading>(res);
}

/**
 * Đọc reading đã cache. Trả về `null` khi chart chưa được khai mở (BE 404).
 * Các lỗi khác (network, auth) vẫn throw bình thường.
 */
export async function getTuviReading(chartId: string): Promise<TuviReading | null> {
  const res = await fetch(`${BOIDE_API_URL}/v1/tuvi/la-so/${chartId}/reading`, {
    headers: authHeaders(),
  });
  if (res.status === 404) return null;
  return unwrap<TuviReading>(res);
}

export type UnlockCungsResult = {
  chart_id: string;
  cungs: TuviCungReading[];
  cungs_unlocked: boolean;
  price_charged: number;
  balance: number | null;
  cached: boolean;
};

/**
 * Mở khoá luận giải 12 cung. Lá số đầu tiên free, lá số mới trừ phí (VND).
 * Throw nếu số dư không đủ (BE trả 400 + error_description "nạp thêm").
 */
export async function unlockTuviCungs(
  chartId: string,
  opts: { forceRefresh?: boolean; year?: number } = {},
): Promise<UnlockCungsResult> {
  const call = async (forceRefresh: boolean) => {
    const res = await fetch(`${BOIDE_API_URL}/v1/tuvi/la-so/${chartId}/reading/cungs`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ force_refresh: forceRefresh, year: opts.year }),
    });
    return unwrap<UnlockCungsResult>(res);
  };
  // Lượt chờ KHÔNG force_refresh: chỉ lấy kết quả của lượt đang chạy, không trả tiền lần nữa.
  return retryWhileInProgress(
    () => call(!!opts.forceRefresh),
    () => call(false),
  );
}

export type TuviReadingEvent =
  | { type: "dai_cuc"; dai_cuc: string }
  | { type: "cungs"; cungs: TuviCungReading[] }
  | {
      type: "done";
      cached: boolean;
      model: string;
      generated_at: string;
      cungs_unlocked?: boolean;
      cungs_locked?: boolean;
      cungs_price?: number;
      cungs_free?: boolean;
    }
  | { type: "error"; error: string };

/**
 * Khai mở lá số qua SSE stream. Yields từng event theo type.
 * Thứ tự dai_cuc / cungs không đảm bảo — xử lý theo type.
 * Dùng fetch + ReadableStream vì endpoint là POST với JWT header.
 */
export async function* streamTuviReading(
  chartId: string,
  opts: { forceRefresh?: boolean; year?: number; signal?: AbortSignal } = {},
): AsyncGenerator<TuviReadingEvent, void, void> {
  const res = await fetch(`${BOIDE_API_URL}/v1/tuvi/la-so/${chartId}/reading/stream`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ force_refresh: !!opts.forceRefresh, year: opts.year }),
    signal: opts.signal,
  });
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status}${text ? `: ${text.slice(0, 120)}` : ""}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n\n")) >= 0) {
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const line = raw.startsWith("data:") ? raw.slice(5).trim() : raw.trim();
      if (!line) continue;
      try {
        yield JSON.parse(line) as TuviReadingEvent;
      } catch {
        // bỏ qua dòng SSE không hợp lệ
      }
    }
  }
}

// ---------- Share (chia sẻ quẻ Kinh Dịch / lá số Tử Vi / Bát Tự) ----------

/** Tuỳ biến nội dung chia sẻ — masking enforce Ở BE (cả JSON lẫn ảnh OG). */
export type ShareOptions = {
  /** Che nhanh toàn bộ thông tin cá nhân (họ tên, ngày/giờ sinh, câu hỏi). */
  mask_pii: boolean;
  /** Kèm lời luận (khai mở / lời thầy) vào link chia sẻ. Mặc định KHÔNG. */
  include_reading: boolean;
  /** Che thêm từng trường cụ thể (whitelist theo loại resource). */
  masked_fields: string[];
  /** Che chuỗi chữ tuỳ ý (tên riêng, địa danh...) — BE thay bằng ***. */
  masked_texts: string[];
  /** Tông ảnh share: sáng (kem) hoặc tối. Vào hash options → cache-bust ảnh. */
  theme: "light" | "dark";
};

type SharedCommon = {
  mask_pii: boolean;
  has_image: boolean;
  options?: ShareOptions;
  slug?: string | null;
  /** Hash tuỳ chọn — cache-bust URL ảnh OG khi người chia sẻ đổi options. */
  image_version?: string;
};

// Dữ liệu công khai đã được BE che theo `options`.
export type SharedResource =
  | (SharedCommon & {
      resource_type: "tuvi";
      data: TuviChart & { reading_data?: TuviReading | null };
    })
  | (SharedCommon & {
      resource_type: "bazi";
      data: BaziChart & { reading_data?: BaziReading | null };
    })
  | (SharedCommon & {
      resource_type: "iching";
      data: {
        hexagram_result: HexagramResult;
        interpretation: string | null;
        diviner_id: string;
      };
    });

export type CreateShareResp = {
  token: string;
  slug?: string | null;
  resource_type: FortuneType;
  mask_pii: boolean;
  options: ShareOptions;
  has_image: boolean;
  /** Hash options — cache-bust URL ảnh khi đổi tuỳ chọn. */
  image_version?: string;
};

/** Tạo/cập nhật link chia sẻ cho 1 resource (idempotent ở BE — BE tự render ảnh OG). */
export async function createShare(
  resourceType: FortuneType,
  resourceId: string,
  options: Partial<ShareOptions>,
): Promise<CreateShareResp> {
  const res = await fetch(`${BOIDE_API_URL}/v1/share`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      resource_type: resourceType,
      resource_id: resourceId,
      mask_pii: options.mask_pii ?? true,
      include_reading: options.include_reading ?? false,
      masked_fields: options.masked_fields ?? [],
      masked_texts: options.masked_texts ?? [],
      theme: options.theme ?? "light",
    }),
  });
  return unwrap<CreateShareResp>(res);
}

/** Chụp board thật trong iframe ẩn rộng 1280px (viewport riêng → layout desktop,
 * đồng nhất mọi thiết bị) → Blob PNG. Render route tự chụp rồi postMessage về.
 * `theme` ∈ light/dark. Timeout tự huỷ để không treo. */
export function captureShareImageViaIframe(
  token: string,
  theme: "light" | "dark" = "light",
  timeoutMs = 15000,
): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("no document"));
      return;
    }
    const iframe = document.createElement("iframe");
    // Rộng 1280 (viewport iframe) → media query Tailwind lg:/md: = desktop.
    iframe.width = "1280";
    iframe.height = "1400";
    iframe.style.cssText =
      "position:fixed;left:-99999px;top:0;width:1280px;height:1400px;border:0;visibility:hidden;";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    iframe.src = `${origin}/render/${token}?theme=${theme}&t=${Date.now()}`;

    let settled = false;
    const cleanup = () => {
      window.removeEventListener("message", onMsg);
      clearTimeout(timer);
      iframe.remove();
    };
    const onMsg = (e: MessageEvent) => {
      if (e.source !== iframe.contentWindow) return;
      const d = e.data as { type?: string; ok?: boolean; blob?: Blob; error?: string };
      if (d?.type !== "boide-share-image") return;
      settled = true;
      cleanup();
      if (d.ok && d.blob) resolve(d.blob);
      else reject(new Error(d.error || "capture failed"));
    };
    const timer = setTimeout(() => {
      if (settled) return;
      cleanup();
      reject(new Error("capture timeout"));
    }, timeoutMs);

    window.addEventListener("message", onMsg);
    document.body.appendChild(iframe);
  });
}

/** Upload ảnh OG (PNG) cho link chia sẻ. KHÔNG set Content-Type cho FormData. */
export async function uploadShareImage(token: string, blob: Blob): Promise<void> {
  const form = new FormData();
  form.append("file", blob, `${token}.png`);
  const res = await fetch(`${BOIDE_API_URL}/v1/share/${token}/image`, {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });
  await unwrap<unknown>(res);
}

/** Tắt chia sẻ (revoke token). */
export async function revokeShare(token: string): Promise<void> {
  const res = await fetch(`${BOIDE_API_URL}/v1/share/${token}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  await unwrap<unknown>(res);
}

/** Đọc nội dung chia sẻ công khai — KHÔNG gửi auth header. */
export async function getSharedResource(token: string): Promise<SharedResource> {
  const res = await fetch(`${BOIDE_API_URL}/v1/share/${token}`, {
    headers: { "Content-Type": "application/json" },
  });
  return unwrap<SharedResource>(res);
}

/** URL ảnh OG (public, bot tải được) cho thẻ og:image. `version` để cache-bust
 * sau khi đổi tuỳ chọn chia sẻ (BE render ảnh mới). */
export function shareImageUrl(token: string, version?: string | number): string {
  const base = `${BOIDE_API_URL}/v1/share/${token}/image`;
  return version ? `${base}?v=${encodeURIComponent(String(version))}` : base;
}

/** Đường dẫn FE của link chia sẻ theo loại resource (kèm slug SEO nếu có). */
export function shareLinkPath(
  resourceType: FortuneType,
  token: string,
  slug?: string | null,
): string {
  const base = resourceType === "iching" ? `/q/${token}` : `/la-so/${token}`;
  return slug ? `${base}/${slug}` : base;
}

// ============================================================
// Matchmaking — Se duyên & hợp tác làm ăn
// ============================================================

export type MatchIntent = "se_duyen" | "lam_an";

/** Các trường user tự bật/tắt hiển thị công khai trên hồ sơ (khớp BE SHAREABLE_FIELDS).
 * Field vắng mặt trong map = mặc định HIỂN THỊ. */
export type ShareableField =
  | "real_name"
  | "full_name"
  | "birth_date"
  | "birth_time"
  | "occupation"
  | "current_location"
  | "hobbies"
  | "phone"
  | "email"
  | "social_links";

export type FieldVisibilityMap = Partial<Record<ShareableField, boolean>>;

/** Vị trí (mô hình mới): toạ độ chính xác + tỉnh sau sáp nhập + note địa chỉ cũ. */
export type LocationValue = {
  latitude?: number | null;
  longitude?: number | null;
  province_code?: string | null;
  region?: string | null;
  location_label?: string | null;
  legacy_location?: string | null;
};

/** Mục tiêu sống/giá trị — tách theo loại. */
export type GoalTagsMap = Partial<Record<MatchIntent, string[]>>;

export type MatchProfile = {
  user_id: number;
  /** URL hồ sơ tuỳ chỉnh (/se-duyen/{handle}); null = dùng user_id làm URL mặc định. */
  handle?: string | null;
  is_active: boolean;
  intents: MatchIntent[];
  display_alias?: string | null;
  gender?: string | null;
  birth_year?: number | null;
  chi_nam?: string | null;
  menh_ngu_hanh?: string | null;
  pref_gender?: string | null;
  pref_gender_lam_an?: string | null;
  pref_age_min?: number | null;
  pref_age_max?: number | null;
  pref_age_min_lam_an?: number | null;
  pref_age_max_lam_an?: number | null;
  field_visibility: FieldVisibilityMap;
  show_avatar?: boolean;
  latitude?: number | null;
  longitude?: number | null;
  province_code?: string | null;
  region?: string | null;
  location_label?: string | null;
  legacy_location?: string | null;
  goal_tags?: GoalTagsMap;
  hobbies?: string[];
  occupation?: string | null;
  photos?: string[];
};

export type CompatBand = { key: string; label: string; description?: string };

export type MatchFeedItem = {
  user_id: number;
  handle?: string | null;
  display_alias?: string | null;
  avatar?: string | null;
  photos?: string[];
  gender?: string | null;
  age?: number | null;
  chi_nam?: string | null;
  prelim_score: number;
  band: CompatBand;
  region?: string | null;
  province?: string | null;
  distance_label?: string | null;
  match_reasons?: string[];
};

/** Metadata cho FE: danh sách tỉnh (sau sáp nhập + alias cũ) + catalog goal tag. */
export type ProvinceMeta = {
  code: string;
  label: string;
  region: "bac" | "trung" | "nam" | string;
  aliases: string[];
};

export type MatchMeta = {
  provinces: ProvinceMeta[];
  goal_tags: Record<MatchIntent, Record<string, string>>;
};

export type SwipeResult = {
  connection_id?: string | null;
  status: "pending" | "connected" | "passed" | string;
  matched: boolean;
};

export type MatchConnectionSummary = {
  id: string;
  type: MatchIntent;
  status: string;
  prelim_score?: number | null;
  other_user_id: number;
  other_alias?: string | null;
  other_avatar?: string | null;
  i_liked?: boolean;
  they_liked?: boolean;
  has_reading: boolean;
};

/** Hồ sơ đầy đủ của 1 người — lọc theo field_visibility họ tự đặt. Xem được
 * ngay khi click trên feed (không cần match), kiểu Facebook Dating. */
export type FullMatchProfile = {
  user_id: number;
  handle?: string | null;
  display_alias?: string | null;
  avatar?: string | null;
  photos?: string[];
  gender?: string | null;
  age?: number | null;
  chi_nam?: string | null;
  menh_ngu_hanh?: string | null;
  prelim_score: number;
  band: CompatBand;
  match_reasons?: string[];
  region?: string | null;
  province?: string | null;
  distance_label?: string | null;
  goal_tags?: string[];
  real_name?: string | null;
  full_name?: string | null;
  birth_date?: string | null;
  birth_time?: string | null;
  occupation?: string | null;
  current_location?: string | null;
  hobbies?: string[];
  phone?: string | null;
  email?: string | null;
  social_links?: Record<string, string>;
  /** Lá số đã mask ngày/giờ sinh + họ tên — luôn hiện cấu trúc (sao/cung/trụ). */
  tuvi_chart?: TuviChart | null;
  bazi_chart?: BaziChart | null;
  field_visibility?: FieldVisibilityMap;
};

export type MatchConnectionDetail = MatchConnectionSummary & {
  other_photos?: string[];
  other_profile?: FullMatchProfile | null;
};

export type CompatReading = {
  connection_id: string;
  reading_md: string;
  score?: number | null;
  band?: CompatBand | null;
  model?: string | null;
  price_charged: number;
  balance?: number | null;
  cached: boolean;
};

export type MatchInvite = { code: string; type: MatchIntent; expires_at?: string | null };

export async function getMatchMeta(): Promise<MatchMeta> {
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/meta`, { headers: authHeaders() });
  return unwrap<MatchMeta>(res);
}

export async function getMatchProfile(): Promise<MatchProfile | null> {
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/profile`, { headers: authHeaders() });
  return unwrap<MatchProfile | null>(res);
}

export async function upsertMatchProfile(input: {
  intents: MatchIntent[];
  display_alias?: string;
  pref_gender?: string | null;
  pref_gender_lam_an?: string | null;
  pref_age_min?: number | null;
  pref_age_max?: number | null;
  pref_age_min_lam_an?: number | null;
  pref_age_max_lam_an?: number | null;
  field_visibility?: FieldVisibilityMap;
  show_avatar?: boolean;
  province_code?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  location_label?: string | null;
  legacy_location?: string | null;
  goal_tags?: GoalTagsMap;
  hobbies?: string[];
  occupation?: string | null;
  handle?: string | null;
}): Promise<MatchProfile> {
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/profile`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  return unwrap<MatchProfile>(res);
}

/** Thêm ảnh vào gallery se duyên (tối đa 9 ảnh tổng cộng). */
export async function uploadMatchPhotos(files: File[]): Promise<{ photos: string[] }> {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/profile/photos`, {
    method: "POST",
    headers: authHeaders(),
    body: fd,
  });
  return unwrap<{ photos: string[] }>(res);
}

export async function deleteMatchPhoto(index: number): Promise<{ photos: string[] }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/profile/photos/${index}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return unwrap<{ photos: string[] }>(res);
}

/** `photos` = toàn bộ URL hiện có theo thứ tự mới (hoán vị của gallery hiện tại). */
export async function reorderMatchPhotos(photos: string[]): Promise<{ photos: string[] }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/profile/photos/order`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ photos }),
  });
  return unwrap<{ photos: string[] }>(res);
}

export async function deactivateMatchProfile(): Promise<{ is_active: boolean }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/profile`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return unwrap(res);
}

export async function getMatchFeed(
  type: MatchIntent,
  limit?: number,
): Promise<{ type: MatchIntent; items: MatchFeedItem[] }> {
  const q = new URLSearchParams({ type });
  if (limit) q.set("limit", String(limit));
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/feed?${q.toString()}`, {
    headers: authHeaders(),
  });
  return unwrap(res);
}

export async function swipeMatch(input: {
  target_user_id: number;
  type: MatchIntent;
  action: "like" | "pass";
}): Promise<SwipeResult> {
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/swipe`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  return unwrap<SwipeResult>(res);
}

/** Hoàn tác lượt quẹt gần nhất với 1 ứng viên. */
export async function undoSwipe(input: {
  target_user_id: number;
  type: MatchIntent;
}): Promise<{ undone: boolean; status?: string }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/undo-swipe`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  return unwrap(res);
}

export async function searchMatch(
  q: string,
  type: MatchIntent,
): Promise<{ type: MatchIntent; items: MatchFeedItem[] }> {
  const params = new URLSearchParams({ q, type });
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/search?${params.toString()}`, {
    headers: authHeaders(),
  });
  return unwrap(res);
}

export async function createMatchInvite(type: MatchIntent): Promise<MatchInvite> {
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/invite?type=${type}`, {
    method: "POST",
    headers: authHeaders(),
  });
  return unwrap<MatchInvite>(res);
}

export async function acceptMatchInvite(code: string): Promise<SwipeResult> {
  const res = await fetch(
    `${BOIDE_API_URL}/v1/matchmaking/invite/${encodeURIComponent(code)}/accept`,
    { method: "POST", headers: authHeaders() },
  );
  return unwrap<SwipeResult>(res);
}

export async function listMatchConnections(status?: string): Promise<MatchConnectionSummary[]> {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/connections${q}`, {
    headers: authHeaders(),
  });
  const data = await unwrap<{ items: MatchConnectionSummary[] }>(res);
  return data.items || [];
}

export async function getMatchConnection(id: string): Promise<MatchConnectionDetail> {
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/connections/${id}`, {
    headers: authHeaders(),
  });
  return unwrap<MatchConnectionDetail>(res);
}

/** Huỷ kết nối với đối phương (đưa kết nối về trạng thái 'passed'). */
export async function disconnectMatchConnection(
  id: string,
): Promise<{ connection_id: string; status: string }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/connections/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return unwrap(res);
}

/** Xem hồ sơ đầy đủ 1 người trên feed (không cần match) — kiểu Facebook Dating.
 * `identifier` là handle tuỳ chỉnh hoặc user_id (URL mặc định khi chưa đặt handle). */
export async function getMatchCandidateProfile(
  identifier: string | number,
  type: MatchIntent,
): Promise<FullMatchProfile> {
  const res = await fetch(
    `${BOIDE_API_URL}/v1/matchmaking/candidates/${identifier}/profile?type=${type}`,
    { headers: authHeaders() },
  );
  return unwrap<FullMatchProfile>(res);
}

/** Đọc cache luận giải tương hợp; null nếu chưa có (BE 404). */
export async function getCompatReading(id: string): Promise<CompatReading | null> {
  const res = await fetch(`${BOIDE_API_URL}/v1/matchmaking/connections/${id}/reading`, {
    headers: authHeaders(),
  });
  if (res.status === 404) return null;
  return unwrap<CompatReading>(res);
}

/** Sinh luận giải tương hợp (trừ ví). Idempotent: trả cache nếu đã có. */
export async function castCompatReading(id: string, forceRefresh = false): Promise<CompatReading> {
  const res = await fetch(
    `${BOIDE_API_URL}/v1/matchmaking/connections/${id}/reading?force_refresh=${forceRefresh}`,
    { method: "POST", headers: authHeaders() },
  );
  return unwrap<CompatReading>(res);
}

/** Nhãn tiếng Việt cho từng trường share. */
export const SHARE_FIELD_LABELS: Record<ShareableField, string> = {
  real_name: "Tên thật",
  full_name: "Họ tên đầy đủ",
  birth_date: "Ngày sinh",
  birth_time: "Giờ sinh",
  occupation: "Nghề nghiệp",
  current_location: "Nơi ở",
  hobbies: "Sở thích",
  phone: "Số điện thoại",
  email: "Email",
  social_links: "Mạng xã hội",
};

// ── Feedback / góp ý ────────────────────────────────────────────────────────
export type FeedbackCategory = "loi" | "gop_y" | "trai_nghiem" | "khac";

export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  loi: "Báo lỗi",
  gop_y: "Góp ý tính năng",
  trai_nghiem: "Trải nghiệm sử dụng",
  khac: "Khác",
};

export async function submitFeedback(input: {
  category: FeedbackCategory;
  content: string;
  email?: string;
  page_url?: string;
}): Promise<{ id: string; message: string }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/feedback`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  return unwrap<{ id: string; message: string }>(res);
}

// ── Homepage reviews (đánh giá thật được admin chọn cho trang chủ) ──────────
export type HomepageReview = {
  id: string;
  display_name: string;
  content: string;
  /** Nhãn trải nghiệm đã map sẵn từ BE: "Kinh Dịch" | "Tử Vi" | "Bát Tự" | ... */
  experience: string;
  /** Sao hiển thị (BE trả 4 hoặc 5 ngẫu nhiên, không phải rating gốc). */
  rating: number;
  created_at: string;
};

export type HomepageReviewsResponse = {
  items: HomepageReview[];
  /** Còn review chưa hiển thị (ngoài exclude + batch này) — dùng cho load-more khi lướt ngang. */
  has_more: boolean;
  summary: {
    total_ratings: number;
    avg_rating: number;
  };
};

/**
 * Public — lấy tối đa 6 review admin đã chọn (BE random trong pool mỗi lần gọi).
 * `excludeIds`: id đã hiển thị — BE loại ra để load thêm batch mới không trùng.
 */
export async function getHomepageReviews(
  limit = 6,
  excludeIds: string[] = [],
): Promise<HomepageReviewsResponse> {
  const exclude = excludeIds.length ? `&exclude=${excludeIds.join(",")}` : "";
  const res = await fetch(`${BOIDE_API_URL}/v1/homepage-reviews?limit=${limit}${exclude}`);
  return unwrap<HomepageReviewsResponse>(res);
}

// ── Affiliate (giới thiệu + chiến dịch) ─────────────────────────────────────
const REFERRAL_KEY = "boide_ref";
const TRACK_KEY = "boide_track";
const REFERRAL_TTL_MS = 60 * 24 * 60 * 60 * 1000; // 60 ngày

/** Lưu 1 mã vào localStorage kèm timestamp để kiểm hạn khi đọc lại. */
function setStoredCode(key: string, code: string): void {
  if (typeof window === "undefined" || !code) return;
  try {
    localStorage.setItem(key, JSON.stringify({ code, ts: Date.now() }));
  } catch {
    /* ignore */
  }
}

/** Đọc mã còn hạn từ localStorage; null nếu không có/hết hạn. */
function getStoredCode(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { code, ts } = JSON.parse(raw) as { code?: string; ts?: number };
    if (!code || !ts || Date.now() - ts > REFERRAL_TTL_MS) return null;
    return code;
  } catch {
    return null;
  }
}

/**
 * Mã giới thiệu đang giữ + nó KHOÁ hay không.
 *
 * 🔴 `locked: true` = mã đến từ LINK (`?ref=`). Mã ấy KHÔNG bị đè — không bởi một link
 * `?ref=` khác bấm sau, không bởi ô nhập tay ở màn đăng nhập (ô đó hiện read-only).
 * Người giới thiệu đầu tiên là người được ghi công; cho đè là mở đường "cướp" người
 * được giới thiệu bằng cách gửi link của mình sau.
 *
 * `locked: false` = mã người dùng TỰ GÕ (ca mạng xã hội nuốt mất link). Vẫn phải lưu
 * xuống storage: đăng nhập Google/Apple trên iOS là redirect RỜI HẲN trang, lúc quay
 * về chỉ còn giá trị đã lưu. Mã gõ tay thì sửa/xoá được (gõ nhầm là chuyện thường).
 *
 * BE còn một lớp khoá nữa: `users.referred_by` chỉ ghi khi đang trống và chỉ cho tài
 * khoản MỚI tạo — tài khoản đã có người giới thiệu thì mã nào gửi lên cũng bị bỏ qua.
 */
export type StoredReferral = { code: string; locked: boolean };

export function getStoredReferralInfo(): StoredReferral | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(REFERRAL_KEY);
    if (!raw) return null;
    const { code, ts, locked } = JSON.parse(raw) as {
      code?: string;
      ts?: number;
      locked?: boolean;
    };
    if (!code || !ts || Date.now() - ts > REFERRAL_TTL_MS) return null;
    // Bản ghi cũ (trước khi có cờ `locked`) chỉ có thể đến từ link ⇒ coi là khoá.
    return { code, locked: locked !== false };
  } catch {
    return null;
  }
}

function writeReferral(code: string, locked: boolean): void {
  if (typeof window === "undefined" || !code) return;
  try {
    localStorage.setItem(REFERRAL_KEY, JSON.stringify({ code, ts: Date.now(), locked }));
  } catch {
    /* ignore */
  }
}

/**
 * Lưu mã giới thiệu từ LINK (`?ref=`) — khoá cứng, kèm hạn 60 ngày.
 * Đã có mã khoá còn hạn thì GIỮ NGUYÊN (không đè); trả về mã đang có hiệu lực.
 */
export function setStoredReferral(code: string): string | null {
  const clean = code.trim().toLowerCase();
  const current = getStoredReferralInfo();
  if (current?.locked) return current.code;
  if (!clean) return current?.code ?? null;
  writeReferral(clean, true);
  return clean;
}

/**
 * Lưu mã người dùng TỰ GÕ ở màn đăng nhập/đăng ký. Không làm gì nếu đang có mã khoá
 * từ link. Chuỗi rỗng = xoá mã gõ tay.
 */
export function setManualReferral(code: string): void {
  if (getStoredReferralInfo()?.locked) return;
  const clean = code.trim().toLowerCase();
  if (!clean) {
    try {
      localStorage.removeItem(REFERRAL_KEY);
    } catch {
      /* ignore */
    }
    return;
  }
  writeReferral(clean, false);
}

/** Đọc mã giới thiệu còn hạn; null nếu không có/hết hạn. */
export function getStoredReferral(): string | null {
  return getStoredReferralInfo()?.code ?? null;
}

/**
 * Kiểm mã giới thiệu có tồn tại không (endpoint public). Dùng khi người dùng TỰ GÕ mã:
 * BE nuốt mã sai trong im lặng lúc đăng ký, không kiểm trước thì người dùng tưởng đã
 * nhập mà người giới thiệu không bao giờ được ghi công.
 */
export async function checkReferralCode(code: string): Promise<{ valid: boolean; code: string }> {
  const res = await fetch(
    `${BOIDE_ACCOUNT_API_URL}/v1/affiliate/codes/${encodeURIComponent(code.trim().toLowerCase())}/check`,
  );
  return unwrap<{ valid: boolean; code: string }>(res);
}

/** Lưu nhãn tracking (từ ?track=) vào localStorage kèm hạn 60 ngày. */
export function setStoredTrack(code: string): void {
  setStoredCode(TRACK_KEY, code);
}

/** Đọc nhãn tracking còn hạn; null nếu không có/hết hạn. */
export function getStoredTrack(): string | null {
  return getStoredCode(TRACK_KEY);
}

export type AffiliateMe = {
  referral_code: string | null;
  referral_link: string | null;
  wallet_amount: number;
  commission_balance: number;
  total_commission: number;
  /** Số người đang gắn mã của tôi. Thiếu ở BE cũ ⇒ FE rơi về tổng của /me/referrals. */
  referral_count?: number;
  /** Mã giới thiệu TÔI đã gắn lúc đăng ký (khoá, không đổi được). */
  referred_by_code?: string | null;
  has_referrer?: boolean;
  campaign: {
    can_create: boolean;
    unlock_threshold: number;
    budget: number;
    budget_used: number;
    budget_available: number;
    max_promo_rate: number;
    owner_rate: number;
    max_codes: number;
  };
  min_payout: number;
  fanpage_url: string;
};

export type AffiliateCommission = {
  order_code: string;
  code: string | null;
  source: string;
  base_amount: number;
  rate: number;
  commission: number;
  status: string;
  created_at: string;
};

export type CampaignCode = {
  id: string;
  code: string;
  label: string | null;
  promo_rate: number;
  owner_rate: number;
  usage_limit: number | null;
  usage_count: number;
  active: boolean;
  created_at: string;
};

export type AffiliateReferral = {
  user_id: number;
  username: string;
  name: string | null;
  code: string | null;
  created_at: string;
};

export type AffiliatePayout = {
  id: string;
  amount: number;
  status: string; // pending | paid | rejected
  note: string | null;
  created_at: string;
  processed_at: string | null;
  bank_name: string | null;
  bank_account_number: string | null;
  bank_account_name: string | null;
};

export async function getAffiliateMe(): Promise<AffiliateMe> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/affiliate/me`, { headers: authHeaders() });
  return unwrap<AffiliateMe>(res);
}

/**
 * Gắn mã người giới thiệu cho tài khoản ĐANG đăng nhập (set-once, BE khoá bằng SQL).
 * Lỗi mang `data.reason`: `already_bound` | `invalid` | `self` | `cycle` | `no_user`.
 */
export async function bindMyReferrer(
  code: string,
): Promise<{ referred_by_code: string; has_referrer: boolean }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/affiliate/me/referrer`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ code: code.trim().toLowerCase() }),
  });
  return unwrap(res);
}

export function referrerErrorReason(err: unknown): string | undefined {
  const data = (err as { data?: { reason?: unknown } } | null)?.data;
  return typeof data?.reason === "string" ? data.reason : undefined;
}

export function clearStoredReferral(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(REFERRAL_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Gọi ngay sau khi CÓ TOKEN (đăng nhập xong, hoặc bấm link `?ref=` khi đã đăng nhập):
 * gắn mã đang giữ vào tài khoản luôn, không đợi tới lượt nạp tiền.
 *
 * Trả mã vừa gắn (để hiện toast), `null` nếu không có gì để gắn / không gắn được.
 * KHÔNG ném — đây là việc phụ, không được làm hỏng lượt đăng nhập.
 *
 * Mã đang giữ chỉ bị XOÁ khi gắn XONG hoặc mã KHÔNG TỒN TẠI. Ca "tài khoản này đã gắn
 * rồi" / "mã của chính mình" thì GIỮ: cùng máy đó có thể đăng ký một tài khoản khác sau,
 * và mã từ link vẫn phải đi theo — cái giá chỉ là một request nhỏ mỗi lần đăng nhập.
 */
export async function autoBindStoredReferral(): Promise<string | null> {
  const code = getStoredReferral();
  if (!code || !getBoideToken()) return null;
  try {
    const res = await bindMyReferrer(code);
    clearStoredReferral();
    return res.referred_by_code;
  } catch (err) {
    if (referrerErrorReason(err) === "invalid") clearStoredReferral();
    return null;
  }
}

export async function setAffiliateCode(code: string): Promise<{ code: string; link: string }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/affiliate/me/code`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ code }),
  });
  return unwrap(res);
}

export async function createCampaignCode(input: {
  code: string;
  label?: string;
  promo_rate: number;
  usage_limit: number;
}): Promise<{ code: string; promo_rate: number; usage_limit: number }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/affiliate/me/campaign-codes`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  return unwrap(res);
}

export async function listCampaignCodes(): Promise<{ items: CampaignCode[] }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/affiliate/me/campaign-codes`, {
    headers: authHeaders(),
  });
  return unwrap(res);
}

/** Mỗi trang bao nhiêu dòng sổ hoa hồng / người giới thiệu. Trùng mặc định của BE. */
export const AFFILIATE_PAGE_SIZE = 10;

/** Bộ lọc nguồn hoa hồng — lọc ở SERVER, không lọc lại trên trang 10 dòng. */
export type CommissionSource = "all" | "referral" | "campaign";

/**
 * Một trang sổ hoa hồng.
 *
 * ⚠️ `total` do BE mới trả; BE cũ không có nên rơi về số dòng của trang — thanh phân
 * trang khi ấy chỉ hiện một trang, không sai lệch gì.
 */
export async function getAffiliateCommissions(
  limit = AFFILIATE_PAGE_SIZE,
  offset = 0,
  source: CommissionSource = "all",
): Promise<{ items: AffiliateCommission[]; total: number }> {
  const res = await fetch(
    `${BOIDE_ACCOUNT_API_URL}/v1/affiliate/me/commissions?limit=${limit}&offset=${offset}&source=${source}`,
    { headers: authHeaders() },
  );
  const data = await unwrap<{ items?: AffiliateCommission[]; total?: number }>(res);
  const items = data?.items ?? [];
  return { items, total: Number(data?.total ?? items.length) };
}

export async function getAffiliateReferrals(
  limit = AFFILIATE_PAGE_SIZE,
  offset = 0,
): Promise<{ items: AffiliateReferral[]; total: number }> {
  const res = await fetch(
    `${BOIDE_ACCOUNT_API_URL}/v1/affiliate/me/referrals?limit=${limit}&offset=${offset}`,
    { headers: authHeaders() },
  );
  const data = await unwrap<{ items?: AffiliateReferral[]; total?: number }>(res);
  const items = data?.items ?? [];
  return { items, total: Number(data?.total ?? items.length) };
}

export async function convertCommission(amount: number): Promise<{
  message: string;
  wallet_amount: number;
  commission_balance: number;
}> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/affiliate/me/convert`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ amount }),
  });
  return unwrap(res);
}

export async function requestAffiliatePayout(input: {
  amount: number;
  bank_name: string;
  bank_account_number: string;
  bank_account_name: string;
}): Promise<{
  message: string;
  payout_id: string;
  amount: number;
  remaining_balance: number;
}> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/affiliate/me/payout`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(input),
  });
  return unwrap(res);
}

export async function getAffiliatePayouts(): Promise<{ items: AffiliatePayout[] }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/affiliate/me/payouts`, {
    headers: authHeaders(),
  });
  return unwrap(res);
}

export type SavedBankAccount = {
  id: string;
  bank_name: string;
  bank_account_number: string;
  bank_account_name: string;
  last_used_at: string;
};

// BE tự lưu (upsert) tài khoản mỗi lần gửi yêu cầu rút thành công
export async function getSavedBankAccounts(): Promise<{ items: SavedBankAccount[] }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/affiliate/me/bank-accounts`, {
    headers: authHeaders(),
  });
  return unwrap(res);
}

export async function deleteSavedBankAccount(id: string): Promise<{ deleted: boolean }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/affiliate/me/bank-accounts/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return unwrap(res);
}

// === Hộp thư (Inbox) — thư hệ thống, thưởng, khiếu nại/đề xuất ===

export type InboxAttachment = {
  name: string;
  size: number;
  content_type?: string;
};

export type InboxMessage = {
  id: number;
  title: string;
  body_md: string;
  reward_amount: number;
  has_reward: boolean;
  expires_at: string | null;
  created_at: string;
  is_read: boolean;
  claimed: boolean;
  reward_expired: boolean;
  attachments?: InboxAttachment[];
};

export type InboxTicketCategory = "gop_y" | "khieu_nai" | "de_xuat" | "khac";

export type InboxTicket = {
  id: number;
  category: string;
  subject: string;
  content: string;
  status: string;
  reply_message_id: number | null;
  created_at: string;
};

export async function listInbox(
  params: { limit?: number; offset?: number } = {},
): Promise<{ items: InboxMessage[]; limit: number; offset: number }> {
  const query = new URLSearchParams({
    limit: String(params.limit ?? 20),
    offset: String(params.offset ?? 0),
  });
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/inbox?${query.toString()}`, {
    headers: authHeaders(),
  });
  return unwrap(res);
}

export async function getInboxUnreadCount(): Promise<{ unread: number }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/inbox/unread-count`, {
    headers: authHeaders(),
  });
  return unwrap(res);
}

export async function getInboxMessage(messageId: number): Promise<InboxMessage> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/inbox/${messageId}`, {
    headers: authHeaders(),
  });
  return unwrap(res);
}

export async function markInboxRead(messageId: number): Promise<{ message: string }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/inbox/${messageId}/read`, {
    method: "POST",
    headers: authHeaders(),
  });
  return unwrap(res);
}

export async function markAllInboxRead(): Promise<{ message: string }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/inbox/read-all`, {
    method: "POST",
    headers: authHeaders(),
  });
  return unwrap(res);
}

export async function claimInboxReward(
  messageId: number,
): Promise<{ message: string; reward_amount: number; current_amount: number }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/inbox/${messageId}/claim`, {
    method: "POST",
    headers: authHeaders(),
  });
  return unwrap(res);
}

export async function deleteInboxMessage(messageId: number): Promise<{ message: string }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/inbox/${messageId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return unwrap(res);
}

export async function createInboxTicket(payload: {
  category: InboxTicketCategory;
  subject: string;
  content: string;
  files?: File[];
}): Promise<{ id: number; message: string }> {
  const fd = new FormData();
  fd.append("category", payload.category);
  fd.append("subject", payload.subject);
  fd.append("content", payload.content);
  (payload.files || []).forEach((f) => fd.append("files", f));
  // KHÔNG set Content-Type cho FormData (browser tự thêm boundary).
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/inbox/tickets`, {
    method: "POST",
    headers: authHeaders(),
    body: fd,
  });
  return unwrap(res);
}

/** Tải 1 tệp đính kèm của thư (kèm Bearer token) rồi lưu xuống máy. */
export async function downloadInboxAttachment(
  messageId: number,
  idx: number,
  filename: string,
): Promise<void> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/inbox/${messageId}/attachments/${idx}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Không tải được tệp");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function listInboxTickets(): Promise<{ items: InboxTicket[] }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/inbox/tickets`, {
    headers: authHeaders(),
  });
  return unwrap(res);
}

// === Popup thông báo (Announcements) — admin tạo, hiện khi vào web ===

export type Announcement = {
  id: number;
  title: string;
  body_md: string;
  /** Đường dẫn TƯƠNG ĐỐI khi ảnh nằm trên MinIO của BE, hoặc URL tuyệt đối khi admin dán link. */
  image_url: string | null;
  cta_label: string | null;
  cta_url: string | null;
  priority: number;
  ends_at: string | null;
  /** false = admin chặn bỏ qua vĩnh viễn → chỉ hiện nút "Tắt thông báo". */
  allow_dismiss: boolean;
  /** Quà kèm popup, cộng thẳng vào Ví Bói. 0 = popup thường, không có quà. */
  reward_amount: number;
  /** Đã nhận quà này rồi (tính theo TÀI KHOẢN — nhận trên app thì web cũng thấy đã nhận). */
  reward_claimed: boolean;
};

/** Ghép base API cho ảnh do BE phục vụ; URL ngoài thì giữ nguyên. */
export function announcementImageSrc(announcement: Announcement): string | null {
  const url = announcement.image_url;
  if (!url) return null;
  return url.startsWith("/") ? `${BOIDE_API_URL}${url}` : url;
}

/**
 * Nền tảng gửi lên BE để lọc popup: mở từ icon màn hình chính (PWA đã cài) →
 * `pwa`, tab trình duyệt thường → `web`. Admin tick riêng từng nền tảng.
 */
function announcementPlatform(): "web" | "pwa" {
  return isStandalonePwa() ? "pwa" : "web";
}

export async function listActiveAnnouncements(): Promise<{ items: Announcement[] }> {
  const res = await fetch(
    `${BOIDE_ACCOUNT_API_URL}/v1/announcements/active?platform=${announcementPlatform()}`,
    { headers: authHeaders() },
  );
  return unwrap(res);
}

/** Đếm lượt popup thật sự hiện ra (số liệu cho admin). Lỗi ở đây không đáng quan tâm. */
export async function markAnnouncementSeen(id: number): Promise<void> {
  await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/announcements/${id}/seen`, {
    method: "POST",
    headers: authHeaders(),
  }).catch(() => undefined);
}

/** "Không hiện lại nữa" — tắt hẳn popup này với tài khoản hiện tại. */
export async function dismissAnnouncement(id: number): Promise<{ message: string }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/announcements/${id}/dismiss`, {
    method: "POST",
    headers: authHeaders(),
  });
  return unwrap(res);
}

/**
 * Nhận quà của popup — MỖI TÀI KHOẢN MỘT LẦN, tiền vào thẳng Ví Bói.
 *
 * BE là nơi chốt "một lần" (cờ `claimed` đặt atomic trước khi cộng ví), nên bấm
 * hai lần hay bấm song song trên hai thiết bị cũng chỉ cộng đúng một lần — lần
 * thua cuộc nhận lỗi "Bạn đã nhận quà này rồi".
 */
export async function claimAnnouncementReward(
  id: number,
): Promise<{ message: string; reward_amount: number; current_amount: number }> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/announcements/${id}/claim`, {
    method: "POST",
    headers: authHeaders(),
  });
  return unwrap(res);
}

// ============================================================
// Bói chỉ tay — xem tướng bàn tay qua ảnh (/v1/palm)
// ============================================================

/** 4 khung ảnh bắt buộc mỗi người. "palm" = ngửa (lòng bàn tay), "back" = úp (mu). */
export type PalmSlot = "left_palm" | "left_back" | "right_palm" | "right_back";

export const PALM_SLOTS: PalmSlot[] = ["left_palm", "left_back", "right_palm", "right_back"];

export type PalmHand = "left" | "right";
export type PalmFinger = "cai" | "tro" | "giua" | "ap_ut" | "ut";
export type PalmFingerprintPattern = "mong_thu" | "xoay_nuoc" | "cung" | "hon_hop";
/** Điểm tụ — trục THỨ HAI của vân: các đường vân quy tụ về đâu. */
export type PalmConvergence = "doc" | "ngang" | "khu_oc" | "lon_xon";
export type PalmWholeHandTrait = "chung_doc" | "xoan_xien_ngang" | "xoan_bat_dinh";

/**
 * Lời khai vân. Hai khoá `left`/`right` là CHỦNG vân (hợp đồng dữ liệu cũ, đừng đổi);
 * `diem_tu` và `chung` là hai khoá THÊM cho trục điểm tụ.
 */
export type PalmFingerprintSelection = Partial<
  Record<PalmHand, Partial<Record<PalmFinger, PalmFingerprintPattern>>>
> & {
  diem_tu?: Partial<Record<PalmHand, Partial<Record<PalmFinger, PalmConvergence>>>>;
  chung?: PalmWholeHandTrait[];
};

/**
 * 🔴 Bảng này KHÔNG ĐỀU và không được làm cho đều: sách không có mục "ngón TRỎ — khu
 * ốc" lẫn "ngón ÁP ÚT — ngang". Hiện ô cho hai cặp ấy là mời người xem khai một thứ
 * rồi BE lặng lẽ bỏ (`palm_fingerprint.CONVERGENCE_BY_FINGER` là nguồn sự thật).
 */
export const PALM_CONVERGENCE_BY_FINGER: Record<PalmFinger, PalmConvergence[]> = {
  cai: ["doc", "ngang", "khu_oc", "lon_xon"],
  tro: ["doc", "ngang", "lon_xon"],
  giua: ["doc", "ngang", "khu_oc", "lon_xon"],
  ap_ut: ["doc", "khu_oc", "lon_xon"],
  ut: ["doc", "ngang", "khu_oc", "lon_xon"],
};

export const PALM_CONVERGENCE_LABELS: Record<PalmConvergence, string> = {
  doc: "Tụ dọc theo ngón",
  ngang: "Tụ theo chiều ngang",
  khu_oc: "Tụ thành khu ốc (xoáy vào giữa)",
  lon_xon: "Tụ lộn xộn, phức tạp",
};

export const PALM_WHOLE_HAND_LABELS: Record<PalmWholeHandTrait, string> = {
  chung_doc: "Nhìn chung điểm tụ nằm dọc theo ngón",
  xoan_xien_ngang: "Có đường xoắn xiên / ngang ở ngón nào đó",
  xoan_bat_dinh: "Có đường xoắn bất định, lệch lạc",
};

export type PalmNailShape = "tu_giac" | "dui" | "chu_nhat" | "kim_cuong" | "kiem" | "rong" | "boi";
export type PalmNailSurface = "lom" | "loi";
export type PalmNailRidge = "khong" | "doc" | "ngang";
/**
 * Đặc điểm móng người xem tự khai.
 *
 * ⚠️ Bốn khoá dưới `ridge` là phần BÙ (09/08/2026) đưa độ phủ nhóm `mong_tay` từ
 * 9/55 lên 53/55. Chúng dùng `string` chứ không phải union: danh sách khoá hợp lệ
 * sinh từ cây taxonomy ở backend (`palm_nail`), gõ lại thành union ở đây là dựng
 * nguồn thứ hai chắc chắn sẽ lệch. Ô chọn luôn dựng từ `PalmNailConfig`.
 */
export type PalmNailSelection = {
  shape?: PalmNailShape;
  surface?: PalmNailSurface;
  ridge?: PalmNailRidge;
  /** Có / mất vành lưỡi liềm trắng ở gốc móng. */
  ban_nguyet?: string;
  /** Bán nguyệt ngón nào chuyển màu phấn hồng. */
  ban_nguyet_ngon?: string[];
  /** Màu & dấu trên móng — chọn nhiều. */
  mau_sac?: string[];
  /** Tình trạng bề mặt — chọn nhiều. */
  tinh_trang?: string[];
  /** Mô tả chi tiết trọn câu — chọn nhiều. */
  mo_ta?: string[];
};

/** Bốn nhóm bù đều là danh sách chọn-nhiều, trừ `ban_nguyet` (chọn một). */
export type PalmNailExtraField = "mau_sac" | "tinh_trang" | "mo_ta" | "ban_nguyet_ngon";

export type PalmFingerprintConfig = {
  /** Khai báo là tuỳ chọn: bỏ trống thì bài luận dùng phần vân máy đọc từ ảnh. */
  optional?: boolean;
  note?: string;
  hands: { key: PalmHand; label: string }[];
  fingers: { key: PalmFinger; label: string }[];
  patterns: { key: PalmFingerprintPattern; label: string; description: string }[];
};

export type PalmNailOption = { key: string; label: string };

export type PalmNailConfig = {
  optional?: boolean;
  note?: string;
  shapes: { key: PalmNailShape; label: string }[];
  surfaces: { key: PalmNailSurface; label: string }[];
  ridges: { key: PalmNailRidge; label: string }[];
  /**
   * Bốn nhóm bù. Nhãn là NGUYÊN VĂN nhãn lá trong cây tướng tay ("Móng mỏng, xòe như
   * cái quạt"), nên FE cố ý KHÔNG giữ bản chép fallback: 20 câu dài chép lại là chắc
   * chắn có ngày lệch, và khi lệch thì app hỏi một đằng còn bài luận nhận nghĩa một
   * nẻo. BE cũ chưa trả nhóm nào thì FE ẩn hẳn nhóm ấy.
   */
  colors?: PalmNailOption[];
  lunulas?: PalmNailOption[];
  lunula_fingers?: PalmNailOption[];
  conditions?: PalmNailOption[];
  details?: PalmNailOption[];
};

export type PalmConfig = {
  enabled: boolean;
  max_persons: number;
  price: number;
  extra_person_price: number;
  /** Văn bản hướng dẫn nghi thức chắp tay (BE giữ để sửa được không cần deploy). */
  ritual_instruction: string;
  slots: { key: PalmSlot; label: string; hint: string; field: string }[];
  /**
   * Ô chọn vân đầu ngón. TUỲ CHỌN: máy đã quét vân từ ảnh, người xem khai thì lời
   * khai thắng bản quét, bỏ trống thì bài luận dùng kết quả máy đọc.
   */
  fingerprints?: PalmFingerprintConfig;
  /** Ba đặc điểm móng người xem tự chọn — cũng tuỳ chọn, cùng quy tắc với vân. */
  nails?: PalmNailConfig;
  sections: { key: string; label: string }[];
  accepted_types: string[];
  /**
   * Luồng nháp → quét 16 tiêu chí → người xem soát bảng → luận.
   * Thiếu = BE cũ, khi đó lùi về `segmentation.enabled` như trước.
   *
   * ⚠️ KHÔNG phải cùng một thứ với `segmentation`: model vẽ nét có thể tắt (hiện
   * đang tắt) trong khi màn soát bảng vẫn chạy — đó là chỗ duy nhất người xem sửa
   * được ô máy đọc sai và khai vân đầu ngón / móng.
   */
  scan_review?: PalmScanReviewConfig;
  /** Model vẽ nét đường chỉ tay (v88). `enabled=false` = đang tạm ẩn bước duyệt nét. */
  segmentation?: PalmSegmentationConfig;
  training_consent?: PalmConsentDocument;
  /** Thông tin sinh tuỳ chọn. Thiếu = BE cũ (vẫn bắt buộc khai ngày sinh). */
  birth?: PalmBirthConfig;
};

/** Ngày sinh có bắt buộc hay không — BE quyết, FE không cứng hoá. */
export type PalmBirthConfig = {
  optional: boolean;
  note: string;
  hour_optional_note: string;
};

/**
 * Thẻ chủ đề bói chỉ tay (`GET /v1/palm/topics`, migration v89).
 *
 * `slots` quyết định form hỏi mấy ảnh, `sections` quyết định bài luận có mục nào.
 * Danh sách rỗng từ BE = chưa chạy migration → FE dùng form toàn diện như trước.
 */
export type PalmTopic = {
  slug: string;
  name: string;
  description: string;
  icon: string;
  group_key: string;
  focus: string;
  price: number;
  extra_person_price: number;
  min_persons: number;
  max_persons: number;
  sections: string[];
  /** Khung ảnh TỐI THIỂU của thẻ — tay mặc định khi `hand_choice` bật. */
  slots: PalmSlot[];
  config: {
    question_required?: boolean;
    birth_required?: boolean;
    question_allowed?: boolean;
  };
  /**
   * Thẻ có hỏi câu hỏi trọng tâm TRƯỚC khi luận không.
   *
   * `false` với thẻ TOÀN DIỆN: bài đã viết đủ 9 mục nên không có trọng tâm nào để
   * chọn, và người xem hỏi thêm được ở phiên chat sau khi đã có bài. BE suy cờ này
   * (mặc định theo slug, DB đè được qua `config.question_allowed`) để FE khỏi cứng
   * hoá slug. BE cũ không trả cờ → FE mặc định `true`, tức hành vi cũ.
   */
  question_allowed?: boolean;
  section_labels: { key: string; label: string }[];
  slot_labels: { key: PalmSlot; label: string; hint: string; field: string }[];
  /** Thẻ chỉ cần MỘT tay → mỗi người tự chọn trái/phải (field `hand` của người đó). */
  hand_choice: boolean;
  hands: { key: PalmHand; label: string }[];
  /**
   * Gợi ý cho NGƯỜI XEM nên chụp tay nào (không phải chỉ thị cho LLM) — chỉ có ý
   * nghĩa khi `hand_choice` bật. `null` = không có gợi ý riêng cho thẻ này.
   */
  hand_recommendation: { role: "active" | "innate" | null; text: string } | null;
  /** Khung ảnh ứng với từng lựa chọn tay — chỉ có khi `hand_choice` bật. */
  slot_labels_by_hand: Partial<
    Record<PalmHand, { key: PalmSlot; label: string; hint: string; field: string }[]>
  >;
  /** Giá MỖI ảnh gửi thêm ngoài mức tối thiểu (tuỳ chọn, không bắt buộc). */
  extra_image_price: number;
};

export type PalmPersonInput = {
  ho_ten?: string;
  nam?: number;
  thang?: number;
  ngay?: number;
  gio?: number;
  phut?: number;
  gioi_tinh?: "nam" | "nu" | "nữ";
  is_lunar?: boolean;
  /** Sinh trong tháng âm NHUẬN — chỉ có nghĩa khi is_lunar = true. */
  is_leap_month?: boolean;
  /** Ngón cái tay nào nằm DƯỚI khi chắp tay → tay đó là tay tích cực. */
  dominant_thumb?: "left" | "right" | null;
  thuan_tay?: "left" | "right" | null;
  /**
   * Tay người này chọn để chụp, khi thẻ chỉ cần MỘT tay (`PalmTopic.hand_choice`).
   * Bỏ trống → BE dùng tay mặc định của thẻ. Khác `dominant_thumb` (ngón cái nào
   * nằm dưới khi chắp tay) và `thuan_tay` (tay thuận) — ba câu hỏi ba việc.
   */
  hand?: "left" | "right" | null;
  /**
   * Bàn tay ĐÃ LƯU dùng cho người này (`PalmProfile.profile_id`).
   *
   * BE lấy ảnh của người thứ nhất trong hồ sơ ấy và COPY vào lượt mới — lượt mới tự
   * sở hữu bản copy, nên xoá bài không làm mù hồ sơ và ngược lại. Khung nào hồ sơ
   * không có thì vẫn phải chụp như thường.
   */
  profile_id?: string;
  /** Tùy chọn: tay trái/phải × 5 ngón, chỉ chứa các ngón đã chọn. */
  van_dau_ngon?: PalmFingerprintSelection;
  /** Tùy chọn: một bộ đặc điểm đại diện cho móng của người xem. */
  dac_diem_mong?: PalmNailSelection;
  use_profile?: boolean;
};

export type PalmStartInput = {
  persons: PalmPersonInput[];
  question?: string;
  include_hexagram?: boolean;
  diviner_id?: string;
  /** Thẻ chủ đề. Bỏ trống = bài toàn diện (hành vi trước v89). */
  topic_slug?: string;
};

/** Một mục checklist đường chỉ tay trong bản quét (mô tả bằng chữ, không toạ độ). */
export type PalmScanLine = {
  key: string;
  label: string;
  primary: boolean;
  trang_thai: string;
  do_dai: string;
  diem_bat_dau: string;
  diem_ket_thuc: string;
  huong: string;
  khoi_diem: string;
  mo_ta: string;
  nhanh_re: { vi_tri_pct: number | null; mo_ta: string }[];
  dut_gay: { vi_tri_pct: number | null; mo_ta: string }[];
  do_tin_cay: number;
};

export type PalmScanMount = {
  key: string;
  label: string;
  do_no: string;
  mo_ta: string;
  do_tin_cay: number;
};

export type PalmScanFinger = { ngon: string; label: string; loai: string; do_tin_cay: number };

export type PalmScanNail = {
  ngon: string;
  label: string;
  hinh_dang: string;
  mau_sac: string;
  ban_nguyet: string;
  mo_ta: string;
};

export type PalmScanPattern = {
  key: string;
  label: string;
  trang_thai: string;
  vi_tri: string;
  mo_ta: string;
  do_tin_cay: number;
};

/**
 * Bản quét MỘT tấm ảnh — dữ kiện thô bằng chữ, do tầng quét (LLM) sinh (v86).
 * Không còn toạ độ/hotspot; giao diện hiển thị thẳng checklist này cho người xem.
 */
export type PalmScan = {
  slot: PalmSlot;
  slot_label: string;
  hand: "left" | "right";
  view: "palm" | "back";
  khung: { khai_bao: string; tu_xac_dinh: string; canh_bao_khung: boolean; ghi_chu: string };
  chat_luong: { du_ro: boolean; nghi_bi_guong: boolean; tu_the: string; van_de: string[] };
  tong_quan: {
    form_tay: { gia_tri: string; mo_ta: string; do_tin_cay: number };
    khi_sac: { gia_tri: string; mo_ta: string; do_tin_cay: number };
    da_gan_khop: { trang_thai: string; mo_ta: string };
    ty_le_ngon: { tro_so_voi_ap_ut: string; ngon_ut: string; mo_ta: string; do_tin_cay: number };
    go: PalmScanMount[];
    van_tay: PalmScanFinger[];
    mong_tay: PalmScanNail[];
  };
  duong_chinh: PalmScanLine[];
  hoa_van: PalmScanPattern[];
  diem_noi_bat: string[];
  ghi_chu_kiem_chung: string[];
  ok: boolean;
};

export type PalmSection = { key: string; label: string; content_md: string };

export type PalmFollowUp = { label: string; question: string };

export type PalmImageMeta = {
  person_index: number;
  slot: PalmSlot;
  label: string;
  key: string;
  width: number;
  height: number;
  size: number;
};

export type PalmHandRoles = {
  person_index: number;
  ho_ten?: string | null;
  mode: "ritual" | "combined";
  dominant_thumb: "left" | "right" | null;
  active_hand: "left" | "right";
  innate_hand: "left" | "right";
  tie_break_hand: "left" | "right" | null;
};

export type PalmReading = {
  reading_id: string;
  session_id: string | null;
  loi_chao: string;
  hand_note: string;
  hand_roles: PalmHandRoles[];
  sections: PalmSection[];
  /** Bảng quét 16 tiêu chí đã dùng để luận (một bản mỗi người). Bài cũ: rỗng. */
  scan_reports?: PalmPersonReport[];
  /** Bản quét từng ảnh của bài mua TRƯỚC bản 16 tiêu chí. Bài mới: rỗng. */
  scans: PalmScan[];
  /** Nét người xem đã duyệt (v88). Bài xem qua `/start` hoặc bài cũ: rỗng. */
  geometry?: PalmImageGeometry[];
  follow_up_questions: PalmFollowUp[];
  disclaimer: string;
  images: PalmImageMeta[];
  fingerprints?: PalmFingerprintSelection[];
  nails?: PalmNailSelection[];
  reading_md: string;
  question?: string | null;
  tuvi_charts: TuviChart[];
  bazi_charts: BaziChart[];
  hexagram: HexagramResult | null;
  price_charged: number;
  balance?: number | null;
  model?: string | null;
  created_at?: string;
  /**
   * Đánh giá của người xem (BE v114). `null`/thiếu = BE chưa lên bản mới hoặc chưa
   * chạy migration ⇒ ẩn khối chấm sao (chấm vào lúc ấy chỉ nhận về lỗi).
   */
  rating?: PalmReadingRatingState | null;
};

export type PalmReadingRatingState = {
  rating: number | null;
  rating_comment: string | null;
  rating_at: string | null;
  rating_comment_rewarded_at: string | null;
};

/**
 * Chấm sao (1..5) + nhận xét cho bài luận chỉ tay. `ratingComment` không truyền =
 * giữ nguyên nhận xét cũ; truyền `null`/"" = xoá. Nhận xét đầu (≥10 từ) được hoàn
 * 1.000đ đúng một lần (`rewarded: true`).
 */
export async function ratePalmReading(
  readingId: string,
  rating: number | null,
  ratingComment?: string | null,
): Promise<{
  reading_id: string;
  rating: number | null;
  rating_comment?: string | null;
  rating_comment_rewarded_at?: string | null;
  rewarded: boolean;
  reward_amount: number;
  current_amount?: number | null;
}> {
  const body: { rating: number | null; rating_comment?: string | null } = { rating };
  if (ratingComment !== undefined) body.rating_comment = ratingComment;
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/readings/${readingId}/rating`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  return unwrap(res);
}

/**
 * Lượt xem tay đang ở đâu.
 *
 * `ready` là bài đã luận xong. Bốn giá trị còn lại là lượt ĐÃ TRẢ TIỀN nhưng chưa
 * ra bài — trước 14/09/2026 chúng không hiện ở đâu cả, rồi 24h sau bị xoá cứng kèm
 * ảnh; nay chúng nằm ngay trong lịch sử để bấm vào là đi tiếp.
 *
 * ⚠️ `undefined` nghĩa là BE chưa lên bản mới ⇒ coi như `ready` (trước đó danh sách
 * chỉ chứa bài đã xong). Đừng đổi mặc định ấy: đoán nhầm thành nháp là đưa người
 * xem sang màn quét của một bài họ đã đọc được.
 */
export type PalmHistoryStage = "ready" | "scanning" | "reading" | "scanned" | "charged";

export type PalmReadingListItem = {
  id: string;
  session_uuid: string | null;
  names: (string | null)[];
  persons_count: number;
  question: string | null;
  price_charged: number;
  created_at: string;
  stage?: PalmHistoryStage;
  is_draft?: boolean;
};

/** Lượt này còn dở hay đã có bài luận để mở. */
export function isPalmDraftItem(item: PalmReadingListItem): boolean {
  if (typeof item.is_draft === "boolean") return item.is_draft;
  return Boolean(item.stage && item.stage !== "ready");
}

/** Nhãn ngắn hiện cạnh một lượt còn dở. `null` = bài đã xong, không cần nhãn. */
export function palmStageLabel(item: PalmReadingListItem): string | null {
  if (!isPalmDraftItem(item)) return null;
  switch (item.stage) {
    case "scanning":
      return "Đang đọc bàn tay…";
    case "reading":
      return "Thầy đang luận…";
    case "scanned":
      return "Chưa luận — bấm để tiếp tục";
    default:
      // `charged`: đã trừ tiền mà chưa có bản quét (việc nền hỏng hoặc chưa chạy tới
      // nơi). Mời họ vào tiếp chứ đừng nói "lỗi" — vào trong sẽ có nút chạy lại.
      return "Còn dở — bấm để tiếp tục";
  }
}

// ── Bước duyệt nét chỉ tay (v88) ────────────────────────────────────────────
//
// Model segmentation vẽ nét trước, người xem sửa, RỒI mới luận. Ba bước, và chỉ
// bước cuối trừ tiền:
//   createPalmDraft  → savePalmGeometry (gọi bao nhiêu lần cũng được) → startPalmDraftReading
//
// Nét `unreviewed` là PHỎNG ĐOÁN của máy, không phải kết quả đã chốt. Giao diện
// phải nói rõ điều đó: người xem tưởng nó đã đúng thì họ không sửa gì, và cả bước
// duyệt trở thành vô nghĩa.

/**
 * Phán quyết của người xem cho một đường:
 * - `unreviewed` chưa xem tới (máy vẽ) — KHÔNG vào dữ liệu học, và bài luận chỉ coi là gợi ý
 * - `confirmed` máy vẽ đúng · `edited` đã kéo lại · `added` tự vẽ thêm
 * - `absent` tay này KHÔNG có đường đó (dữ kiện quý nhất người xem cho được)
 *
 * BE tự suy lại trạng thái bằng cách so với nét gốc, nên FE gửi gần đúng là đủ.
 */
export type PalmGeometryStatus = "unreviewed" | "confirmed" | "edited" | "added" | "absent";

export type PalmPoint = [number, number];

/**
 * Nét thứ hai, thứ ba... của CÙNG một loại đường (vd hai đường hôn nhân song
 * song). Không có `status` riêng: nó chỉ tồn tại vì người xem chủ động vẽ, nên
 * luôn ngầm định là "tự vẽ thêm" — không có bản model để so mà suy confirmed/edited.
 */
export type PalmExtraStroke = {
  points: PalmPoint[];
  width_px?: number;
  length_px?: number;
  bbox?: number[] | null;
};

export type PalmGeometryEntry = {
  key: string;
  label: string;
  kind: "polyline" | "polygons";
  status: PalmGeometryStatus;
  confidence: number;
  source: "model" | "user";
  points?: PalmPoint[];
  polygons?: PalmPoint[][];
  width_px?: number;
  length_px?: number;
  bbox?: number[] | null;
  /** Chỉ có ở lớp `polyline`. Rỗng/thiếu = tay này chỉ có một nét của đường đó. */
  extra?: PalmExtraStroke[];
};

/**
 * Một hoa văn (vd một ngôi sao, một cù lao) người xem tự TÔ bằng cọ trên ảnh.
 * Không có `status`: không model nào gieo mầm được các hoa văn ghim được, nên
 * một ghim tồn tại nghĩa là người xem đã tự tay đặt nó — khác hẳn `classes`
 * (đường/vùng). Là một nét cọ (`points` + `width_px` người xem tự chỉnh cỡ),
 * không phải một điểm đơn — khoanh đúng phạm vi hoa văn thay vì chấm một chỗ.
 */
export type PalmPatternMark = {
  key: string;
  label: string;
  points: PalmPoint[];
  width_px?: number;
  note?: string;
};

/** Nét của MỘT tấm ảnh. Toạ độ theo pixel của chính ảnh đã chuẩn hoá đó. */
export type PalmImageGeometry = {
  person_index: number;
  slot: PalmSlot;
  hand: PalmHand;
  view: "palm" | "back";
  width: number;
  height: number;
  model?: string | null;
  /** Chiều đầu ngón đo trên mask. "unknown" = BE không nói mốc tuổi từ nét. */
  fingers_dir?: "up" | "down" | "left" | "right" | "unknown";
  palm_box?: number[] | null;
  oriented?: boolean;
  reviewed_count: number;
  classes: Record<string, PalmGeometryEntry>;
  patterns?: PalmPatternMark[];
};

export type PalmEditableClass = {
  key: string;
  label: string;
  kind: "polyline" | "polygons";
  /** false = model chưa được dạy đường này, chỉ người xem tự vẽ được. */
  seeded_by_model: boolean;
  primary: boolean;
  /** true = cho vẽ thêm nét thứ hai/ba của cùng đường (mọi lớp `polyline`). */
  multi: boolean;
  hint: string;
};

/** Một trong 14 hoa văn cho FE dựng bảng chọn khi ghim — sinh từ `PATTERN_SPECS`. */
export type PalmPatternClass = {
  key: string;
  label: string;
  hint: string;
};

export type PalmScanReviewConfig = {
  enabled: boolean;
  /** `0` = nháp không hết hạn (mặc định từ 14/09/2026). Không còn UI nào đọc số này. */
  draft_ttl_hours: number;
  draft_expires?: boolean;
  notice: string;
};

export type PalmSegmentationConfig = {
  enabled: boolean;
  model?: string | null;
  classes: PalmEditableClass[];
  statuses: PalmGeometryStatus[];
  draft_ttl_hours: number;
  draft_expires?: boolean;
  notice: string;
  patterns: PalmPatternClass[];
  pattern_notice: string;
};

export type PalmConsentDocument = {
  version: string;
  title: string;
  summary: string[];
  body_md: string;
  /**
   * `true` = phải đồng ý mới dùng được bói chỉ tay (v91) → dựng màn chặn ngay khi
   * vào trang. BE cũ chỉ có `optional`; đọc `required ?? !optional` để chạy với cả hai.
   */
  required?: boolean;
  optional: boolean;
  default: boolean;
  decline_note?: string;
};

export type PalmTrainingConsent = {
  granted: boolean;
  granted_at?: string | null;
  version?: string | null;
};

export type PalmDraft = {
  draft_id: string;
  status: "draft";
  expires_at: string | null;
  price: number;
  persons: PalmPersonInput[];
  question: string | null;
  hand_roles: PalmHandRoles[];
  images: Omit<PalmImageMeta, "key">[];
  geometry: PalmImageGeometry[];
  segmentation: { model?: string | null; seeded_at?: string | null; available: boolean };
  classes: PalmEditableClass[];
  patterns: PalmPatternClass[];
  tuvi_charts: TuviChart[];
  bazi_charts: BaziChart[];
  hexagram: HexagramResult | null;
  training_consent: PalmTrainingConsent | null;
  consent_document: PalmConsentDocument;
  /** Bảng 16 tiêu chí — `done: false` khi chưa chạy bước quét (nháp vừa tạo). */
  scan?: { done: boolean; reviewed: boolean; persons: PalmPersonReport[] };
  /**
   * Việc nền đang chạy trên chính bản nháp này. BE cũ chưa có v92 thì thiếu trường
   * này — coi như `idle`, luồng chạy y như trước.
   *
   * Có nó ở đây thì người xem đóng tab giữa chừng rồi mở lại vẫn thấy "đang luận,
   * còn chừng này nữa" thay vì một nút bấm mời họ trả tiền lần nữa.
   */
  job?: Pick<PalmJob, "stage" | "state"> & Partial<PalmJob>;
};

export type PalmDraftListItem = {
  id: string;
  persons_count: number;
  names: (string | null)[];
  question: string | null;
  expires_at: string | null;
  created_at: string;
};

// ── Bảng quét 16 tiêu chí ────────────────────────────────────────────────────
//
// Tầng quét chạy MỘT call cho TẤT CẢ ảnh của một người (trước đây một call mỗi
// ảnh), trả về báo cáo Markdown 16 mục do BE tách thành bảng sửa được.
//
// Vì sao có bước này: máy chỉ nhìn được một tấm ảnh, còn người xem đang cầm chính
// bàn tay ấy. Dòng nào ảnh không cho thấy thì máy PHẢI ghi "[Không quan sát rõ...]"
// thay vì đoán — và đó chính là danh sách `missing` để người xem điền nốt.
//
// Luồng đầy đủ:
//   createPalmDraft → savePalmGeometry (duyệt nét, miễn phí)
//   → scanPalmDraft (TRỪ TIỀN) → savePalmScan (sửa bảng, miễn phí, nhiều lần)
//   → startPalmDraftReading (luận, KHÔNG trừ thêm)

/**
 * Trạng thái một dòng trong bảng. BE tự suy từ bản model đọc ra, **không nhận
 * nhãn client gửi** — nên gửi lại đúng chữ cũ thì dòng vẫn là `scanned`.
 * - `scanned` máy đọc được · `missing` ảnh không cho thấy, cần người xem bổ sung
 * - `filled` người xem bổ sung · `edited` người xem sửa · `added` người xem thêm mới
 */
/**
 * Trạng thái một dấu hiệu sau bước soát. BE tự suy từ bản model đọc ra
 * (`predicted`, bất biến) và **không nhận nhãn client gửi** — gửi lại đúng chữ cũ
 * thì dấu hiệu vẫn là `scanned`.
 * - `scanned` máy đọc được · `edited` người xem sửa mô tả
 * - `added` người xem tự thêm (hoặc khai vân/móng) · `removed` người xem khẳng định KHÔNG có
 */
export type PalmSignalStatus = "scanned" | "edited" | "added" | "removed";

/** Ai đưa dấu hiệu này vào. Trục ĐỘC LẬP với `trang_thai`, đừng gộp hai thứ. */
export type PalmSignalSource = "ai" | "khai_bao" | "nguoi_xem";

export type PalmSignal = {
  /** Khoá duy nhất theo dấu hiệu + khung ảnh; BE mới trả trường này khi tách hai tay. */
  key?: string;
  /** Mã lá trong cây dấu hiệu — dùng để gửi phần sửa về đúng dòng. */
  id: string;
  /** Nhãn kèm cấp cha ("Gò Kim Tinh — Đầy đặn, cao"): nhãn lá trần mất chỗ bám. */
  label: string;
  /** Model/người xem thấy gì trên tay này. */
  quan_sat: string;
  /** Khung ảnh làm bằng chứng ("" nếu do khai báo). */
  anh: string;
  /** Tay chứa dấu hiệu. Client cũ có thể chưa nhận trường này nên vẫn suy từ `anh`. */
  tay?: "trai" | "phai" | "";
  nguon: PalmSignalSource;
  trang_thai: PalmSignalStatus;
};

export type PalmSignalGroup = {
  /** Mã nhóm gốc trong cây ("go", "sinh_menh"…). */
  group: string;
  label: string;
  rows: PalmSignal[];
};

/** Mô tả ngoài cây — khí sắc, nước da, độ dày mỏng. MỖI TAY MỘT BẢN. */
export type PalmHandOverview = {
  tay: "trai" | "phai";
  mo_ta: string;
};

export type PalmPersonReport = {
  person_index: number;
  name: string;
  /** Tay THẬT SỰ có ảnh trong lượt này — 1 hoặc 2. */
  hands: ("trai" | "phai")[];
  tong_quan: PalmHandOverview[];
  /**
   * Điểm model tự thấy không chắc. BE vẫn trả, nhưng FE **không hiển thị nữa** — khối
   * "Chi tiết ảnh chưa thể hiện rõ" đã bỏ khỏi bảng dấu hiệu: nó chỉ kể ra những gì
   * máy KHÔNG đọc được, không giúp người xem soát gì thêm.
   */
  chua_ro: string[];
  groups: PalmSignalGroup[];
  /**
   * Nhóm đã xét mà không có dấu hiệu nào. Khối "có thể bạn muốn bổ sung" dựng từ
   * đây — thay chỗ của `missing` ở bảng 16 tiêu chí cũ.
   */
  empty_groups: { group: string; label: string; ly_do?: string }[];
  stats: Record<PalmSignalStatus | "total", number>;
  /** Khai báo thủ công hiện có, để điền sẵn ô chọn. `null` = chưa khai. */
  van_dau_ngon?: PalmFingerprintSelection | null;
  dac_diem_mong?: PalmNailSelection | null;
};

/** Kết quả `POST/PUT /drafts/{id}/scan`. */
export type PalmScanResult = {
  draft_id: string;
  status: "scanned";
  expires_at: string | null;
  question: string | null;
  persons: PalmPersonReport[];
  /** Người xem đã bấm lưu bảng ít nhất một lần chưa. */
  reviewed: boolean;
  model: string;
  signal_sources: { key: PalmSignalSource; label: string }[];
  signal_statuses: { key: PalmSignalStatus; label: string }[];
  notice: string;
  price_charged: number;
  balance?: number | null;
};

// ─── Việc nền: quét & luận chạy ngầm, FE hỏi thăm tiến độ ────────────────────
//
// Hai bước này mất 4–5 phút. Giữ chúng trong một request đồng bộ là chắc chắn đứt:
// Cloudflare cắt ở ~100s (lỗi 524), nginx mặc định 60s. BE vẫn chạy tới cùng nên
// hậu quả là TIỀN ĐÃ TRỪ mà người xem không bao giờ thấy kết quả — không có mức
// timeout nào ở FE sửa được chuyện đó.

export type PalmJobStage = "" | "scan" | "reading";
export type PalmJobState = "idle" | "running" | "done" | "failed";

export type PalmJob = {
  draft_id: string;
  /** Việc nào đang chạy (hoặc vừa xong). */
  stage: PalmJobStage;
  state: PalmJobState;
  progress: {
    done: number;
    total: number;
    /** BE tính sẵn để FE khỏi tự chia và tự xử ca `total = 0`. */
    percent: number;
    /** Vd "Dat — Các đường chỉ tay". Rỗng thì đừng hiện dòng nào. */
    label: string;
  };
  error: string;
  elapsed_seconds: number;
  /** Việc treo quá lâu = tiến trình chạy nó đã chết. Mời người xem bấm lại. */
  stale: boolean;
  price_charged: number;
  session_uuid: string | null;
  balance?: number | null;
};

/**
 * `POST /drafts/{id}/scan` trả về MỘT TRONG HAI: bảng đã quét sẵn (gọi lại lượt đã
 * quét xong) hoặc phiếu nhận việc. Phân biệt bằng `state` — `PalmScanResult` không
 * có trường ấy.
 */
export function isPalmJob(res: PalmScanResult | PalmJob): res is PalmJob {
  return typeof (res as PalmJob).state === "string";
}

/** Hỏi thăm việc nền. Gọi vài giây một lần trong lúc chờ; payload cố ý mỏng. */
export async function getPalmJob(draftId: string): Promise<PalmJob> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/drafts/${draftId}/job`, {
    headers: authHeaders(),
  });
  return unwrap<PalmJob>(res);
}

/** Một mẩu tri thức tướng tay hiện lúc chờ — rút từ chính hai cuốn sách của cây. */
export type PalmTip = {
  id: string;
  title: string;
  /** Nhóm gốc ("Đường Trí Tuệ", "Các gò trên bàn tay"). */
  group: string;
  text: string;
  /** `true` = dấu hiệu này CÓ trên tay người xem, không phải tri thức chung. */
  found: boolean;
  /** Tên người mang dấu hiệu (lượt nhiều người). Rỗng với tips chung. */
  subject: string;
};

/**
 * Tips cho màn chờ. Truyền `draftId` của lượt đã quét xong thì tips lấy theo ĐÚNG
 * dấu hiệu tìm thấy trên tay họ — lúc chờ luận, đó là phần mở màn của chính bài sắp
 * hiện ra chứ không còn là chữ lấp chỗ trống.
 *
 * Lỗi thì trả rỗng: không có tri thức thì màn chờ vẫn là màn chờ chạy được, còn ném
 * lỗi ở đây là làm gãy đúng cái màn đang phục vụ một lượt ĐÃ TRỪ TIỀN.
 */
export async function getPalmTips(draftId = "", count = 0): Promise<PalmTip[]> {
  try {
    const params = new URLSearchParams();
    if (draftId) params.set("draft_id", draftId);
    if (count) params.set("count", String(count));
    const res = await fetch(`${BOIDE_API_URL}/v1/palm/tips?${params.toString()}`, {
      headers: authHeaders(),
    });
    const data = await unwrap<{ tips?: PalmTip[] }>(res);
    return data.tips ?? [];
  } catch {
    return [];
  }
}

/** Vá từng phần, KHÔNG gửi lại cả bảng (một bàn tay có thể tới 150 dấu hiệu). */
export type PalmScanUpdateInput = {
  persons: {
    person_index: number;
    /** Sửa mô tả một dấu hiệu máy đã thấy. */
    edits?: { id: string; quan_sat?: string }[];
    /** Dấu hiệu người xem tự thêm qua ô tìm kiếm. */
    added?: { id: string; quan_sat?: string }[];
    /** Dấu hiệu người xem khẳng định KHÔNG có. */
    removed_ids?: string[];
    /** Khai ở bước soát bảng, không phải lúc tạo nháp. Bỏ qua = không đổi. */
    van_dau_ngon?: PalmFingerprintSelection;
    dac_diem_mong?: PalmNailSelection;
  }[];
};

/** Payload gửi lên khi lưu nét — chỉ cần gửi ảnh nào có thay đổi. */
export type PalmGeometryUpdateInput = {
  images: {
    person_index: number;
    slot: PalmSlot;
    classes: Record<
      string,
      {
        status: PalmGeometryStatus;
        points?: PalmPoint[] | null;
        polygons?: PalmPoint[][] | null;
        width_px?: number | null;
        extra?: { points?: PalmPoint[] | null; width_px?: number | null }[] | null;
      }
    >;
    patterns?: {
      key: string;
      points?: PalmPoint[] | null;
      width_px?: number | null;
      note?: string | null;
    }[];
  }[];
  share_for_training?: boolean;
  consent_version?: string;
};

export async function getPalmConfig(): Promise<PalmConfig> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/config`);
  return unwrap<PalmConfig>(res);
}

/**
 * Bước 1 — upload ảnh, model vẽ nét. MIỄN PHÍ, chưa gọi LLM nào.
 * Multipart giống `startPalmReading`.
 */
export async function createPalmDraft(
  input: PalmStartInput,
  images: Map<string, File>,
): Promise<PalmDraft> {
  const form = new FormData();
  form.append("payload", JSON.stringify(input));
  for (const [field, file] of images) form.append(field, file, file.name || `${field}.jpg`);

  const res = await fetch(`${BOIDE_API_URL}/v1/palm/drafts`, {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });
  return unwrap<PalmDraft>(res);
}

export async function getPalmDraft(draftId: string): Promise<PalmDraft> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/drafts/${draftId}`, {
    headers: authHeaders(),
  });
  return unwrap<PalmDraft>(res);
}

export async function listPalmDrafts(): Promise<PalmDraftListItem[]> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/drafts`, { headers: authHeaders() });
  return unwrap<PalmDraftListItem[]>(res);
}

/** Bước 2 — lưu nét đã sửa. Vẫn miễn phí, gọi lại bao nhiêu lần cũng được. */
export async function savePalmGeometry(
  draftId: string,
  input: PalmGeometryUpdateInput,
): Promise<{
  draft_id: string;
  geometry: PalmImageGeometry[];
  reviewed_classes: number;
  expires_at: string | null;
  training_consent: PalmTrainingConsent | null;
}> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/drafts/${draftId}/geometry`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(input),
  });
  return unwrap(res);
}

/**
 * Bước 3 — nhận việc quét. **ĐÂY là chỗ trừ tiền.**
 *
 * Trả về NGAY (việc quét chạy nền, 60–180s): hoặc `PalmJob` khi vừa nhận việc,
 * hoặc `PalmScanResult` khi bản nháp đã có bảng rồi. Dùng `isPalmJob()` để phân biệt
 * — gọi lại cho cùng bản nháp KHÔNG trừ thêm lần nào và BE trả lại đúng bảng đang
 * có, kể cả phần người xem đã sửa (quét lại sẽ ghi đè công sửa của họ).
 *
 * Tiền vẫn trừ ĐỒNG BỘ trong call này, có chủ ý: hết số dư là thứ phải báo lúc người
 * xem còn đang nhìn màn hình, không phải một lỗi hiện ra sau bốn phút chờ.
 */
export async function scanPalmDraft(
  draftId: string,
  input: {
    question?: string;
    share_for_training?: boolean;
    consent_version?: string;
  } = {},
): Promise<PalmScanResult | PalmJob> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/drafts/${draftId}/scan`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(input),
  });
  return unwrap<PalmScanResult | PalmJob>(res);
}

/** Bước 4 — lưu bảng người xem sửa & bổ sung. Miễn phí, gọi lại bao nhiêu lần cũng được. */
export async function savePalmScan(
  draftId: string,
  input: PalmScanUpdateInput,
): Promise<PalmScanResult> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/drafts/${draftId}/scan`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(input),
  });
  return unwrap<PalmScanResult>(res);
}

/** Một mục trong cây dấu hiệu, kèm phần luận nghĩa để người xem biết mình đang thêm gì. */
export type PalmTaxonomyHit = {
  id: string;
  label: string;
  /** Đường dẫn đầy đủ ("Các gò trên bàn tay > Gò Kim Tinh > Đầy đặn…"). */
  path: string;
  text: string;
};

/**
 * Tìm dấu hiệu trong cây tướng tay — ô "thêm mục máy bỏ sót" ở bước soát bảng.
 *
 * Tìm trên BE chứ không tải cả cây xuống: 54k ký tự nhãn đẩy về máy người xem mỗi
 * lần mở bảng là vô ích, họ chỉ cần đúng vài dòng đang tìm. Lỗi thì trả rỗng —
 * không tìm được một dấu hiệu không được làm gãy cả màn soát.
 */
export async function searchPalmTaxonomy(query: string): Promise<PalmTaxonomyHit[]> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/taxonomy?q=${encodeURIComponent(query)}`, {
    headers: authHeaders(),
  });
  const data = await unwrap<{ mode: string; results?: PalmTaxonomyHit[] }>(res);
  return data.results ?? [];
}

/**
 * Thông tin sinh khai ở BƯỚC CUỐI (sau khi bàn tay đã đọc xong).
 *
 * Chỉ mang phần sinh: tay đã chụp và kết quả chắp tay không được đổi ở bước này vì
 * bản quét đã lập theo chúng. Bỏ trống cả `nam`/`thang`/`ngay` = chỉ xem tay, khai
 * nửa vời mới là lỗi; `gio` bỏ trống vẫn hợp lệ ("không biết giờ").
 */
export type PalmPersonBirthInput = {
  person_index: number;
  ho_ten?: string;
  nam?: number;
  thang?: number;
  ngay?: number;
  gio?: number;
  phut?: number;
  gioi_tinh?: "nam" | "nu";
  is_lunar?: boolean;
  /** Sinh trong tháng âm NHUẬN — chỉ có nghĩa khi is_lunar = true. */
  is_leap_month?: boolean;
  use_profile?: boolean;
};

/**
 * Bước 5 — nhận việc luận. KHÔNG trừ thêm nếu bước quét đã trừ.
 *
 * Trả về NGAY một `PalmJob`; bài luận chạy nền (120–240s), theo dõi qua
 * `getPalmJob`. Xong thì lấy bài bằng `getPalmReading(draftId)` — id bài luận CHÍNH
 * LÀ id bản nháp.
 *
 * Đây là chỗ hỏi ba thứ không cần cho việc đọc bàn tay: câu hỏi, thông tin sinh
 * (tuỳ chọn → lá số Tử Vi/Bát Tự để đối chiếu) và gieo quẻ (tuỳ chọn). Lỗi khai
 * thiếu vẫn nổ ngay trong call này chứ không đợi tới lúc chạy xong.
 */
export async function startPalmDraftReading(
  draftId: string,
  input: {
    question?: string;
    include_hexagram?: boolean;
    diviner_id?: string;
    persons?: PalmPersonBirthInput[];
    share_for_training?: boolean;
    consent_version?: string;
  } = {},
): Promise<PalmJob> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/drafts/${draftId}/reading`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(input),
  });
  return unwrap<PalmJob>(res);
}

export async function deletePalmDraft(draftId: string): Promise<void> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/drafts/${draftId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  await unwrap(res);
}

export type PalmImageIssue = {
  code: string;
  /** `error` = nên chụp lại; `warn` = vẫn dùng được nhưng nên xem lại. */
  severity: "error" | "warn";
  message: string;
};

export type PalmImageCheck = {
  slot: PalmSlot;
  verdict: "good" | "warn" | "reject";
  issues: PalmImageIssue[];
  hand_found: boolean;
  coverage: number;
  confidence: number;
  /** Tay/mặt model đọc ra từ ảnh; `null` khi tắt cờ hoặc call lỗi. */
  detected: {
    hand: "left" | "right" | null;
    view: "palm" | "back" | null;
    matches: boolean | null;
  } | null;
  cropped: boolean;
  width: number;
  height: number;
  /** Data URL ảnh ĐÃ cắt — dùng thay tấm đang giữ. `null` khi BE không xử lý được. */
  image: string | null;
};

/**
 * Chấm một ảnh bàn tay ngay lúc chụp + nhận lại bản đã cắt. MIỄN PHÍ, không lưu gì.
 *
 * Không bao giờ chặn: `verdict` chỉ để hiện lời nhắc. Gọi thất bại thì bỏ qua — mất
 * một lời nhắc còn hơn chặn người ta chụp tiếp.
 */
export async function checkPalmImage(file: File, slot: PalmSlot): Promise<PalmImageCheck> {
  const body = new FormData();
  body.append("file", file);
  body.append("slot", slot);
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/check-image?slot=${slot}`, {
    method: "POST",
    // KHÔNG set Content-Type: trình duyệt phải tự thêm boundary của multipart.
    headers: authHeaders(),
    body,
  });
  return unwrap<PalmImageCheck>(res);
}

/** Data URL → File, để thay tấm ảnh người xem đang giữ bằng bản đã cắt. */
export async function dataUrlToFile(dataUrl: string, name: string): Promise<File> {
  const blob = await (await fetch(dataUrl)).blob();
  const ext = blob.type === "image/webp" ? "webp" : "jpg";
  return new File([blob], `${name}.${ext}`, { type: blob.type });
}

/** Ảnh của bản nháp (cùng cơ chế token với ảnh bài luận → phải fetch ra blob). */
export async function fetchPalmDraftImage(
  draftId: string,
  personIndex: number,
  slot: PalmSlot,
): Promise<Blob> {
  const res = await fetch(
    `${BOIDE_API_URL}/v1/palm/drafts/${draftId}/images/${personIndex}/${slot}`,
    { headers: authHeaders() },
  );
  if (!res.ok) throw new Error("Không tải được ảnh bàn tay");
  return res.blob();
}

export async function getPalmTrainingConsent(): Promise<{
  consent: PalmTrainingConsent;
  document: PalmConsentDocument;
  donated: { images?: number; reviewed_classes?: number; last_donated_at?: string | null };
  needs_reconsent: boolean;
  /** Đã đồng ý ĐÚNG bản chính sách đang phát hành. */
  satisfied?: boolean;
  /** Chưa đồng ý mà tính năng đang bắt buộc → chặn ở cửa vào trang. */
  blocked?: boolean;
  /** Số bàn tay đang lưu — rút đồng ý là mất chừng này. */
  saved_hands?: number;
}> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/training-consent`, {
    headers: authHeaders(),
  });
  return unwrap(res);
}

export async function setPalmTrainingConsent(input: {
  granted: boolean;
  consent_version?: string;
  purge_existing?: boolean;
}): Promise<{
  consent: PalmTrainingConsent;
  purged_images: number;
  /** Số bàn tay đã lưu bị xoá kèm khi rút đồng ý. */
  purged_hands?: number;
  donated: { images?: number; reviewed_classes?: number };
  blocked?: boolean;
}> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/training-consent`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(input),
  });
  return unwrap(res);
}

// ─── Bàn tay đã lưu: hỏi thẻ chủ đề khác không phải chụp lại ─────────────────

/** Một bàn tay đã lưu. `persons[].slots` = khung ảnh hồ sơ ĐANG CÓ. */
export type PalmProfile = {
  profile_id: string;
  name: string;
  persons: {
    person_index: number;
    ho_ten: string;
    hand?: PalmHand | null;
    has_birth: boolean;
    slots: PalmSlot[];
  }[];
  persons_count: number;
  images: { person_index: number; slot: PalmSlot; label: string; width: number; height: number }[];
  scanned: boolean;
  reviewed: boolean;
  source_reading_id?: string | null;
  last_used_at?: string | null;
  created_at?: string | null;
};

export async function listPalmProfiles(): Promise<{
  profiles: PalmProfile[];
  consent_required: boolean;
}> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/profiles`, { headers: authHeaders() });
  return unwrap(res);
}

export async function renamePalmProfile(profileId: string, name: string): Promise<PalmProfile> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/profiles/${profileId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ name }),
  });
  return unwrap(res);
}

export async function deletePalmProfile(profileId: string): Promise<{ profile_id: string }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/profiles/${profileId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return unwrap(res);
}

export async function getPalmProfileImage(
  profileId: string,
  personIndex: number,
  slot: PalmSlot,
): Promise<Blob> {
  const res = await fetch(
    `${BOIDE_API_URL}/v1/palm/profiles/${profileId}/images/${personIndex}/${slot}`,
    { headers: authHeaders() },
  );
  if (!res.ok) throw new Error("Không tải được ảnh bàn tay");
  return res.blob();
}

/** Khung ảnh thẻ mới cần mà hồ sơ chưa có — BE trả kèm lỗi `missing_images`. */
export type PalmMissingSlots = {
  person_index: number;
  slots: { key: PalmSlot; label: string; hint: string; field: string }[];
}[];

/**
 * Hỏi một thẻ chủ đề KHÁC trên bàn tay đã lưu.
 *
 * Ảnh là TUỲ CHỌN: chỉ gửi khung thẻ mới cần mà hồ sơ chưa có. Thiếu thì BE trả
 * lỗi kèm `missing_slots` — bắt lỗi đó để mở đúng chừng ấy ô chụp thay vì bắt
 * người xem chụp lại cả bộ.
 */
export async function createPalmProfileReading(
  profileId: string,
  payload: {
    topic_slug?: string;
    question?: string;
    include_hexagram?: boolean;
    diviner_id?: string;
    hands?: (PalmHand | null)[];
  },
  images?: Map<string, File>,
): Promise<PalmReading & { profile_id: string }> {
  const form = new FormData();
  form.append("payload", JSON.stringify(payload));
  for (const [field, file] of images ?? []) form.append(field, file);
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/profiles/${profileId}/readings`, {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });
  return unwrap(res);
}

/**
 * Bói chỉ tay — call dài (upload ảnh + lập lá số + AI luận ảnh, 60–180s).
 *
 * Multipart: field `payload` là chuỗi JSON, mỗi ảnh là field `p{i}_{slot}`.
 * KHÔNG set Content-Type để trình duyệt tự sinh boundary.
 */
export async function startPalmReading(
  input: PalmStartInput,
  images: Map<string, File>,
): Promise<PalmReading> {
  const form = new FormData();
  form.append("payload", JSON.stringify(input));
  for (const [field, file] of images) form.append(field, file, file.name || `${field}.jpg`);

  const res = await fetch(`${BOIDE_API_URL}/v1/palm/start`, {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });
  return unwrap<PalmReading>(res);
}

export function palmImageField(personIndex: number, slot: PalmSlot): string {
  return `p${personIndex}_${slot}`;
}

/**
 * Thẻ chủ đề hub ĐANG BÁN. BE vẫn trả đủ thẻ chuyên đề, nhưng hub chỉ bán thẻ toàn
 * diện — lọc ở ĐÂY, một chỗ duy nhất, để hub chụp mới và luồng hỏi lại trên bàn tay
 * đã lưu không lệch nhau, và để thẻ chọn sẵn (`topics[0]`) không rơi vào một chuyên
 * đề đang bị ẩn (người xem sẽ mua thẻ họ không hề thấy).
 *
 * Trả `[]` khi BE chưa chạy migration v89 — không phải lỗi, luồng cũ (4 ảnh, 9 mục,
 * giá env) vẫn chạy.
 */
const HUB_PALM_TOPIC_SLUGS = new Set(["toan-dien"]);

export async function listPalmTopics(): Promise<PalmTopic[]> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/topics`);
  const data = await unwrap<{ topics: PalmTopic[] }>(res);
  return (data?.topics ?? []).filter((topic) => HUB_PALM_TOPIC_SLUGS.has(topic.slug));
}

export type PalmReadingPage = {
  items: PalmReadingListItem[];
  total: number;
  page: number;
  limit: number;
};

/** Mỗi trang bao nhiêu bài. Trùng mặc định của BE — đổi ở đây là đổi cả hai. */
export const PALM_READINGS_PAGE_SIZE = 10;

/**
 * Một trang lịch sử luận giải.
 *
 * ⚠️ **Chịu được CẢ HAI khuôn trả về.** BE cũ trả một mảng trần (trần cứng 50 bài),
 * BE mới trả `{items, total, page, limit}`. Nhờ vậy thứ tự deploy nào cũng an toàn —
 * đừng bỏ nhánh chịu-mảng-cũ trước khi BE đã lên bản mới ở mọi môi trường.
 */
export async function listPalmReadings(
  page = 1,
  limit = PALM_READINGS_PAGE_SIZE,
): Promise<PalmReadingPage> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/readings?page=${page}&limit=${limit}`, {
    headers: authHeaders(),
  });
  const data = await unwrap<PalmReadingListItem[] | PalmReadingPage>(res);
  if (Array.isArray(data)) {
    // BE cũ: không phân trang, nên chính mảng ấy là trọn danh sách nó chịu trả.
    return { items: data, total: data.length, page: 1, limit: data.length || limit };
  }
  return {
    items: data?.items ?? [],
    total: Number(data?.total ?? 0),
    page: Number(data?.page ?? page),
    limit: Number(data?.limit ?? limit),
  };
}

export async function getPalmReading(readingId: string): Promise<PalmReading> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/readings/${readingId}`, {
    headers: authHeaders(),
  });
  return unwrap<PalmReading>(res);
}

export async function deletePalmReading(readingId: string): Promise<void> {
  const res = await fetch(`${BOIDE_API_URL}/v1/palm/readings/${readingId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  await unwrap(res);
}

/**
 * Tải một ảnh bàn tay đã chuẩn hoá (endpoint yêu cầu Bearer token nên
 * `<img src>` trực tiếp không dùng được — phải fetch ra blob rồi createObjectURL).
 */
export async function fetchPalmImage(
  readingId: string,
  personIndex: number,
  slot: PalmSlot,
): Promise<Blob> {
  const res = await fetch(
    `${BOIDE_API_URL}/v1/palm/readings/${readingId}/images/${personIndex}/${slot}`,
    { headers: authHeaders() },
  );
  if (!res.ok) throw new Error("Không tải được ảnh bàn tay");
  return res.blob();
}

// ============================================================
// Bói Live — phiên bói bằng giọng nói realtime (/v1/live)
// ============================================================

export type LiveDiviner = Diviner & { voice_name?: string };

export type LiveSpeechRate = "slow" | "normal" | "fast";

export type LiveConfig = {
  enabled: boolean;
  price_per_second: number;
  price_per_minute: number;
  bill_interval_seconds: number;
  min_start_seconds: number;
  max_minutes: number;
  balance: number;
  affordable_seconds: number;
  diviners: LiveDiviner[];
};

export type LiveTranscriptSegment = {
  role: "user" | "diviner";
  text: string;
  t: number; // giây tính từ đầu phiên
  order?: number; // thứ tự event chính xác trong phiên live; lịch sử cũ có thể không có
  source?: "voice" | "text"; // voice = phụ đề ASR (có thể sai), text = user gõ tay; thiếu = voice
};

export type LiveToolName = "gieo_que_kinh_dich" | "xem_la_so_tu_vi" | "xem_la_so_bat_tu";

export type LiveToolResult = {
  name: LiveToolName | string;
  args: Record<string, unknown>;
  ok: boolean;
  raw_data: unknown; // HexagramResult | TuviChart(-id/created_at) | BaziChart(-id/created_at)
  error?: string | null;
  t: number;
  order?: number;
};

export type LiveSessionSummary = {
  id: number;
  session_uuid: string;
  diviner_id: string;
  status: string;
  end_reason?: string | null;
  started_at?: string | null;
  duration_seconds: number;
  seconds_charged: number;
  amount_charged: number;
};

export type LiveSessionDetail = LiveSessionSummary & {
  ended_at?: string | null;
  transcript: LiveTranscriptSegment[];
  tool_results: LiveToolResult[];
  // Ghi chú buổi xem cho user (v52) — sinh nền sau khi phiên kết thúc, có thể null
  note?: string | null;
  // Đánh giá cả phiên (1..5) + nhận xét — null khi user chưa chấm
  rating?: number | null;
  rating_comment?: string | null;
  rating_comment_rewarded_at?: string | null;
};

export async function getLiveConfig(): Promise<LiveConfig> {
  const res = await fetch(`${BOIDE_API_URL}/v1/live/config`, { headers: authHeaders() });
  return unwrap<LiveConfig>(res);
}

/** Mỗi trang bao nhiêu phiên live. Trùng mặc định của BE — đổi ở đây là đổi cả hai. */
export const LIVE_SESSIONS_PAGE_SIZE = 10;

export async function listLiveSessions(
  page = 1,
  pageSize = LIVE_SESSIONS_PAGE_SIZE,
): Promise<{ items: LiveSessionSummary[]; total: number; page: number; page_size: number }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/live/sessions?page=${page}&page_size=${pageSize}`, {
    headers: authHeaders(),
  });
  return unwrap(res);
}

export async function getLiveSession(sessionId: number): Promise<LiveSessionDetail> {
  const res = await fetch(`${BOIDE_API_URL}/v1/live/sessions/${sessionId}`, {
    headers: authHeaders(),
  });
  return unwrap<LiveSessionDetail>(res);
}

export async function deleteLiveSession(sessionId: number): Promise<{ message: string }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/live/sessions/${sessionId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return unwrap<{ message: string }>(res);
}

/** Chấm sao (1..5) cho CẢ phiên Bói Live; rating=null để bỏ chấm. */
export async function rateLiveSession(
  sessionId: number,
  rating: number | null,
  ratingComment?: string | null,
): Promise<{
  session_id: string;
  rating: number | null;
  rating_comment?: string | null;
  rating_comment_rewarded_at?: string | null;
  rewarded: boolean;
  reward_amount: number;
  current_amount?: number | null;
}> {
  const body: { rating: number | null; rating_comment?: string | null } = { rating };
  if (ratingComment !== undefined) body.rating_comment = ratingComment;
  const res = await fetch(`${BOIDE_API_URL}/v1/live/sessions/${sessionId}/rating`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  return unwrap(res);
}

/** URL WebSocket phiên live — token qua query param (browser không set được header cho WS). */
export function liveWsUrl(divinerId: string, speechRate: LiveSpeechRate = "slow"): string {
  const base = BOIDE_API_URL || (typeof window !== "undefined" ? window.location.origin : "");
  const wsBase = base.replace(/^http/, "ws");
  const token = encodeURIComponent(getBoideToken());
  return `${wsBase}/v1/live/ws?token=${token}&diviner_id=${encodeURIComponent(divinerId)}&speech_rate=${encodeURIComponent(speechRate)}`;
}

// ============================================================
// Chia sẻ cảm nhận nhận thưởng — user đăng bài về Bói Đê lên MXH,
// gửi link để admin duyệt và thưởng qua Inbox reward (/v1/social-reviews)
// ============================================================

export type SocialReviewPlatform = "facebook" | "tiktok" | "instagram" | "threads" | "other";

export type SocialReviewStatus = "pending" | "approved" | "rejected";

export type SocialReviewProgram = {
  reward_amount: number;
  is_active: boolean;
  criteria_note?: string | null;
};

export type SocialReviewSubmission = {
  id: number;
  platform: SocialReviewPlatform;
  post_url: string;
  note?: string | null;
  status: SocialReviewStatus;
  reward_amount?: number | null;
  admin_note?: string | null;
  created_at: string;
  reviewed_at?: string | null;
};

export async function getSocialReviewProgram(): Promise<SocialReviewProgram> {
  const res = await fetch(`${BOIDE_API_URL}/v1/social-reviews/program`, {
    headers: authHeaders(),
  });
  return unwrap<SocialReviewProgram>(res);
}

export async function submitSocialReview(payload: {
  platform: SocialReviewPlatform;
  post_url: string;
  note?: string;
}): Promise<{ id: number; status: SocialReviewStatus; message?: string }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/social-reviews`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  return unwrap(res);
}

export async function listMySocialReviews(): Promise<{ items: SocialReviewSubmission[] }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/social-reviews/me`, {
    headers: authHeaders(),
  });
  return unwrap(res);
}

// ============================================================
// Bot chăm sóc khách hàng — public/optional-auth SSE chat
// ============================================================

export type SupportConversationStatus = "none" | "bot" | "human" | "closed";
export type SupportMessageRole = "user" | "bot" | "admin";

export type SupportMessage = {
  id: number;
  role: SupportMessageRole;
  content: string;
  created_at: string;
  attachments?: SupportAttachment[];
};

export type SupportAttachment = {
  name?: string;
  size?: number;
  content_type?: string;
  url: string;
};

export type SupportMessagesResult = {
  conversation_id?: number;
  conversation?: null;
  status: SupportConversationStatus;
  /** Số giây còn lại trước khi bot tự tiếp quản (đang chờ nhân viên); null = không có lịch. */
  bot_takeover_in?: number | null;
  messages: SupportMessage[];
};

export type SupportStreamEvent =
  | { type: "text"; text: string }
  | { type: "human_mode" }
  | { type: "bot_mode" }
  | { type: "notice"; text: string }
  | { type: "error"; message: string }
  | { type: "done" };

const SUPPORT_VISITOR_KEY = "boide_support_visitor_id";

/** Stable anonymous id for support chat. Signed-in requests still include it harmlessly. */
export function getSupportVisitorId(): string {
  if (typeof window === "undefined") return "";
  const stored = localStorage.getItem(SUPPORT_VISITOR_KEY);
  if (stored) return stored;
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  localStorage.setItem(SUPPORT_VISITOR_KEY, id);
  return id;
}

export async function listSupportMessages(input: {
  visitorId: string;
  afterId?: number;
}): Promise<SupportMessagesResult> {
  const query = new URLSearchParams({
    visitor_id: input.visitorId,
    after_id: String(input.afterId ?? 0),
  });
  const res = await fetch(`${BOIDE_API_URL}/v1/support/messages?${query}`, {
    headers: authHeaders(),
  });
  return unwrap<SupportMessagesResult>(res);
}

export async function startNewSupportConversation(
  visitorId: string,
): Promise<{ closed_conversation_id?: number | null; status: "none" }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/support/conversations/new`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ visitor_id: visitorId }),
  });
  return unwrap(res);
}

export async function sendSupportImage(input: {
  image: File;
  caption?: string;
  visitorId: string;
  pageUrl?: string;
}): Promise<{
  conversation_id: number;
  status: "human";
  message: SupportMessage;
}> {
  const form = new FormData();
  form.append("image", input.image);
  form.append("caption", input.caption ?? "");
  form.append("visitor_id", input.visitorId);
  if (input.pageUrl) form.append("page_url", input.pageUrl);
  const res = await fetch(`${BOIDE_API_URL}/v1/support/images`, {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });
  return unwrap(res);
}

export async function fetchSupportAttachment(path: string): Promise<Blob> {
  const url =
    /^https?:\/\//i.test(path) || path.startsWith("blob:") || path.startsWith("data:")
      ? path
      : `${BOIDE_API_URL}${path.startsWith("/") ? path : `/${path}`}`;
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error("Không tải được hình ảnh.");
  return res.blob();
}

/**
 * Read the support endpoint's POST response as SSE. We intentionally parse it
 * here instead of EventSource because the endpoint needs a JSON request body.
 */
export async function streamSupportMessage(input: {
  message: string;
  visitorId: string;
  pageUrl?: string;
  signal?: AbortSignal;
  onEvent: (event: SupportStreamEvent) => void;
}): Promise<void> {
  const res = await fetch(`${BOIDE_API_URL}/v1/support/chat`, {
    method: "POST",
    headers: authHeaders({
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    }),
    body: JSON.stringify({
      message: input.message,
      visitor_id: input.visitorId,
      page_url: input.pageUrl,
    }),
    signal: input.signal,
  });

  if (!res.ok) {
    const detail = await res.text();
    throw new Error(detail || `Không thể kết nối hỗ trợ (HTTP ${res.status})`);
  }
  if (!res.body) throw new Error("Trình duyệt không hỗ trợ phản hồi trực tiếp.");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const dispatch = (block: string) => {
    const lines = block.split(/\r?\n/);
    const firstDataLine = lines.findIndex((line) => line.startsWith("data:"));
    if (firstDataLine < 0) return;
    const payload = lines
      .slice(firstDataLine)
      .map((line, index) => (index === 0 ? line.replace(/^data:\s?/, "") : line))
      .join("\n");
    if (!payload) return;
    if (payload === "[DONE]") input.onEvent({ type: "done" });
    else if (payload === "[HUMAN_MODE]") input.onEvent({ type: "human_mode" });
    else if (payload === "[BOT_MODE]") input.onEvent({ type: "bot_mode" });
    else if (payload.startsWith("[NOTICE]")) {
      input.onEvent({ type: "notice", text: payload.replace(/^\[NOTICE\]\s*/, "") });
    } else if (payload.startsWith("[ERROR]")) {
      input.onEvent({ type: "error", message: payload.replace(/^\[ERROR\]\s*/, "") });
    } else input.onEvent({ type: "text", text: payload });
  };

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() ?? "";
    blocks.forEach(dispatch);
    if (done) break;
  }
  if (buffer.trim()) dispatch(buffer);
}

// ============================================================
// Bói Đê — Vận khí theo kỳ (Hôm nay / Vận tuần / Vận tháng / Xem ngày)
// Miễn phí nhưng CÓ CỬA SỔ: kỳ chỉ mở đúng lúc, đóng sau 24h. Server là
// nguồn sự thật về trạng thái — FE chỉ hiển thị `state` và đếm ngược.
// ============================================================

export type VanKhiKind = "day" | "week" | "month";
/** open = kỳ đang diễn ra (miễn phí) · locked = kỳ sắp tới · closed = kỳ đã qua. Hai loại sau mở bằng số dư. */
export type VanKhiState = "locked" | "open" | "closed";
export type VanKhiMucDo = "cao" | "vua" | "thap";

export type VanKhiWindow = { open_at: string; close_at: string };

export type VanKhiPeriod = {
  kind: VanKhiKind;
  /** day: 2026-07-28 · week: 2026-W31 · month: 2026-L06 (hậu tố N nếu tháng nhuận). */
  key: string;
  label: string;
  sub_label: string;
  start_date: string;
  end_date: string;
  state: VanKhiState;
  /** Chỉ có khi state = open. */
  close_at: string | null;
  /** Mốc mở kế tiếp CỦA KỲ NÀY (null khi đã đóng hẳn). */
  open_at: string | null;
  windows: VanKhiWindow[];
  lunar: {
    lunar_day: number;
    lunar_month: number;
    lunar_year: number;
    is_leap_month: boolean;
  } | null;
  /** Đã từng mở đúng cửa sổ → đọc lại được mãi. */
  has_reading?: boolean;
  can_read?: boolean;
  /** Giá mở kỳ đã qua / sắp tới (đ). 0 khi là kỳ hiện tại hoặc đã mở rồi. */
  price?: number;
  /** true = mở được miễn phí (kỳ hiện tại, hoặc đã mở rồi). */
  is_free?: boolean;
  /** true = kỳ đã qua / sắp tới, trả phí thì mở được. */
  can_purchase?: boolean;
};

export type VanKhiSegment = { label: string; muc_do: VanKhiMucDo; note: string };

export type VanKhiNotableDay = {
  solar_date: string;
  display: string;
  lunar_display: string;
  can_chi_ngay: string;
  truc: string;
  sao: string;
  is_hoang_dao: boolean;
  score: number;
  verdict: string;
};

export type VanKhiNapAm = { name: string; element: string };
export type VanKhiHuong = { hy_than: string; tai_than: string };
/** Quan hệ ngũ hành khí kỳ ↔ bản mệnh: sinh nhập · sinh xuất · khắc nhập · khắc xuất · tỷ hoà. */
export type VanKhiNguHanh = { key: string; term: string; meaning: string; delta: number };
export type VanKhiNgayKy = { key: string; name: string; y_nghia: string; delta: number };
/** Màu + số may mắn theo ngũ hành (BE: `van_khi_almanac.may_man`). */
export type VanKhiMayMan = {
  hanh: string;
  ly_do: string;
  mau_hop: string[];
  mau_noi_bat: string[];
  mau_tranh: string[];
  hanh_tranh?: string;
  so: number[];
};
export type VanKhiTuoiHop = { tam_hop: string[]; luc_hop: string | null; xung: string };
/** Thẻ Tiết khí (BE: `van_khi_almanac.tiet_khi_info`). Mốc giờ là giờ VN, không kèm múi. */
export type VanKhiTietKhi = {
  ten: string;
  slogan: string;
  dac_diem: string;
  kinh_do: number;
  mua: string;
  hanh_mua: string;
  tang_phu_mua: string;
  hanh: string;
  tang_phu: string;
  tu_quy: boolean;
  tu_quy_note: string | null;
  ngay_toi_lap: number;
  thoi_tiet: { bac: string; nam: string };
  nen_lam: string[];
  khong_nen: string[];
  cham_soc: string[];
  nen_an: string[];
  han_che: string[];
  tinh_than: string;
  bat_dau: string;
  ket_thuc: string;
  am_lich: string;
  thang_tiet: { can_chi: string; so: number };
  ngay_thu: number;
  tong_ngay: number;
  con_ngay: number;
  tiet_ke: string;
  ca_nhan?: { menh: string; term: string; loi_khuyen: string };
};

/** Bộ xương tất định BE tính sẵn — FE render badge/ngày, không tự tính lại. */
export type VanKhiSkeleton = {
  kind: VanKhiKind;
  period_key: string;
  label: string;
  sub_label: string;
  start_date: string;
  end_date: string;
  personalized: boolean;
  energy_score: number;
  bucket: string;
  user_element: string | null;
  period_element: string;
  element_relation: string | null;
  /** Bài sinh trước 21/10/2026 không có các khoá dưới. */
  period_element_source?: "ngày" | "tháng";
  nap_am?: VanKhiNapAm | null;
  ngu_hanh?: VanKhiNguHanh | null;
  huong?: VanKhiHuong | null;
  may_man?: VanKhiMayMan | null;
  tiet_khi_info?: VanKhiTietKhi | null;
  giao_tiet?: { ngay: string; ten: string }[];
  /** Kỳ tuần/tháng: hướng lấy theo ngày thuận nhất này. */
  huong_ngay?: string | null;
  direction: string;
  can_chi: { ngay: string; thang: string; nam: string };
  tiet_khi: string;
  lunar_date: string;
  best_days: VanKhiNotableDay[];
  caution_days: VanKhiNotableDay[];
  avg_day_score: number | null;
  day_detail?: VanKhiDayDetail | null;
  blocks?: {
    label: string;
    lunar_from: number;
    lunar_to: number;
    from_date: string;
    to_date: string;
    avg_score: number;
    best_day: string;
  }[];
};

export type VanKhiDayDetail = {
  solar_date: string;
  display: string;
  lunar_display: string;
  can_chi_ngay: string;
  tiet_khi: string;
  truc: { name: string; y_nghia: string; tot_cho: string; xau_cho: string };
  sao: { name: string; is_hoang_dao: boolean; y_nghia: string };
  is_hoang_dao: boolean;
  gio_hoang_dao: { gio_chi: string; khung_gio: string; sao: string }[];
  nap_am?: VanKhiNapAm | null;
  huong?: VanKhiHuong | null;
  ngay_ky?: VanKhiNgayKy[];
  may_man?: VanKhiMayMan | null;
  tuoi_hop?: VanKhiTuoiHop | null;
  tuoi: { chi_tuoi: string; kind: string; delta: number } | null;
  score: number;
  verdict: string;
};

export type VanKhiReading = {
  id: number;
  kind: VanKhiKind;
  period_key: string;
  period_start: string;
  period_end: string;
  energy_score: number;
  bucket: string;
  title: string;
  hashtags: string[];
  reading_md: string;
  nen: string[];
  tranh: string[];
  segments: VanKhiSegment[];
  skeleton: VanKhiSkeleton;
  diviner_id: string;
  reading_model: string;
  opened_at: string | null;
  created_at: string | null;
  /**
   * Điểm/bộ xương ở trên là bản TÍNH LẠI lúc đọc, nhưng lời văn viết trên bảng điểm
   * cũ (cách chấm đổi, hoặc người xem sửa ngày sinh) → mời thầy luận lại miễn phí
   * qua `openVanKhiReading(..., { refresh: true })`. BE cũ không trả khoá này.
   */
  stale?: boolean;
};

export type VanKhiPeriodsResult = {
  kind: VanKhiKind;
  /** Giờ server (VN) — dùng làm mốc đếm ngược thay vì tin đồng hồ máy user. */
  server_time: string;
  periods: VanKhiPeriod[];
  /** Cửa sổ mở gần nhất của loại kỳ này (để đếm ngược "Kỳ tiếp theo mở sau"). */
  next_open_at: string | null;
};

export type VanKhiReadingResult = {
  period: VanKhiPeriod;
  reading: VanKhiReading | null;
  has_reading: boolean;
  /** Mở MIỄN PHÍ được (kỳ đang diễn ra). */
  can_open: boolean;
  /** Kỳ đã qua / sắp tới — trả phí thì mở được. */
  can_purchase: boolean;
  price: number;
  next_open_at: string | null;
};

export type VanKhiOpenResult = {
  period: VanKhiPeriod;
  reading: VanKhiReading;
  /** true = đã mở trước đó, BE trả bài cũ (không tốn thêm call LLM). */
  reused: boolean;
  /** 0 khi là kỳ hiện tại; > 0 khi trả phí mở kỳ đã qua / sắp tới. */
  price_charged: number;
  balance: number | null;
  /** true = bài cũ (`stale`) vừa được thầy luận lại theo điểm hiện tại, miễn phí. */
  rewritten?: boolean;
};

export type VanKhiDayLabel = {
  key: string;
  label: string;
  icon: string;
  hint: string;
  polarity: "good" | "bad";
  weight: number;
  /** Căn cứ thật lấy từ engine (trực/sao/ngũ hành/xung hợp tuổi/ngày kỵ). */
  reasons: string[];
  /** Lời khuyên cụ thể cho ĐÚNG ngày này (kèm giờ hoàng đạo / hướng). BE cũ không có. */
  detail?: string;
};

export type VanKhiDayLabelCategory = {
  key: string;
  label: string;
  good: VanKhiDayLabel[];
  bad: VanKhiDayLabel[];
};

export type VanKhiXemNgayResult = {
  categories_meta: { key: string; label: string }[];
  today: string;
  result: VanKhiDayDetail & {
    date: string;
    day_element: string;
    personalized: boolean;
    categories: VanKhiDayLabelCategory[];
  };
};

export async function listVanKhiPeriods(kind: VanKhiKind): Promise<VanKhiPeriodsResult> {
  const res = await fetch(`${BOIDE_API_URL}/v1/boi-de/van-khi/periods?kind=${kind}`, {
    headers: authHeaders(),
  });
  return unwrap<VanKhiPeriodsResult>(res);
}

/** Đọc một kỳ. `periodKey` rỗng = kỳ đang diễn ra. */
export async function getVanKhiReading(
  kind: VanKhiKind,
  periodKey?: string,
): Promise<VanKhiReadingResult> {
  const path = periodKey
    ? `/v1/boi-de/van-khi/${kind}/${encodeURIComponent(periodKey)}`
    : `/v1/boi-de/van-khi/${kind}`;
  const res = await fetch(`${BOIDE_API_URL}${path}`, { headers: authHeaders() });
  return unwrap<VanKhiReadingResult>(res);
}

/**
 * Mở kỳ (sinh bài luận) — chỉ thành công khi cửa sổ đang mở. Call dài 10–40s.
 * `refresh: true` = bài đã mở nhưng `stale` → thầy luận lại miễn phí (bài không stale
 * thì BE trả bài cũ, không gọi LLM).
 */
export async function openVanKhiReading(
  kind: VanKhiKind,
  periodKey: string,
  divinerId?: string,
  options?: { refresh?: boolean },
): Promise<VanKhiOpenResult> {
  const res = await fetch(
    `${BOIDE_API_URL}/v1/boi-de/van-khi/${kind}/${encodeURIComponent(periodKey)}/open`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ diviner_id: divinerId ?? null, refresh: options?.refresh ?? false }),
    },
  );
  return unwrap<VanKhiOpenResult>(res);
}

/**
 * Mở (hoặc dùng lại) phiên "hỏi thêm thầy" cho một kỳ đã có bài. Mở phiên miễn phí;
 * mỗi lượt hỏi trong phiên tính phí theo token như mọi phiên chat Bói Đê.
 */
export async function openVanKhiChat(
  kind: VanKhiKind,
  periodKey: string,
): Promise<{ session_id: string; reused: boolean }> {
  const res = await fetch(
    `${BOIDE_API_URL}/v1/boi-de/van-khi/${kind}/${encodeURIComponent(periodKey)}/chat`,
    { method: "POST", headers: authHeaders() },
  );
  return unwrap<{ session_id: string; reused: boolean }>(res);
}

// === Xem ngày theo việc (/v1/xem-ngay) — miễn phí, tất định, đứng riêng ngoài Vận khí ===
export type XemNgayRole = { role: string; label: string; gioi_tinh: "nam" | "nu" | null };
export type XemNgayPurpose = {
  key: string;
  label: string;
  icon: string;
  mo_ta: string;
  slogan: string;
  sub_slogan: string;
  tuoi_label: string;
  max_people: number;
  luu_y: string;
  inputs: ("huong" | "mau_xe" | "huong_di")[];
  ket_qua: string[];
  subs: { key: string; label: string }[];
  people: XemNgayRole[];
};
export type XemNgayOptions = {
  mau_xe: { ten: string; hanh: string }[];
  huong: { son: string; goc: number; range: string; huong: string }[];
  khoang: { key: string; label: string }[];
  /** 8 hướng cho việc xuất hành — tên trùng tên Hỷ Thần / Tài Thần BE trả. */
  huong_di?: { huong: string; goc: number }[];
};
export type VanKhiNgocHapSao = { name: string; viec: string[]; mo_ta: string };
export type XemNgayVerdict = "tot" | "duoc" | "tranh";
export type XemNgayDay = {
  date: string;
  display: string;
  lunar_display: string;
  can_chi_ngay: string;
  truc: string;
  sao: string;
  is_hoang_dao: boolean;
  score: number;
  verdict: XemNgayVerdict;
  /** Ngày rơi vào năm âm người đứng tên phạm Kim Lâu / Hoang Ốc / Tam Tai → không bao giờ "tốt". */
  pham_han_nam: boolean;
  /** Xuất hành: hướng người xem chọn trùng thần nào của ngày ("Hỷ Thần" / "Tài Thần"). */
  huong_trung?: string[];
  /** Sao Ngọc Hạp đúng việc đang xem (tên). */
  ngoc_hap?: { tot: string[]; xau: string[] } | null;
  ly_do_tot: string[];
  ly_do_xau: string[];
  gio_hoang_dao: { gio_chi: string; khung_gio: string; sao: string }[];
  huong: VanKhiHuong | null;
  /** Chỉ có ở các ngày trong `best`. */
  gio_tot?: VanKhiGioTot[];
};
/** Kim Lâu · Hoang Ốc · Tam Tai của một người trong MỘT năm âm — khoảng vắt qua Tết thì mỗi người có nhiều dòng. */
export type XemNgayHanNam = {
  birth: number | string;
  nam: number;
  can_chi_nam: string;
  role?: string;
  label?: string;
  nam_sinh_am: number;
  chi_tuoi: string;
  tuoi_mu: number;
  canh_bao: string[];
  on: string[];
};
export type XemNgayPerson = {
  role: string;
  label: string;
  gioi_tinh: "nam" | "nu" | null;
  nam_sinh: number | string;
  nam_sinh_am: number;
  can_chi: string;
  chi_tuoi: string;
  menh: string | null;
  hanh: string | null;
  cung_phi?: string;
  nhom_menh?: string;
};
export type XemNgayHuongGoiY = {
  huong: string;
  goc: number;
  range: string;
  du_nien: string;
  muc: string;
  tom_tat: string;
  pham_tam_sat: boolean;
};
export type XemNgayResult = {
  purpose: Pick<XemNgayPurpose, "key" | "label" | "icon" | "mo_ta" | "luu_y"> & {
    /** Khoá loại việc của engine — gửi sang Bói Đê để thầy chấm cùng bảng. */
    kind?: string;
  };
  sub: { key: string; label: string } | null;
  from: string;
  to: string;
  today: string;
  personalized: boolean;
  from_profile: boolean;
  people: XemNgayPerson[];
  best: XemNgayDay[];
  days: XemNgayDay[];
  counts: Record<XemNgayVerdict, number>;
  han_nam: XemNgayHanNam[];
  phuong_nam?: {
    chi_nam: string;
    tam_sat: { phuong: string; son: string[] };
    thai_tue: string;
    tue_pha: string;
  };
  huong_danh_gia?: {
    goc: number;
    huong: string;
    son: string;
    range: string;
    canh_bao: string[];
    du_nien?: string;
    tot?: boolean;
    muc?: string;
    tom_tat?: string;
  };
  huong_goi_y?: XemNgayHuongGoiY[];
  mau_danh_gia?: { mau: string; hanh: string; muc: string; nhan_xet: string };
  mau_goi_y?: { tot_nhat: string[]; hop: string[]; tranh: string[] };
  muon_tuoi?: { nam_sinh: number; can_chi: string; tuoi_mu: number; ly_do: string[] }[];
  /** Năm âm mà danh sách mượn tuổi áp dụng (năm đầu tiên gia chủ phạm hạn). */
  muon_tuoi_nam?: number;
  nam_cuoi_dep?: {
    nam: number;
    can_chi: string;
    tuoi_mu: number;
    kim_lau: string | null;
    tam_tai: boolean;
    dep: boolean;
  }[];
  /** Cưới hỏi, khi có đủ hai tuổi. */
  hop_tuoi?: XemNgayHopTuoi;
  /** Mua nhà / nhập trạch / khởi công, khi có cung phi người đứng tên. */
  bo_tri?: XemNgayBoTri[];
  /** Khai trương: tuổi nên nhờ mở hàng. */
  nguoi_mo_hang?: {
    nam_sinh: number;
    can_chi: string;
    tuoi_mu: number;
    menh: string | null;
    ly_do: string[];
  }[];
  huong_di?: string;
};
export type XemNgayHopTuoi = {
  chong: { nam_sinh_am: number; can_chi: string; cung_phi: string };
  vo: { nam_sinh_am: number; can_chi: string; cung_phi: string };
  tieu_chi: { key: string; label: string; chi_tiet: string; nhan_xet: string; diem: number }[];
  diem: number;
  toi_da: number;
  muc: string;
  ghi_chu: string;
};
export type XemNgayBoTri = {
  key: string;
  label: string;
  huong: { huong: string; du_nien: string }[];
  toa?: { huong: string; du_nien: string }[];
  goi_y: string;
};

export async function listXemNgayPurposes(): Promise<{
  purposes: XemNgayPurpose[];
  options?: XemNgayOptions;
  profile?: { ten: string | null; birth_date: string; gioi_tinh: "nam" | "nu" | null } | null;
}> {
  const res = await fetch(`${BOIDE_API_URL}/v1/xem-ngay/viec`, { headers: authHeaders() });
  return unwrap(res);
}

export async function timXemNgay(params: {
  viec: string;
  tu?: string;
  den?: string;
  khoang?: string;
  namSinh?: string[];
  gioiTinh?: (string | null)[];
  viecCon?: string;
  huong?: number | null;
  mauXe?: string | null;
  huongDi?: string | null;
  dungHoSo?: boolean;
}): Promise<XemNgayResult> {
  const qs = new URLSearchParams({ viec: params.viec });
  if (params.tu) qs.set("tu", params.tu);
  if (params.den) qs.set("den", params.den);
  if (params.khoang) qs.set("khoang", params.khoang);
  const years = (params.namSinh ?? []).map((y) => y.trim());
  if (years.some(Boolean)) qs.set("nam_sinh", years.join(","));
  if (params.gioiTinh?.some(Boolean))
    qs.set("gioi_tinh", params.gioiTinh.map((g) => g ?? "").join(","));
  if (params.viecCon) qs.set("viec_con", params.viecCon);
  if (params.huong !== undefined && params.huong !== null) qs.set("huong", String(params.huong));
  if (params.mauXe) qs.set("mau_xe", params.mauXe);
  if (params.huongDi) qs.set("huong_di", params.huongDi);
  if (params.dungHoSo === false) qs.set("dung_ho_so", "false");
  const res = await fetch(`${BOIDE_API_URL}/v1/xem-ngay/tim?${qs.toString()}`, {
    headers: authHeaders(),
  });
  return unwrap<XemNgayResult>(res);
}

/** Một dòng trong bảng giải thích điểm — các `delta` cộng lại đúng bằng điểm ngày. */
export type VanKhiBreakdown = {
  group: "nen" | "ngay" | "tuoi" | "viec";
  label: string;
  delta: number;
  detail: string;
};
export type VanKhiGioTot = {
  chi: string;
  khung: string;
  sao_hoang_dao: string | null;
  ly_thuan_phong: string;
  stars: number;
  ly_do: string[];
};
/** Bảng "Lịch ngày" — mọi thông số của một ngày (BE: `services/van_khi/lich_ngay.py`). */
export type VanKhiLichNgay = {
  date: string;
  today: string;
  display: string;
  lunar_display: string;
  lunar: { day: number; month: number; year: number };
  can_chi: { ngay: string; thang: string; nam: string };
  gio_hien_tai: string | null;
  tiet_khi: string;
  personalized: boolean;
  score: number;
  bucket: string;
  verdict: string;
  breakdown: VanKhiBreakdown[];
  chi_so: { ngay: number; thang: number; nam: number | null };
  nen_van_nam: { score: number; chi_nam: string; tuoi_mu: number; ghi_chu: string[] } | null;
  gio_tot: VanKhiGioTot[];
  may_man: VanKhiMayMan | null;
  tuoi_hop: VanKhiTuoiHop | null;
  truc: { name: string; y_nghia: string; tot_cho: string; xau_cho: string };
  sao: { name: string; is_hoang_dao: boolean; y_nghia: string };
  nhi_thap_bat_tu: { name: string; hanh: string; con_vat: string; tot: boolean; y_nghia: string };
  /** Sao tốt / xấu theo Ngọc Hạp Thông Thư (tháng âm + can chi ngày). */
  ngoc_hap?: { tot: VanKhiNgocHapSao[]; xau: VanKhiNgocHapSao[] };
  ngay_ky: VanKhiNgayKy[];
  nap_am: VanKhiNapAm | null;
  /** Đã gộp trực + sao + 28 sao + ngày kỵ; việc bị kỵ ở bất kỳ nguồn nào KHÔNG nằm ở `nen_lam`. */
  nen_lam: string[];
  /** Xếp theo độ nặng: ngày kỵ / trực xấu → sao xấu → trực. */
  khong_nen: string[];
  /** Một câu chốt cho ngày (BE cũ chưa có). */
  viec_tom_tat?: string;
  /** Từng căn cứ kèm lời giải thích thường (BE cũ chưa có). */
  viec_can_cu?: { ten: string; tot: boolean; giai_thich: string }[];
  huong: { than: string; huong: string; do: string | null; dung_cho: string }[];
  tuoi_xung_khac: string[];
  gio_hoang_dao: { gio_chi: string; khung_gio: string; sao: string }[];
  ly_thuan_phong: {
    name: string;
    tot: boolean;
    y_nghia: string;
    gio: { chi: string; khung: string }[];
  }[];
  tiet_khi_info: VanKhiTietKhi | null;
  week: { date: string; score: number; bucket: string }[];
};

export async function getVanKhiLichNgay(date?: string): Promise<VanKhiLichNgay> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  const res = await fetch(`${BOIDE_API_URL}/v1/boi-de/van-khi/ngay${qs}`, {
    headers: authHeaders(),
  });
  return unwrap<VanKhiLichNgay>(res);
}

/** Thẻ Tiết khí của một ngày — tất định, miễn phí; đăng nhập thì có thêm `ca_nhan`. */
export async function getVanKhiTietKhi(
  date?: string,
): Promise<{ date: string; today: string; tiet_khi: VanKhiTietKhi }> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  const res = await fetch(`${BOIDE_API_URL}/v1/boi-de/van-khi/tiet-khi${qs}`, {
    headers: authHeaders(),
  });
  return unwrap<{ date: string; today: string; tiet_khi: VanKhiTietKhi }>(res);
}

/** Nhãn ngày tốt/bất lợi — tất định, miễn phí, khách chưa đăng nhập cũng gọi được. */
export async function getVanKhiXemNgay(date?: string): Promise<VanKhiXemNgayResult> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  const res = await fetch(`${BOIDE_API_URL}/v1/boi-de/van-khi/xem-ngay${qs}`, {
    headers: authHeaders(),
  });
  return unwrap<VanKhiXemNgayResult>(res);
}

// === Wallpaper — ảnh nền từ quẻ Kinh Dịch ===
// Việc GHÉP ẢNH nằm ở trình duyệt (`lib/wallpaper-canvas.ts`); BE chỉ lưu bytes
// + metadata. Vì thế nhóm này không có endpoint "render" nào.

export type WallpaperBackground = {
  id: number;
  slug: string;
  name: string;
  orientation: "portrait" | "square" | "landscape";
  ngu_hanh: string | null;
  tags: string[];
  width: number;
  height: number;
  /** Đường dẫn TƯƠNG ĐỐI — ghép với BOIDE_API_URL qua helper bên dưới. */
  image_path: string;
  thumb_path: string;
};

export type SavedWallpaper = {
  id: number;
  token: string;
  source_type: string;
  source_id: string | null;
  background_id: number | null;
  width: number;
  height: number;
  title: string | null;
  options: Record<string, unknown>;
  is_public: boolean;
  created_at: string;
  image_path: string;
};

export type PublicWallpaper = {
  id: number;
  token: string;
  title: string | null;
  width: number;
  height: number;
  created_at: string;
  source_type: string;
  image_path: string;
};

export type WallpaperSavePayload = {
  source_id?: string | null;
  background_id?: number | null;
  title?: string | null;
  /**
   * snake_case khớp schema pydantic — đừng gửi camelCase của FE vào đây.
   *
   * ⚠️ Type này phải khớp `WallpaperLayout` bên
   * `src/api/v1/endpoints/wallpaper/schemas/wallpaper.py`. Thiếu một trường thì
   * pydantic BỎ nó trong im lặng: ảnh vẫn lưu, chỉ là mở lại mất đúng tuỳ chọn
   * người dùng vừa chỉnh. `tsc` là hàng rào duy nhất canh chiều này.
   */
  layout: {
    ratio: string;
    scale: number;
    link_positions: boolean;
    main_x: number;
    main_y: number;
    bien_x: number;
    bien_y: number;
    hex_color: string;
    link_colors: boolean;
    text_color: string;
    link_bien_color: boolean;
    bien_color: string;
    link_moving_color: boolean;
    moving_color: string;
    link_bien_moving_color: boolean;
    bien_moving_color: string;
    show_bien: boolean;
    show_name: boolean;
    name_mode: string;
    text_scale: number;
    bg_offset_x: number;
    bg_offset_y: number;
    /** Phóng ảnh nền, 1 = vừa khít khung (cover). */
    bg_zoom: number;
    /** Xoay ảnh nền: 0 | 90 | 180 | 270. */
    bg_rotate: number;
  };
};

/** URL ảnh nền đầy đủ — CÔNG KHAI, dùng được thẳng trong `<img>`/canvas. */
export function wallpaperBackgroundImageUrl(id: number): string {
  return `${BOIDE_API_URL}/v1/wallpapers/backgrounds/${id}/image`;
}

/** URL thumbnail cho grid chọn nền (nhẹ hơn nhiều so với ảnh full). */
export function wallpaperBackgroundThumbUrl(id: number): string {
  return `${BOIDE_API_URL}/v1/wallpapers/backgrounds/${id}/thumb`;
}

/** URL ảnh đã chia sẻ — công khai, không cần token. */
export function publicWallpaperImageUrl(token: string): string {
  return `${BOIDE_API_URL}/v1/wallpapers/public/${token}/image`;
}

export async function listWallpaperBackgrounds(): Promise<WallpaperBackground[]> {
  const res = await fetch(`${BOIDE_API_URL}/v1/wallpapers/backgrounds`, {
    headers: authHeaders(),
  });
  const data = await unwrap<{ items: WallpaperBackground[] }>(res);
  return data.items || [];
}

/** Lưu ảnh vừa ghép. FormData nên KHÔNG set Content-Type (browser tự đặt boundary). */
export async function saveWallpaper(
  blob: Blob,
  payload: WallpaperSavePayload,
): Promise<SavedWallpaper> {
  const form = new FormData();
  form.append("file", blob, "wallpaper.jpg");
  form.append("payload", JSON.stringify({ source_type: "iching", ...payload }));
  const res = await fetch(`${BOIDE_API_URL}/v1/wallpapers`, {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });
  return unwrap<SavedWallpaper>(res);
}

export async function listMyWallpapers(
  page = 1,
  limit = 10,
): Promise<{ items: SavedWallpaper[]; total: number; page: number; limit: number }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/wallpapers?page=${page}&limit=${limit}`, {
    headers: authHeaders(),
  });
  return unwrap<{ items: SavedWallpaper[]; total: number; page: number; limit: number }>(res);
}

/** Ảnh của tôi cần Bearer header nên `<img src>` không dùng được — fetch ra blob. */
export async function fetchWallpaperImage(id: number): Promise<Blob> {
  const res = await fetch(`${BOIDE_API_URL}/v1/wallpapers/${id}/image`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Không tải được ảnh nền");
  return res.blob();
}

export async function deleteWallpaper(id: number): Promise<void> {
  const res = await fetch(`${BOIDE_API_URL}/v1/wallpapers/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  await unwrap<{ message: string }>(res);
}

/** Bật/thu hồi link công khai. Trả token để dựng link chia sẻ. */
export async function setWallpaperPublic(
  id: number,
  isPublic: boolean,
): Promise<{ token: string; is_public: boolean }> {
  const res = await fetch(`${BOIDE_API_URL}/v1/wallpapers/${id}/share`, {
    method: isPublic ? "POST" : "DELETE",
    headers: authHeaders(),
  });
  return unwrap<{ token: string; is_public: boolean }>(res);
}

/** Trang công khai — không auth. 404 khi chủ ảnh đã thu hồi link. */
export async function getPublicWallpaper(token: string): Promise<PublicWallpaper> {
  const res = await fetch(`${BOIDE_API_URL}/v1/wallpapers/public/${token}`);
  return unwrap<PublicWallpaper>(res);
}

// ============================================================
// Bootstrap — gộp các lời gọi "mở app" (`GET /v1/bootstrap`)
// ============================================================

/** Mỗi khoá giống hệt `data` của endpoint lẻ tương ứng. */
export type BootstrapData = {
  me: BoideUser | null;
  profile: BoideProfile | null;
  streak: StreakMe | null;
  checkin: CheckinStatus | null;
  inbox_unread: { unread: number } | null;
  quota: QuotaStatus | null;
  quick_topup_offer: QuickTopupOffer | null;
  announcements: { items: Announcement[] } | null;
  affiliate: AffiliateMe | null;
  daily_fortune: DailyFortuneResponse | null;
  /** Các phần BE lỗi/quá hạn — client tự gọi endpoint lẻ cho những phần này. */
  failed: BootstrapSection[];
};

export type BootstrapSection = Exclude<keyof BootstrapData, "failed">;

/**
 * `null` khi BE chưa có endpoint này (404 — FE/BE deploy lệch nhau): caller lùi về
 * gọi endpoint lẻ. Lỗi khác (auth, 5xx, mạng) thì ném như mọi hàm khác.
 */
export async function getBootstrap(): Promise<BootstrapData | null> {
  const res = await fetch(`${BOIDE_ACCOUNT_API_URL}/v1/bootstrap?platform=${announcementPlatform()}`, {
    headers: authHeaders(),
  });
  if (res.status === 404) return null;
  const data = await unwrap<BootstrapData>(res);
  const offer = data.quick_topup_offer;
  // Cùng kiểm tra với `getQuickTopupOffer`: offer không có id là "chưa có offer".
  return {
    ...data,
    quick_topup_offer: offer && typeof offer.id === "number" ? offer : null,
    failed: Array.isArray(data.failed) ? data.failed : [],
  };
}
